import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Database, 
  Server, 
  Bot,
  Code,
  Table,
  FileJson,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  RefreshCw,
  Copy,
  Check,
  X,
  Image,
  Download,
  ExternalLink,
  Cpu,
  Zap,
  Shield,
  GitBranch,
  Workflow,
  Lock,
  Users,
  AlertTriangle,
  ArrowRight,
  Network,
  BookOpen,
  Building2,
  DollarSign,
  Target,
  Layers,
  Crown,
  Clock,
  Briefcase,
  Search,
  FolderOpen,
  FileText,
  Settings,
  Eye,
  UserPlus,
  Mail,
  ArrowRightCircle,
  ArrowLeft,
  ClipboardCheck,
  FormInput,
  Globe,
  Link2
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import ReleaseNotesAutoFeed from '../../components/admin/ReleaseNotesAutoFeed';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================================================
// NAVIGATION HIERARCHY
// ============================================================================
// Phase 2 restructure: collapse the previous five technical/security/AI/user
// pillars into four operator-friendly groups (Release / Engineering /
// Operations / Billing). Release notes are promoted to the top so the most
// recent platform changes are one click away on every visit.
const NAV_HIERARCHY = {
  'release': {
    label: 'Release',
    icon: Clock,
    color: 'amber',
    description: "What's new across versions",
    subTabs: [
      { id: 'release-notes', label: 'Release Notes', icon: Clock },
    ]
  },
  'engineering': {
    label: 'Engineering',
    icon: Cpu,
    color: 'blue',
    description: 'Architecture, security & AI internals',
    subTabs: [
      { id: 'architecture', label: 'Architecture', icon: Network },
      { id: 'database', label: 'Database', icon: Database },
      { id: 'api', label: 'API', icon: Server },
      { id: 'security', label: 'Security', icon: Lock },
      { id: 'zero-trust', label: 'Zero-Trust', icon: Eye },
      { id: 'rbac-audit', label: 'RBAC Audit', icon: Users },
      { id: 'auth-roadmap', label: 'Auth Roadmap', icon: Lock },
      { id: 'agents', label: 'AI Agents', icon: Bot },
      { id: 'byom', label: 'BYOM / LLM Router', icon: Zap },
      { id: 'sentinel', label: 'SAM.gov Sentinel', icon: Target },
      { id: 'ebuy-sentinel', label: 'GSA eBuy Sentinel', icon: FileText },
      { id: 'collaboration', label: 'Consultant ↔ Contractor', icon: Users },
      { id: 'workflows', label: 'Workflows', icon: Workflow },
      { id: 'versioning', label: 'Versioning Policy', icon: GitBranch },
    ]
  },
  'operations': {
    label: 'Operations',
    icon: Crown,
    color: 'teal',
    description: 'Day-to-day playbooks for every role',
    subTabs: [
      { id: 'functional-guide', label: "Owner's Guide", icon: BookOpen },
      { id: 'signup-flow', label: 'Signup Flow', icon: UserPlus },
      { id: 'consultant-guide', label: 'Consultant Guide', icon: Briefcase },
      { id: 'contractor-account', label: 'Contractor Account', icon: Building2 },
      { id: 'brand-assets', label: 'Brand Assets', icon: Image },
    ]
  },
  'billing': {
    label: 'Billing',
    icon: DollarSign,
    color: 'emerald',
    description: 'Stripe, plans, AI credit billing',
    subTabs: [
      { id: 'stripe-overview', label: 'Stripe Overview', icon: DollarSign },
      { id: 'plan-catalog', label: 'Plan Catalog', icon: Layers },
      { id: 'ai-credits', label: 'AI Credit Billing', icon: Cpu },
      { id: 'webhooks-audit', label: 'Webhooks & Audit', icon: GitBranch },
    ]
  }
};

// Reverse lookup: given a sub-tab id, return its parent category id. Used for
// deep-link parsing so a URL of `?tab=database` resolves to the Engineering
// pillar without callers needing to know the grouping.
const SUBTAB_TO_CATEGORY = Object.entries(NAV_HIERARCHY).reduce(
  (acc, [catId, cat]) => {
    cat.subTabs.forEach((sub) => {
      acc[sub.id] = catId;
    });
    return acc;
  },
  {}
);

// ============================================================================
// DATABASE SCHEMA DOCUMENTATION
//
// The platform runs in MongoDB-only mode (`USE_MONGODB_ONLY=true`). The
// "tables" below are MongoDB collections — accessed via Motor in the
// `app/routes/mongo_*.py` modules. Field references use dot-notation; nested
// arrays are noted with `[]`. The legacy Postgres schema is no longer in the
// read path (its full removal is on the v4.x roadmap).
// ============================================================================
const DATABASE_TABLES = {
  core: {
    title: "Core Platform Collections",
    tables: [
      { name: "organizations", description: "Multi-tenant organization records (Platform / Consultancy / Contractor / Reviewer). Holds plan + billing + AI Credits state.", columns: "id, name, type, slug, uei, cage_code, subscription_plan, max_pursuits, purchased_pursuit_slots, ai_credit_balance_cents, ai_credit_auto_recharge, trial_credits_granted, is_trial, trial_ends_at" },
      { name: "users", description: "User accounts with role-based access control. `password_hash` uses bcrypt; sessions are JWT-only (no DB session table).", columns: "id, organization_id, email, role, first_name, last_name, password_hash, is_active, last_login_at" },
      { name: "invitations", description: "Pending user / consultancy invitations with single-use token validation.", columns: "id, organization_id, email, role, token, status, expires_at, created_by" },
      { name: "audit_logs", description: "Append-only audit trail for tenant-scoped actions (PURSUIT_ARCHIVED, PURSUIT_DELETED, SLOT_TOPUP, etc.).", columns: "id, organization_id, user_id, user_email, action, table_name, record_id, metadata, timestamp" },
    ]
  },
  relationship: {
    title: "Relationship & Access Collections",
    tables: [
      { name: "consultant_client_links", description: "Maps a consultancy to one or more client orgs. ACTIVE rows grant the consultancy visibility into every pursuit owned by the client. Created by `POST /api/consultant-invites/invite` from the contractor's Command Center; consumed by `pursuit_to_response.collaborators[]`.", columns: "id, consultant_org_id, client_org_id, status, created_at, linked_at" },
      { name: "trial_requests", description: "Trial access requests pending Super Admin approval (deprecated — most tenants now self-serve via signup).", columns: "id, company_name, contact_email, status, approved_by, created_organization_id" },
    ]
  },
  pursuit: {
    title: "Pursuit Management Collections",
    tables: [
      { name: "pursuits", description: "Active federal contract pursuits. Soft-deleted (`is_deleted`/`deleted_at`) and archivable (`archived_at` + reason). `created_by` powers viewer-relative `origin` resolution.", columns: "id, organization_id, opportunity_name, agency_code, solicitation_number, naics_code, status, procurement_phase, response_deadline, pwin_score, archived_at, archive_reason, archive_notes, archived_by, deleted_at, is_deleted, created_by, created_at" },
      { name: "extracted_requirements", description: "Output of the RFP Shredder. Post-Apr 2026 records carry source-context fields for clickable rows. Post-May 2026 (v3.11.x) carry the federal-template self-scoring rubric (`self_score` 0-3 or null) and the 3-level capability hierarchy (`task_area` Level 1, `sub_area` Level 2). Legacy rows have null for the new fields and surface gracefully.", columns: "id, pursuit_id, extraction_job_id, requirement_text, requirement_type, section_reference, category, confidence_score, compliance_status, source_document_name, source_context, source_approx_page, source_section_title, self_score, task_area, sub_area" },
      { name: "evaluations", description: "Go/No-Go evaluation results: Bid Pursuit Scorecard answers, AI prefill output, decision snapshot.", columns: "pursuit_id, scorecard_answers, scorecard_score, decision, decision_at, decision_by, ai_prefill_at, reasoning_log" },
      { name: "pursuit_kb_documents", description: "Documents attached to a pursuit (RFP, capability statements). `group_id` 1=RFP, 2=Capability. Files stored in GridFS.", columns: "id, pursuit_id, group_id, file_name, mime_type, gridfs_file_id, is_gold_source, deleted_at, uploaded_by, uploaded_at" },
      { name: "extraction_jobs", description: "Tracks RFP Shredder runs (sync + SSE streaming). Records LLM model, cost, status, errors.", columns: "id, pursuit_id, status, started_at, completed_at, model_used, total_cost_cents, requirement_count, error" },
      { name: "rtm_matrix", description: "Compliance Matrix (formerly RTM — collection name preserved for data-contract stability). Caches the LLM enrichments mapped onto canonical extracted_requirements rows. `entries[]` carries proposal-grade compliance metadata: section, response approach, volume category, writer comments, and the federal compliance status (FULLY_COMPLIANT / PARTIALLY_COMPLIANT / NON_COMPLIANT / SOW_RESTATE).", columns: "id, pursuit_id, organization_id, entries[]: {requirement_id, requirement_text, source_section, response_section, response_approach, compliance_status, volume_category, writer_comments, sow_restate, …}, deleted_at" },
      { name: "capability_matrix", description: "LCAT (Labor Category) staffing readiness matrix. Each `entries[]` element captures a labor category required by the pursuit. v3.11.2+ adds `task_area` per LCAT — federal Capability Matrix template's 'Applicable #' linkage that ties LCATs to capability domains.", columns: "id, pursuit_id, organization_id, entries[]: {id, lcat_name, lcat_code, task_area, required_qty, available_qty, required_clearance, required_certifications[], required_experience_years, status (MET|PARTIAL|GAP), gap_severity (HIGH|MEDIUM|LOW), notes, teaming_recommendation}, deleted_at" },
      { name: "gap_analysis", description: "Gap Tracker (PP-Mapped) rows — manually-curated gap list with per-row 'Map to Past Performance' for differentiation. Distinct from Opportunity Intake (extraction) and the Compliance Matrix (proposal mapping). v3.11.2+ adds `capability_statement` (positive proposal narrative) and `bidder_response` (internal response approach), restoring federal Capability sheet parity. Soft-deleted via `deleted_at`. Native Mongo CRUD endpoints live in `mongo_pursuits.py` (the legacy SQL workspace router is skipped under USE_MONGODB_ONLY=true).", columns: "id, pursuit_id, organization_id, requirement_name, requirement_description, requirement_source, status, severity, solution_type, solution_notes, mapped_past_performance_id, mapping_confidence, mapping_notes, capability_statement, bidder_response, deleted_at, created_at, updated_at" },
      { name: "pursuit_scorecards", description: "35-question Bid Pursuit Scorecard responses. Sections: Prospect, Internal, Market. Drives P(Go), P(Win), Strategy signals. Soft-deleted.", columns: "id, pursuit_id, organization_id, answers (JSON: question_id → answer), score, deal_killers, unknowns, last_modified_by, deleted_at" },
    ]
  },
  knowledge: {
    title: "Knowledge Base Collections",
    tables: [
      { name: "kb_documents", description: "Org-level knowledge documents (Past Performance, Capability Statements, Resumes). Storage: GridFS.", columns: "id, organization_id, doc_type, file_name, mime_type, gridfs_file_id, is_gold_source, deleted_at, uploaded_by" },
      { name: "fs.files / fs.chunks", description: "MongoDB GridFS — backing store for all uploaded files referenced by `gridfs_file_id` in pursuit_kb_documents and kb_documents.", columns: "_id, length, chunkSize, uploadDate, filename, metadata" },
    ]
  },
  ai_config: {
    title: "AI, BYOM, and Sentinel Collections",
    tables: [
      { name: "system_settings", description: "Super Admin AI config bookkeeping (default provider/model shown on the Settings UI). Not used as an LLM-call fallback — every call resolves a BYOM key from the calling org.", columns: "key, value (JSON: default_provider, default_model, feature_flags)" },
      { name: "organizations.ai_config", description: "Per-tenant BYOM configuration embedded on each organization document. The api_key is encrypted at rest with Fernet (AES-128-CBC + HMAC) keyed by the `AI_CONFIG_ENCRYPTION_KEY` env var; legacy plaintext rows are lazy-migrated on the next read.", columns: "ai_config.{provider, model, api_key (fernet:…), enabled, capture_thinking, updated_at}" },
      { name: "sentinel_matches", description: "SAM.gov opportunities matched by the Sentinel worker. Tier-scored against tenant preferences.", columns: "id, organization_id, notice_id, title, match_score, match_tier, processed_at" },
      { name: "sentinel_preferences", description: "Org-level NAICS/PSC + filter preferences fed to the Sentinel worker.", columns: "organization_id, naics_codes[], psc_codes[], min_value, max_value, set_asides[]" },
    ]
  },
  billing: {
    title: "Billing & Stripe Collections",
    tables: [
      { name: "payment_transactions", description: "Idempotency anchor for every Stripe Checkout Session. Webhook handlers look up rows here by `stripe_session_id` and short-circuit when status is already COMPLETED — preventing double-applies.", columns: "id, organization_id, stripe_session_id, purpose (SLOT_TOPUP|SUBSCRIPTION|OTHER), amount_cents, currency, status (INITIATED|COMPLETED|FAILED|EXPIRED), metadata, created_at, completed_at" },
      { name: "subscription_history", description: "Audit trail of plan changes — record per upgrade / downgrade / reactivation.", columns: "id, organization_id, from_plan, to_plan, stripe_subscription_id, effective_at, recorded_by" },
    ]
  },
  reference: {
    title: "Reference Data Collections",
    tables: [
      { name: "federal_agencies", description: "Federal agency directory with hierarchy.", columns: "id, name, abbreviation, parent_agency_id, agency_type" },
      { name: "naics_codes", description: "NAICS code library for opportunity matching.", columns: "code, title, description, sector" },
      { name: "psc_codes", description: "Product/Service codes for contract classification.", columns: "code, description, category" },
      { name: "contract_vehicles", description: "Government-wide contract vehicles (GSA, SEWP, etc.).", columns: "id, name, agency, vehicle_type, expiration_date" },
    ]
  }
};

// ============================================================================
// API ENDPOINTS DOCUMENTATION
// ============================================================================
const API_ENDPOINTS = {
  auth: {
    title: "Authentication",
    description: "User authentication and session management",
    endpoints: [
      { method: "POST", path: "/api/auth/login", description: "Authenticate user and receive JWT token" },
      { method: "POST", path: "/api/auth/register", description: "Register new user (invite flow only)" },
      { method: "POST", path: "/api/auth/logout", description: "Invalidate current session" },
      { method: "GET", path: "/api/auth/me", description: "Get current user profile" },
      { method: "POST", path: "/api/auth/refresh", description: "Refresh JWT token" },
    ]
  },
  organizations: {
    title: "Organizations",
    description: "Multi-tenant organization management",
    endpoints: [
      { method: "GET", path: "/api/organizations", description: "List organizations (admin only)" },
      { method: "GET", path: "/api/organizations/{id}", description: "Get organization details" },
      { method: "PUT", path: "/api/organizations/{id}", description: "Update organization settings" },
      { method: "POST", path: "/api/organizations/{id}/invite", description: "Invite user to organization" },
    ]
  },
  pursuits: {
    title: "Pursuits",
    description: "Pursuit/opportunity management. List + single-record responses include viewer-relative `origin` ('INVITED' | 'SELF_CREATED') and `collaborators[]` (OWNER + linked consultancies).",
    endpoints: [
      { method: "GET", path: "/api/pursuits", description: "List pursuits for organization. Supports include_archived, organization_id, stage, origin filters (origin filtered client-side)." },
      { method: "POST", path: "/api/pursuits", description: "Create new pursuit (origin auto-resolves to viewer's relationship)" },
      { method: "GET", path: "/api/pursuits/{id}", description: "Get pursuit details (includes origin + collaborators)" },
      { method: "PUT", path: "/api/pursuits/{id}", description: "Update pursuit" },
      { method: "POST", path: "/api/pursuits/{id}/archive", description: "Archive pursuit (frees slot). Reviewer → 403. Body: { archive_reason, archive_notes? }" },
      { method: "POST", path: "/api/pursuits/{id}/unarchive", description: "Unarchive pursuit. Returns 402 when at slot cap (unlimited plans bypass)." },
      { method: "POST", path: "/api/purge/pursuit/{id}/delete", description: "Soft-delete pursuit (30d retention). Reviewer → 403. Cross-tenant guard via consultant_client_links." },
      { method: "POST", path: "/api/pursuits/{id}/evaluate", description: "Run Go/No-Go evaluation" },
      { method: "GET", path: "/api/pursuits/{id}/gaps", description: "List Gap Tracker rows. v3.11.2+ enriched with `mapped_past_performance_title` join; filters out soft-deleted rows." },
      { method: "POST", path: "/api/pursuits/{id}/gaps", description: "v3.11.2 — Create a Gap Tracker row (Mongo-native). Includes the federal-template `capability_statement` + `bidder_response` fields." },
      { method: "PATCH", path: "/api/pursuits/{id}/gaps/{gap_id}", description: "v3.11.2 — Partial update; exclude_unset semantics preserve fields not in the body." },
      { method: "DELETE", path: "/api/pursuits/{id}/gaps/{gap_id}", description: "v3.11.2 — Soft-delete (sets `deleted_at`)." },
      { method: "GET", path: "/api/pursuits/{id}/coverage-map", description: "v3.11.3 — Cross-Tool Coverage Map. Joins extracted_requirements + capability_matrix + gap_analysis + rtm_matrix by task_area; returns per-area distributions, totals strip, and notes flags telling the UI which tools don't yet carry task_area." },
      { method: "POST", path: "/api/pursuits/{id}/capability-matrix", description: "Save the LCAT staffing matrix (entries[]). v3.11.2+ each entry can carry a `task_area` (federal 'Applicable #' linkage)." },
      { method: "GET", path: "/api/pursuits/{id}/capability-matrix", description: "Load the LCAT staffing matrix; returns the persisted entries[] array." },
      { method: "POST", path: "/api/pursuits/{id}/capability-matrix/ai-detect/init", description: "Two-Phase Progressive UI — instant DB read of canonical LCAT seeds." },
      { method: "POST", path: "/api/pursuits/{id}/capability-matrix/ai-detect/batch", description: "Two-Phase Progressive UI — parallel LLM batch enriching LCATs (fills task_area, gap_severity, teaming_recommendation, etc.)." },
      { method: "POST", path: "/api/pursuits/{id}/rtm/ai-map/init", description: "Two-Phase Progressive UI — instant DB read of canonical Compliance Matrix entries (route name preserved as 'rtm' for data-contract stability)." },
      { method: "POST", path: "/api/pursuits/{id}/rtm/ai-map/batch", description: "Two-Phase Progressive UI — parallel LLM batch filling Compliance Matrix analytical columns (compliance_status, response_approach, volume_category, writer_comments, etc.)." },
      { method: "GET", path: "/api/pursuits/{id}/pwin-composite", description: "v3.11.1+ — Composite pWin breakdown across 5 signals (AI Analysis 40%, Bid Pursuit Scorecard 25%, Capability Matrix 20%, Compliance Matrix 15%, Self-Score Coverage 10%) with renormalisation across the active subset." },
      { method: "POST", path: "/api/pursuits/{id}/pwin-composite", description: "Recalculate + persist the composite pWin to the pursuit document." },
    ]
  },
  knowledge: {
    title: "Knowledge Base",
    description: "Document and asset management",
    endpoints: [
      { method: "GET", path: "/api/knowledge-base/documents", description: "List organization documents" },
      { method: "POST", path: "/api/knowledge-base/upload", description: "Upload document" },
      { method: "DELETE", path: "/api/knowledge-base/documents/{id}", description: "Delete document" },
      { method: "GET", path: "/api/knowledge-base/past-performances", description: "List past performances" },
      { method: "GET", path: "/api/knowledge-base/key-personnel", description: "List key personnel" },
    ]
  },
  ai: {
    title: "AI Services",
    description: "AI-powered analysis and generation",
    endpoints: [
      { method: "POST", path: "/api/pursuit-kb/{id}/run-analysis", description: "Run AI analysis pipeline (NAICS/PSC extraction, similarity, teaming, scoring)" },
      { method: "POST", path: "/api/pursuit-kb/{id}/upload", description: "Upload document (auto-triggers score recalculation)" },
      { method: "POST", path: "/api/llm/pwin-oracle", description: "Run AI Go/No-Go decision with reasoning trace" },
      { method: "POST", path: "/api/llm/test", description: "Test LLM provider connectivity" },
      { method: "GET", path: "/api/ai/requirements/{pursuit_id}", description: "List extracted requirements with self_score, task_area, sub_area" },
      { method: "PATCH", path: "/api/ai/requirements/{id}/status", description: "Update compliance_status (MET / PARTIAL / GAP / NOT_EVALUATED)" },
      { method: "PATCH", path: "/api/ai/requirements/{id}/self-score", description: "v3.11.0 — Set the federal 0-3 self-scoring rubric (or null to clear)" },
      { method: "GET", path: "/api/ai/requirements/{pursuit_id}/self-score-summary", description: "v3.11.0 — Distribution + averages across the rubric (used by the Go/No-Go heatmap card and the slider hints)" },
      { method: "PATCH", path: "/api/ai/requirements/{id}/hierarchy", description: "v3.11.3 — Override AI-inferred Task Area / Sub-Area. Empty string clears, null leaves unchanged" },
      { method: "GET", path: "/api/ai/requirements/{pursuit_id}/hierarchy-summary", description: "v3.11.3 — Task Area → Sub-Area rollup with self-score distribution per group" },
    ]
  },
  llm: {
    title: "LLM Router (BYOM)",
    description: "Model-agnostic AI configuration with Bring Your Own Model support",
    endpoints: [
      { method: "GET", path: "/api/llm/providers", description: "List supported LLM providers and models" },
      { method: "GET", path: "/api/llm/system-config", description: "Get system-wide AI configuration (Super Admin)" },
      { method: "PUT", path: "/api/llm/system-config", description: "Update system AI defaults (Super Admin)" },
      { method: "GET", path: "/api/llm/org-config", description: "Get organization BYOM configuration" },
      { method: "PUT", path: "/api/llm/org-config", description: "Update organization BYOM settings" },
      { method: "DELETE", path: "/api/llm/org-config", description: "Remove BYOM config, revert to system default" },
      { method: "POST", path: "/api/llm/test", description: "Test LLM configuration before saving" },
      { method: "GET", path: "/api/llm/resolve/{org_id}", description: "Debug: Check which config will be used" },
      { method: "POST", path: "/api/llm/pwin-oracle", description: "Run Go/No-Go analysis with BYOM" },
      { method: "GET", path: "/api/llm/reasoning-log/{pursuit_id}", description: "Get Claude's thinking trace" },
    ]
  },
  sentinel: {
    title: "SAM.gov Sentinel",
    description: "Autonomous opportunity scouting from SAM.gov",
    endpoints: [
      { method: "GET", path: "/api/sentinel/preferences", description: "Get org's NAICS/PSC preferences" },
      { method: "PUT", path: "/api/sentinel/preferences", description: "Update scouting preferences" },
      { method: "POST", path: "/api/sentinel/scan", description: "Trigger manual opportunity scan" },
      { method: "GET", path: "/api/sentinel/matches", description: "List matched opportunities" },
      { method: "POST", path: "/api/sentinel/ingest/{notice_id}", description: "Ingest opportunity to pipeline" },
      { method: "GET", path: "/api/sentinel/stats", description: "Get scouting statistics" },
    ]
  }
};

// ============================================================================
// AI AGENTS
// ============================================================================
const AI_AGENTS = [
  {
    name: "SAM.gov Sentinel",
    status: "active",
    description: "Autonomous opportunity scouting agent. Monitors SAM.gov for new opportunities matching your NAICS/PSC codes",
    triggers: ["Scheduled scan (every 4 hours)", "Manual 'Run Scan' trigger"],
    outputs: ["New opportunities", "Match scores (Gold/Silver/Bronze)", "Auto-ingest to pipeline"]
  },
  {
    name: "RFP Analyzer (Shredder)",
    status: "active",
    description: "Extracts requirements, evaluation criteria, and key dates from RFP documents. Supports PDF, DOCX",
    triggers: ["Document upload", "Manual trigger", "Sentinel auto-ingest"],
    outputs: ["Requirements list", "Evaluation criteria", "Timeline milestones", "Formatting requirements"]
  },
  {
    name: "pWin Oracle (Go/No-Go)",
    status: "active",
    description: "AI-powered Go/No-Go decision engine with multi-model support (BYOM). Uses configurable LLM for bid decisions",
    triggers: ["Manual evaluation", "Fit score complete"],
    outputs: ["GO/NO_GO/CONDITIONAL decision", "pWin percentage", "Risk factors", "Claude thinking trace"]
  },
  {
    name: "Fit Score Calculator",
    status: "active",
    description: "3-phase scoring: (1) Metadata baseline from field completeness, (2) Document coverage bonus auto-triggered on upload, (3) AI competitive alignment via Run Analysis",
    triggers: ["Field entry (metadata baseline)", "Document upload (auto)", "Run Analysis (manual)"],
    outputs: ["Metadata baseline %", "Document coverage bonus", "Competitive alignment score", "Agency experience bonus"]
  },
  {
    name: "Proposal Drafter",
    status: "active",
    description: "Generates initial proposal section drafts using KB and RFP requirements",
    triggers: ["Section request", "Template selection"],
    outputs: ["Draft text", "Citation sources", "Compliance mapping"]
  },
  {
    name: "Compliance Checker",
    status: "active",
    description: "Validates proposal sections against RFP requirements",
    triggers: ["Draft save", "Manual check"],
    outputs: ["Compliance status", "Missing requirements", "Suggestions"]
  },
  {
    name: "Competitive Intel",
    status: "planned",
    description: "Analyzes competitor strengths and pricing strategies",
    triggers: ["Competitor added", "Manual analysis"],
    outputs: ["Threat assessment", "Differentiators", "Price-to-win"]
  }
];

// ============================================================================
// SECURITY CONFIG
// ============================================================================
const SECURITY = {
  roles: [
    { name: "SUPER_ADMIN", level: 100, description: "Platform owner - full system access" },
    { name: "CONSULTANT", level: 80, description: "Manages multiple contractor clients" },
    { name: "CONTRACTOR_ADMIN", level: 60, description: "Organization admin - full org access" },
    { name: "BID_MANAGER", level: 40, description: "Creates and manages pursuits" },
    { name: "REVIEWER", level: 20, description: "Read-only access to assigned pursuits" },
  ],
  dataIsolation: [
    { rule: "Organization scoping", description: "All queries automatically filtered by organization_id" },
    { rule: "Cross-org prevention", description: "JWT contains org_id, validated on every request" },
    { rule: "Consultant access", description: "Consultants see linked clients only via consultant_client_links" },
    { rule: "Document isolation", description: "Documents scoped to organization, never cross-searchable" },
  ],
  knownGaps: [
    { id: "GAP-001", severity: "Medium", description: "Audit log export missing encryption", status: "Planned Q1" },
    { id: "GAP-002", severity: "Low", description: "Session timeout not configurable per-org", status: "Backlog" },
    { id: "GAP-003", severity: "Low", description: "Password complexity rules not enforced", status: "Backlog" },
    { id: "GAP-004", severity: "Medium", description: "MFA not implemented", status: "Planned Q2" },
    { id: "GAP-005", severity: "High", description: "Document upload size limit bypass possible", status: "Fixed" },
    { id: "GAP-006", severity: "Medium", description: "Rate limiting not applied to AI endpoints", status: "In Progress" },
  ]
};

// ============================================================================
// WORKFLOWS
// ============================================================================
const WORKFLOWS = {
  // ── Pursuit Lifecycle ─────────────────────────────────────────
  pursuitLifecycle: {
    title: "Pursuit Lifecycle",
    subtitle: "The 8-screen journey from opportunity discovery to proposal submission",
    flowNote: "The Go/No-Go Dashboard is a risk-assessment checkpoint, not a gate. The team always proceeds with the pursuit — the dashboard quantifies the risk level so leadership understands what they are accepting. All downstream screens (Gap Analysis through Submission Guide) are always accessible.",
    steps: [
      {
        step: 0,
        name: "Pursuit Pipeline",
        path: "/{workspace}/pipeline",
        icon: "Kanban",
        color: "slate",
        purpose: "Central hub — all pursuits at a glance in Kanban or list view with FAR Phase badges and Fit Score indicators.",
        entryPoint: "User clicks 'Create Pursuit' or claims a Sentinel opportunity.",
        actions: ["Create pursuit (Agency, Bureau, Title, Set-Aside)", "Drag-and-drop stage changes", "View FAR Phase badge per card", "Archive / Delete pursuits"],
        outputs: ["New pursuit record created", "Pursuit enters Intake stage"],
        dataCreated: "pursuits (id, title, agency_code, bureau_code, status, procurement_phase)"
      },
      {
        step: 1,
        name: "Opportunity Intake",
        path: "/{workspace}/pursuit/{id}/intake",
        icon: "ClipboardList",
        color: "blue",
        purpose: "Capture all opportunity metadata — the foundation for every downstream score and analysis.",
        entryPoint: "Click into a pursuit from Pipeline.",
        actions: [
          "Fill General Information (Title, RFP#, Contract Value, Deadline, Set-Aside)",
          "Select Agency & Bureau (cascading dropdowns from federal_agencies / federal_bureaus)",
          "Configure FAR Lifecycle Phase (6 phases with FAR references)",
          "Add Teaming Partners (max 5, workshare %, role/swimlane)",
          "Set Prime Bidder (auto-populated from Client Directory org profile)"
        ],
        outputs: ["Metadata baseline for Fit Score (Phase 1: 0-50%)", "Agency/Bureau/NAICS/PSC stored for downstream matching"],
        dataCreated: "pursuits (agency_code, bureau_name, naics_code, psc_code, teaming_plan[], prime_metadata{})"
      },
      {
        step: 2,
        name: "Document Ingestion Hub",
        path: "/{workspace}/pursuit/{id}/ingestion",
        icon: "FolderOpen",
        color: "violet",
        purpose: "Upload, classify, and AI-process all pursuit documents across 6 standardized groups.",
        entryPoint: "'Manage Documents' from Intake or sidebar.",
        actions: [
          "Upload to 6 Groups: RFP/Solicitations, Capability/Strategy, Past Performance, Key Personnel, Winning Proposals, Other References",
          "AI Shred RFPs (Dual-Phase SSE: Regex 'shall/must' extraction + LLM implicit requirement extraction)",
          "Confirm extracted data in RFP Confirmation Modal (Deadline, Portal URL, Volume Count, NAICS, PSC, Prime Bidder)",
          "Run AI Analysis (6-stage pipeline: Vectorize, Extract NAICS/PSC, Validate Compliance, Similarity Check, Teaming Score, Final Score)",
          "Semantic search across all uploaded documents"
        ],
        outputs: [
          "Extracted requirements (shall/must statements)",
          "NAICS/PSC compliance badges (Green/Yellow/Red)",
          "Similarity matches between RFP and Winning Proposals",
          "Teaming partner-to-requirement coverage scores",
          "Fit Score jumps to Phase 2 (50-100%)"
        ],
        dataCreated: "pursuit_kb_documents, pursuit_kb_chunks (with embeddings), extracted_requirements"
      },
      {
        step: 3,
        name: "Go/No-Go Dashboard",
        path: "/{workspace}/pursuit/{id}/gonogo",
        icon: "Target",
        color: "amber",
        purpose: "Risk-assessment checkpoint. Quantifies pursuit risk so leadership understands what they are accepting. This is NOT a gate — the team always proceeds.",
        entryPoint: "Sidebar or 'Next' from Ingestion Hub.",
        actions: [
          "Review Fit Score with Metadata-Only / Document-Verified badge",
          "Run pWin Oracle (AI-powered bid decision with BYOM model selection)",
          "View AI reasoning trace (Claude thinking block)",
          "Add/manage Competitors (name, threat level, incumbent status)",
          "Add/manage Risks & Mitigations",
          "Run What-If Scenarios (pWin delta simulation)",
          "Record Go/No-Go decision with rationale"
        ],
        outputs: ["pWin percentage", "Risk level assessment (High/Medium/Low)", "Competitive landscape documented", "Decision recorded for audit trail"],
        dataCreated: "pursuit_evaluations, pursuit_competitors, pursuit_risks, pursuit_mitigations, pursuit_whatif_scenarios, pursuit_analysis (reasoning_log)",
        plannedEnhancements: [
          {
            id: "bid-pursuit-scorecard",
            title: "Bid Pursuit Scorecard Integration",
            status: "PLANNED",
            reference: "Modeled after the Shipley Capture / federal Capture-Management framework — a defensible structured scoring approach used across the federal BD industry.",
            summary: "Structured 35-question scoring methodology (based on the Shipley Capture framework) that replaces heuristic-only pWin with a defensible, mechanical P(Go)/P(Win) calculation.",
            domains: [
              {
                name: "Prospect Information",
                questionRange: "Q1–Q12",
                focus: "P(Go) — Customer Analysis",
                description: "Evaluates the opportunity itself: budget approval, requirement clarity, evaluation criteria, ability to influence decision-makers, contract terms acceptability.",
                aiAutoScoreable: "~8 of 12 (from RFP text, agency metadata, and NAICS/Set-Aside data)",
                humanRequired: "Decision-maker influence, behind-the-scenes issues, buy-vs-build status"
              },
              {
                name: "Internal Information",
                questionRange: "Q13–Q27",
                focus: "P(Win) — Company Analysis",
                description: "Evaluates the bidder's readiness: management support, technical resources, past performance track record, teaming partner complexity, IP protection, strategic alignment.",
                aiAutoScoreable: "~5 of 15 (from Knowledge Base, Past Performance records, and Gap Analysis)",
                humanRequired: "Management sponsorship, will to win, investment affordability, delivery capacity, existing business risk"
              },
              {
                name: "Market / Competitive Information",
                questionRange: "Q28–Q35",
                focus: "Strategy — Environment Analysis",
                description: "Evaluates the competitive landscape: known bidders, incumbent threats, competitor favoritism by decision-makers, technology risk, market positioning impact of a win.",
                aiAutoScoreable: "~4 of 8 (from Competitive Intel entries and RFP structure analysis)",
                humanRequired: "Competitor favoritism, starting disadvantage assessment, reputation impact"
              }
            ],
            scoringLogic: {
              scale: "1–5 per question (1 = Serious Disadvantage, 5 = Strong Advantage) + 'Unknown' state",
              maxScore: "175 (35 questions x 5 max)",
              dealKillerRule: "Any question flagged as a 'Potential Deal Killer' with a score of 1 overrides the aggregate — pWin drops to near-zero regardless of total score. This models fatal misses in federal contracting (e.g., no funded budget, unacceptable T&Cs, wired incumbent).",
              unknownAsRisk: "'Unknown' answers are tracked separately. A pursuit with 10+ Unknowns is itself a red flag — it means the team is bidding blind. Unknown count surfaces as a distinct risk metric."
            },
            hybridApproach: "AI pre-fills ~17 of 35 questions from RFP analysis, metadata, KB, competitive intel, and gap analysis data. The remaining ~18 questions require human judgment and are highlighted for manual review. This is not a fully autonomous score — it's an AI-assisted structured assessment.",
            uiIntegration: "Collapsible 'Bid Pursuit Scorecard Breakdown' panel inside the Go/No-Go Dashboard. Shows the three domains with individual question scores, Deal Killer flags, and Unknown count. The pWin Oracle reasoning trace references specific scorecard question categories.",
            dataSources: [
              "Opportunity Intake (Agency, NAICS, Set-Aside, contract value, deadline)",
              "Document Ingestion (RFP text analysis — evaluation criteria, LPTA detection, requirement clarity)",
              "Knowledge Base (Past Performance records, Key Personnel, certifications)",
              "Gap Analysis (capability gaps, teaming partner coverage)",
              "Competitive Intel (known competitors, incumbent status, threat levels)"
            ],
            pipelineUseCase: "Enables VP of Sales / Capture Manager to compare pursuits objectively: 'Pursuit A is 40/175 — pull the team. Pursuit B is 150/175 — double down.' Replaces gut-feel pipeline management with quantified resource allocation."
          }
        ]
      },
      {
        step: 4,
        name: "Gap Analysis & Teaming",
        path: "/{workspace}/pursuit/{id}/gap-analysis",
        icon: "UsersRound",
        color: "orange",
        purpose: "Map extracted requirements against team capabilities. Identify gaps and assign teaming partners to close them.",
        entryPoint: "Sidebar or 'Next' from Go/No-Go.",
        actions: [
          "Review auto-imported requirements from AI Shredder",
          "Classify gaps by type (Technical, Management, Past Performance, Staffing, Security, Pricing)",
          "Assign teaming partners to cover specific gaps",
          "Define resolution strategies for each gap",
          "Track gap closure progress"
        ],
        outputs: ["Gap inventory with coverage status", "Partner-to-requirement assignments", "Resolution strategies feeding Compliance Tracker"],
        dataCreated: "pursuit_gap_analysis (requirement_source, gap_type, resolution_strategy, assigned_partner)",
        plannedEnhancements: [
          {
            id: "capability-matrix",
            title: "Capability Matrix & LCAT Map ('Meatball Chart')",
            status: "PLANNED",
            reference: "Modeled after a real $500M+ federal IT services BPA solicitation — Capability Matrix used for Teaming Partner evaluation. Real-world artifact used as design reference.",
            summary: "A structured teaming gap analysis tool modeled after federal 'Meatball Charts.' Maps the government's task areas against the proposed team (Prime + Subcontractors) with a 4-level color-coded scoring system anchored to Past Performance evidence. Includes a Labor Category (LCAT) overlay with bench strength tracking.",
            capabilityMatrixSchema: {
              title: "Capability Matrix Structure",
              description: "A grid where rows are RFQ requirements (3-level hierarchy) and columns are team members. Each cell is a capability score backed by past performance evidence.",
              rows: {
                level1: "Task Area (e.g., 'Analytics & Data Engineering', 'Cloud Technologies', 'Cybersecurity')",
                level2: "Sub-Area (e.g., 'AI/ML', 'Cloud-Native Architecture', 'Threat Prevention & Detection')",
                level3: "Specific Requirement (e.g., 'Method selection, model development and validation for AI/ML applications')",
                sowReference: "Each row traces to a SOW section reference (e.g., 'SOW § 8.13.1')",
                autoImport: "Rows auto-populate from AI Shredder extraction — requirements with SOW references already linked"
              },
              columns: "Prime Bidder + each Subcontractor/Teaming Partner (from Teaming Plan configured in Opportunity Intake, Step 1)",
              cells: "4-level capability score per team member per requirement"
            },
            scoringSystem: {
              title: "4-Level Capability Scoring",
              description: "Scoring anchored to Past Performance — not just 'can you do it' but 'can you PROVE you've done it.' This mirrors federal evaluator expectations.",
              levels: [
                { score: 0, color: "RED", label: "No Capability / No Past Performance", meaning: "Team member has no demonstrated capability. This is a gap that must be filled by a teaming partner or resolved." },
                { score: 1, color: "YELLOW", label: "Some Understanding", meaning: "Team member has theoretical knowledge but no contractual past performance to cite. Evaluators may rate this as 'Some Confidence' at best." },
                { score: 2, color: "GREEN", label: "Good Understanding with 1 Past Performance", meaning: "Team member has demonstrated capability with at least one relevant contract reference. Sufficient for compliance." },
                { score: 3, color: "BLUE", label: "Expert Understanding with 2+ Past Performances", meaning: "Team member is a proven expert with multiple contract references. This is your discriminator — use these cells to build Win Themes." }
              ]
            },
            lcatOverlay: {
              title: "Labor Category (LCAT) Map",
              description: "A staffing blueprint that maps RFQ-defined job titles to actual personnel. Education/experience thresholds come from the RFQ (ingested via AI Shredder); the team maps their candidates against those thresholds.",
              fields: [
                { name: "Labor Category", description: "RFQ-defined job title (e.g., 'Software Architect', 'Cyber Software Engineer', 'GIS Analyst')" },
                { name: "Task Areas Applicable", description: "Which of the 11+ task areas this LCAT covers" },
                { name: "Education Requirement", description: "Minimum degree level from RFQ (e.g., 'Master's in CS or related field')" },
                { name: "Experience Requirement", description: "Minimum years from RFQ (e.g., '10 years in software architecture')" },
                { name: "Proposed Candidate", description: "Actual person or 'TBD' from team's bench" },
                { name: "Bench Strength", description: "Number of qualified personnel available NOW — not just 'do you have the skill' but 'do you have the people ready?'" },
                { name: "Compliance Status", description: "Compliant / Non-Compliant — based on candidate's resume vs RFQ minimums" }
              ],
              complianceNote: "A non-compliant LCAT (candidate doesn't meet education/experience minimums) is a hard disqualification risk. The system flags these as CRITICAL."
            },
            aggregateOutputs: [
              { metric: "% GREEN/BLUE Coverage", description: "Percentage of requirement cells scored 2 or 3. Target: >80% for competitive bids." },
              { metric: "RED Cell Count", description: "Number of uncovered requirements. Each RED cell is a gap that needs a teaming partner or a No-Go signal." },
              { metric: "Bench Strength Index", description: "Average bench depth across critical LCATs. Low bench = high staffing risk on large BPAs." },
              { metric: "Past Performance Coverage", description: "% of task areas with at least one BLUE (expert) team member. Drives evaluator confidence." }
            ],
            taskAreaReference: {
              title: "Standard Federal IT-Services Task Areas",
              description: "11 task areas with ~50+ sub-areas covering the full spectrum of federal IT services. These serve as the row taxonomy for the capability matrix.",
              areas: [
                "1. Analytics & Data Engineering (BI, AI/ML, Data Warehousing, Big Data)",
                "2. Geographic Information Systems (GIS Platform, Transportation Use Cases)",
                "3. Cloud Technologies (Cloud-Native, Managed Services, DevSecOps, Migrations)",
                "4. Cybersecurity & Information Security (IAM, Threat Detection, Incident Response, FedRAMP)",
                "5. Application Development & Software Engineering (Full Lifecycle, Agile, CI/CD, Low-Code, 508)",
                "6. Maintenance & Operations (SW/DB Maintenance, Help Desk, Disaster Recovery, Site Installation)",
                "7. Process Engineering (Process Analysis, RPA, Tech Stack Alignment)",
                "8. Training, Documentation & Content Management (Tech Docs, UX/UI, Training Delivery)",
                "9. Project Management (Program Mgmt, Task Order Mgmt, Financial Mgmt, EPMR, Risk Mgmt)",
                "10. Future & Emerging Technologies (IoT, Blockchain, Edge Computing, Quantum, 5G)",
                "11. Software Development Models & Standards (ITIL, ISO 9001, CMMI, SWEBOK, DevSecOps, Pace Layering)"
              ]
            },
            connectionToOtherSteps: [
              { step: "Step 1 (Opportunity Intake)", connection: "Teaming Partners configured in Intake become the column headers in the Capability Matrix. Prime Bidder metadata pre-fills the first column." },
              { step: "Step 2 (Ingestion Hub)", connection: "AI Shredder extracts task areas and specific requirements from the RFQ/SOW to auto-populate matrix rows with SOW references." },
              { step: "Step 3 (Go/No-Go + Bid Pursuit Scorecard)", connection: "Aggregate capability scores (RED cell count, % coverage) feed Scorecard Q13 ('Do we have the technical resources?') and Q14 ('Do we have management support?'). The matrix output directly impacts P(Win)." },
              { step: "Step 5 (Compliance Tracker)", connection: "Capability gaps (RED/YELLOW cells) surface as 'Non-Compliant' or 'Partial' rows in the Compliance Matrix. LCAT compliance status feeds the Compliance Matrix's staffing compliance section." },
              { step: "Step 6 (Proposal Workspace)", connection: "BLUE cells (expert + 2 PPs) are flagged as Win Theme candidates. Writers see which team member's past performance to cite for each section." },
              { step: "Knowledge Base", connection: "Past Performance records, Key Personnel resumes, and certifications from the KB provide the evidence links for GREEN/BLUE capability scores." }
            ],
            aiIntegration: "The AI can scan uploaded capability matrices (CSV/Excel) against extracted RFQ requirements and auto-detect gaps: 'Warning: No team coverage for Task Area 4.2 (DevSecOps). Suggest finding a partner with NAICS 541511 expertise.' The pWin Oracle consumes aggregate scores (% coverage, RED count, bench strength) as structured inputs to its P(Win) calculation.",
            pipelineUseCase: "On large multi-award vehicles ($100M+), the Capability Matrix is the single most important teaming artifact. It lets the Capture Manager objectively compare partner combinations: 'Team A covers 85% GREEN/BLUE with 3 RED cells. Team B covers 92% with 1 RED cell but weaker bench strength.' This replaces relationship-based teaming decisions with data-driven partner selection."
          }
        ]
      },
      {
        step: 5,
        name: "Compliance Tracker",
        path: "/{workspace}/pursuit/{id}/compliance",
        icon: "ShieldCheck",
        color: "teal",
        purpose: "Compliance Matrix — maps every RFP requirement to a proposal section and tracks compliance status. Ensures nothing is missed before drafting begins.",
        entryPoint: "Sidebar or 'Next' from Gap Analysis.",
        actions: [
          "Auto-import extracted requirements as Compliance Matrix rows",
          "Map each requirement to a Proposal Volume/Section",
          "Set compliance status (Compliant, Partial, Non-Compliant, Not Addressed)",
          "Add evidence notes and cross-references",
          "Filter/sort by status, volume, source section",
          "Track overall coverage percentage"
        ],
        outputs: ["Compliance coverage % (feeds back into Fit Score)", "Requirement-to-section traceability for Proposal Workspace", "Gap alerts for unaddressed requirements"],
        dataCreated: "pursuit_compliance_matrix (section_reference, requirement_text, compliance_status, response_location, evidence_notes)",
        plannedEnhancements: [
          {
            id: "rtm-compliance-matrix",
            title: "Federal Compliance & Evaluation Matrix (Compliance Matrix)",
            status: "PLANNED",
            reference: "Modeled after a real federal compliance & evaluation matrix from a U.S. Government IT-services solicitation. Real-world artifact used as design reference.",
            summary: "A structured Compliance Matrix modeled after real federal compliance matrices. Bridges what the government wants (SOW) and how they grade the response (Evaluation Factors). Serves three functions simultaneously: Compliance Tracker, Evaluation Guide, and Instructional Map for technical writers.",
            schema: {
              title: "Compliance Matrix Row Schema",
              fields: [
                { name: "RFQ/SOW Reference", description: "Section, page, paragraph from the solicitation (e.g., 'SOW 3.2.1, pg. 8, para 4')" },
                { name: "Requirement Text", description: "The actual requirement language — auto-imported from AI Shredder or manually entered" },
                { name: "Evaluation Factor", description: "Which evaluation factor this maps to (Factor 1: Technical, Factor 2: Transition, Factor 3: Past Performance, etc.) with relative weight/importance" },
                { name: "Proposal Volume / Section", description: "Where in the proposal this is addressed (e.g., 'Volume I: Technical, Section 3.2')" },
                { name: "Compliance Status", description: "Compliant / Partial / Non-Compliant / Not Addressed" },
                { name: "Win Theme", description: "Strategic argument for why the team is the best fit for this requirement — not just compliance, but differentiation" },
                { name: "Confidence Self-Assessment", description: "High / Some / Low — mirrors the government's own Confidence Rating scale. Team self-rates before submission to predict evaluator scoring" },
                { name: "Writer Guidance", description: "Instructions and suggestions for technical writers (e.g., 'Emphasize high-volume recruitment capability, cite specific metrics')" },
                { name: "Evidence / Cross-Reference", description: "Links to Past Performance records, Key Personnel resumes, certifications that substantiate the response" }
              ]
            },
            evaluationFramework: {
              title: "Government Evaluation Rating Scales",
              description: "The Compliance Matrix captures evaluation criteria so the team can self-assess against the government's own grading rubric before submission.",
              technicalRatings: [
                { rating: "High Confidence", definition: "The Government has high confidence the Quoter understands the requirement, proposes a sound approach, and will succeed with little or no Government intervention." },
                { rating: "Some Confidence", definition: "The Government has some confidence the Quoter understands the requirement and will succeed with some Government intervention." },
                { rating: "Low Confidence", definition: "The Government has low confidence the Quoter understands the requirement or will succeed even with Government intervention." }
              ],
              pastPerformanceRatings: [
                { rating: "Acceptable", definition: "Demonstrates same/similar requirement and will be successful." },
                { rating: "Unacceptable", definition: "Does not demonstrate same/similar requirement and will not be successful." },
                { rating: "Neutral", definition: "Neither recent nor relevant — counts neither favorably nor unfavorably." }
              ]
            },
            keyDesignPrinciples: [
              {
                principle: "Win Theme as First-Class Field",
                detail: "Most compliance trackers stop at 'addressed / not addressed.' This Compliance Matrix includes a Win Theme column — the strategic argument for why the team is the best fit, not just that the box was checked."
              },
              {
                principle: "Evaluation Factor Weighting",
                detail: "Requirements are linked to their parent evaluation factor with descending importance captured (e.g., Technical Capability > Transition > Past Performance). This drives writer resource allocation — more review cycles on higher-weight factors."
              },
              {
                principle: "Page Limit Cross-Reference",
                detail: "Hard page limits (e.g., 35 pages for Technical Volume) are a disqualification risk. The Compliance Matrix cross-references page counts from the Proposal Workspace and flags when a volume approaches or exceeds its limit."
              },
              {
                principle: "SOW Restating Detection",
                detail: "Federal evaluators explicitly penalize proposals that 'merely restate the SOW.' The Confidence Self-Assessment and future AI evaluation simulation should flag sections that parrot requirement language without demonstrating specific capability."
              },
              {
                principle: "Confidence Rating as Pre-Submission QA",
                detail: "Before final submission, the BD lead reviews each Compliance Matrix row's self-assessed Confidence Rating. Rows rated 'Low Confidence' trigger mandatory revision cycles. This is the internal quality gate."
              }
            ],
            connectionToOtherSteps: [
              { step: "Step 2 (Ingestion Hub)", connection: "AI Shredder extracts requirements that auto-populate Compliance Matrix rows. RFP Confirmation Modal captures evaluation factors and page limits." },
              { step: "Step 3 (Go/No-Go + Bid Pursuit Scorecard)", connection: "Scorecard Q5 ('Requirements clearly defined?') and Q6 ('Evaluation criteria known?') are directly answerable from Compliance Matrix completeness. The Scorecard tells you whether to bid; the Compliance Matrix tells you how to bid." },
              { step: "Step 4 (Gap Analysis)", connection: "Gaps identified in Step 4 appear as 'Non-Compliant' or 'Not Addressed' rows in the Compliance Matrix. Gap resolution strategies feed the Win Theme and Writer Guidance columns." },
              { step: "Step 6 (Proposal Workspace)", connection: "Each proposal section in the editor is anchored to its Compliance Matrix rows. Writers see the requirement, win theme, and guidance inline while drafting." },
              { step: "Step 7 (Submission Guide)", connection: "Compliance Matrix compliance coverage % and page limit status feed the Submission Guide's readiness checklist." }
            ],
            aiIntegration: "The pWin Oracle's Reasoning Trace can simulate a government evaluator using the Confidence Rating definitions to predict whether a draft section would receive High, Some, or Low Confidence. This uses the same Claude 4.6 Adaptive Thinking engine as the Bid Pursuit Scorecard, but focused on section-level quality rather than pursuit-level viability."
          }
        ]
      },
      {
        step: 6,
        name: "Proposal Workspace",
        path: "/{workspace}/pursuit/{id}/proposal-workspace",
        icon: "FileText",
        color: "indigo",
        purpose: "AI-assisted proposal drafting organized by volume/section tree. Requirements are anchored to sections, and winning proposal text is surfaced as ghost text.",
        entryPoint: "Sidebar or 'Next' from Compliance Tracker.",
        actions: [
          "Navigate volume/section tree (Technical, Past Performance, Cost, Admin)",
          "View anchored 'Shall' requirement statements per section",
          "Generate AI draft shells (ghost text templates)",
          "Insert Source Recommendations from winning proposals (similarity matches)",
          "Edit and refine section content with auto-save (30s)",
          "Track section status (Not Started, Drafting, In Review, Compliant)",
          "View page limits and formatting lock"
        ],
        outputs: ["Drafted proposal sections", "Section compliance status", "Version history"],
        dataCreated: "proposal_drafts (section_name, content, version, status)"
      },
      {
        step: 7,
        name: "Submission Guide",
        path: "/{workspace}/pursuit/{id}/submission-guide",
        icon: "FileCheck",
        color: "emerald",
        purpose: "Final pre-submission launch checklist — live countdown, formatting compliance, red flag alerts, and deliverables verification.",
        entryPoint: "Sidebar or 'Next' from Proposal Workspace.",
        actions: [
          "Monitor live countdown timer to primary deadline",
          "Manage key deadlines (add/complete/delete with timeline view)",
          "Track formatting requirements (Critical/Strict/Info severity levels)",
          "Manage Red Flag Alerts (Amendments, Corrections, Deadline Changes)",
          "Verify deliverables checklist (volumes, certifications, forms)",
          "Open procurement portal link",
          "Export deadlines to calendar"
        ],
        outputs: ["Submission readiness status", "All deliverables verified", "Portal link for final upload"],
        dataCreated: "pursuit_deadlines, pursuit_formatting_requirements, pursuit_red_flags"
      }
    ],
    supportingScreens: [
      {
        name: "Marketplace Sentinel",
        path: "/sentinel",
        purpose: "SAM.gov opportunity scouting with NAICS/PSC auto-matching. Claimed opportunities enter the Pipeline at Step 0.",
        actions: ["Configure matching preferences", "Scan SAM.gov", "Review Gold/Silver/Bronze matches", "Claim to Pipeline or Dismiss"]
      },
      {
        name: "Knowledge Base",
        path: "/knowledge-base",
        purpose: "Corporate asset library — Past Performances, Key Personnel, Certifications. Referenced by AI Analysis and Proposal Workspace for source recommendations.",
        actions: ["Upload corporate documents", "Manage Past Performance records", "Update Key Personnel", "Maintain certifications"]
      },
      {
        name: "AI Configuration (BYOM)",
        path: "/admin/ai-config",
        purpose: "System-wide AI model configuration. Determines which LLM powers the pWin Oracle, Shredder, and Proposal Drafter.",
        actions: ["Select provider (Anthropic/Gemini/OpenAI)", "Configure model", "Set system fallback key", "Enable org-level BYOM overrides"]
      }
    ]
  },
  // ── Legacy screens list (kept for stats card) ─────────────────
  screens: [
    { name: "Pursuit Pipeline", path: "/pipeline", description: "Kanban view of all active pursuits by stage with FAR Phase badges", actions: ["Create pursuit", "Move stage", "Update FAR Phase", "Archive"] },
    { name: "Opportunity Intake", path: "/pursuit/{id}/intake", description: "Opportunity metadata capture with Agency/Bureau, NAICS, Teaming, Prime Bidder, FAR Phase", actions: ["Fill General Info", "Select Agency/Bureau", "Add Teaming Partners", "Set FAR Phase"] },
    { name: "Document Ingestion Hub", path: "/pursuit/{id}/ingestion", description: "6-group document upload, AI Shredding (SSE), RFP Confirmation, Run AI Analysis", actions: ["Upload to 6 groups", "AI Shred RFPs", "Confirm NAICS/PSC/Prime", "Run AI Analysis"] },
    { name: "Go/No-Go Dashboard", path: "/pursuit/{id}/gonogo", description: "Risk-assessment checkpoint with Fit Score, pWin Oracle, Competitive Intel, What-If Scenarios", actions: ["Review Fit Score", "Run pWin Oracle", "Add Competitors/Risks", "Record Decision"] },
    { name: "Gap Analysis & Teaming", path: "/pursuit/{id}/gap-analysis", description: "Map requirements to capabilities, identify gaps, assign teaming partners", actions: ["Review requirements", "Classify gaps", "Assign partners", "Track resolution"] },
    { name: "Compliance Tracker", path: "/pursuit/{id}/compliance", description: "Compliance Matrix — map every RFP requirement to proposal section", actions: ["Import requirements", "Map to sections", "Set compliance status", "Track coverage %"] },
    { name: "Proposal Workspace", path: "/pursuit/{id}/proposal-workspace", description: "AI-assisted proposal drafting with volume/section tree and source recommendations", actions: ["Draft sections", "Anchor requirements", "Import ghost text", "Auto-save"] },
    { name: "Submission Guide", path: "/pursuit/{id}/submission-guide", description: "Final pre-submission checklist with countdown, formatting rules, red flags", actions: ["Monitor deadline", "Check formatting", "Acknowledge red flags", "Export calendar"] },
    { name: "Marketplace Sentinel", path: "/sentinel", description: "SAM.gov opportunity scouting with NAICS/PSC auto-matching", actions: ["Configure preferences", "Scan SAM.gov", "Review matches", "Claim to Pipeline"] },
    { name: "Knowledge Base", path: "/knowledge-base", description: "Corporate asset library — Past Performance, Key Personnel, Certifications", actions: ["Upload docs", "Manage PP", "Update personnel", "Certifications"] },
    { name: "AI Configuration (BYOM)", path: "/admin/ai-config", description: "System-wide AI model configuration with multi-provider BYOM support", actions: ["Select provider", "Configure model", "Set system key", "Manage org overrides"] },
    { name: "Admin Dashboard", path: "/admin", description: "Platform administration for Super Admin", actions: ["Manage orgs", "View metrics", "System config", "Audit logs"] },
    { name: "Contractor Lifecycle", path: "/admin/lifecycle", description: "CRM view from lead to active contractor", actions: ["Promote leads", "Approve trials", "Assign consultants", "Grant slots"] }
  ],
  fitScoreLifecycle: {
    phases: [
      {
        id: 'prerequisites',
        name: 'Prerequisites (Gates)',
        range: 'No Score',
        trigger: 'Opportunity Creation, Field Entry & Document Upload',
        description: 'Metadata completeness and document uploads are prerequisites that ENABLE scoring — they do not contribute to the Fit Score. The widget shows a checklist of what is missing.',
        factors: [
          { name: 'Metadata Complete', description: 'Agency, NAICS Code, and Opportunity Description must be filled in', weight: 'Gate' },
          { name: 'RFP Document', description: 'At least 1 RFP/Solicitation document uploaded in the Ingestion Hub', weight: 'Gate' },
          { name: 'Capability Document', description: 'At least 1 Capability/Strategy document uploaded (or equivalent in Global Knowledge Base)', weight: 'Gate' }
        ]
      },
      {
        id: 'ai-analysis',
        name: 'Fit Score: 5-Factor AI Analysis',
        range: '0-100%',
        trigger: 'Manual "Run AI Analysis" on Ingestion Hub',
        description: 'The Fit Score measures alignment between the RFP requirements and the team\'s capabilities. Every percentage point represents actual measured fit. Calculated by AI across 5 weighted factors totaling 100%.',
        factors: [
          { name: 'NAICS/PSC Compliance', description: 'Extracted codes from RFP compared against Prime + Teaming Partners\' registered codes. Full match = 15%, partial = 5-10%, none = 0%.', weight: '0-15%' },
          { name: 'Proposal Similarity', description: 'Semantic matching of RFP requirements against winning proposals in Knowledge Base.', weight: '0-25%' },
          { name: 'Teaming Coverage', description: 'Role-to-requirement matching: extracted task areas mapped against team capabilities. Gaps reduce this factor.', weight: '0-25%' },
          { name: 'Agency Experience', description: 'Past performance records matching the target agency/bureau.', weight: '0-15%' },
          { name: 'Requirement Addressability', description: 'Percentage of extracted shall/must requirements addressable by capability docs and KB assets.', weight: '0-20%' }
        ]
      }
    ],
    tooltipText: "Fit Score measures alignment between the RFP and your team's capabilities. Run AI Analysis on the Ingestion Hub to calculate. Prerequisites (metadata + documents) must be complete first.",
    implementedChange: {
      status: "IMPLEMENTED",
      implementedDate: "April 5, 2026",
      title: "Fit Score Redesign — Score Measures Fit, Not Completeness",
      rationale: "The previous Fit Score rewarded data entry (Phase 1: 0-50%) and file uploads (Phase 2: 50-90%). A user who filled in all metadata and uploaded 6 document groups got ~90% before any analysis ran. That was a completeness score, not a fit score.",
      coreChange: "Phase 1 (Metadata) and Phase 2 (Documents) are now prerequisites/gates that ENABLE scoring. The Fit Score comes entirely from 5-factor AI comparative analysis.",
      uiStates: [
        { state: "PREREQUISITES INCOMPLETE", color: "red", description: "Missing metadata fields or documents. Shows a checklist of what's needed." },
        { state: "ANALYSIS REQUIRED", color: "amber", description: "Prerequisites met. Run AI Analysis on Ingestion Hub to calculate the score." },
        { state: "AI ANALYZED", color: "emerald", description: "Fit Score calculated. Shows the actual percentage from 5-factor analysis." }
      ],
      affectedComponents: [
        { component: "Risk Widget (PursuitRiskWidget)", change: "Shows prerequisite checklist, analysis prompt, or score gauge depending on state." },
        { component: "Floating HUD (RiskAssessmentHUD)", change: "Three visual states: red shield (prerequisites), amber target (analysis required), or score gauge." },
        { component: "Pursuit Pipeline Cards", change: "Shows 'Fit: —' for unanalyzed pursuits. Shows 'Fit: X%' only after AI Analysis." },
        { component: "Go/No-Go Dashboard", change: "Score ring chart and badge show 'PENDING ANALYSIS' until AI Analysis runs." },
        { component: "Score Source Badge", change: "'METADATA ONLY' badge retired. New badges: PREREQUISITES INCOMPLETE, ANALYSIS REQUIRED, AI ANALYZED." }
      ],
      futureScoreInputs: [
        { source: "Capability Matrix (Step 4)", factor: "% GREEN/BLUE coverage and RED cell count could augment the Teaming Coverage factor." },
        { source: "Compliance Tracker (Step 5)", factor: "Compliance Matrix compliance coverage % could feed a Requirement Traceability bonus." },
        { source: "Bid Pursuit Scorecard (Step 3)", factor: "P(Win) measures pursuit viability — complementary to Fit Score. Fit = 'Can we do it?', pWin = 'Should we do it?'" }
      ]
    }
  },
  scoreFidelity: {
    title: "Score Fidelity & Data Source Indicators",
    description: "Understanding the three Fit Score states and when to trust the numbers",
    badges: [
      {
        id: 'prerequisites-incomplete',
        label: 'PREREQUISITES INCOMPLETE',
        color: 'red',
        meaning: 'Missing required metadata or documents — no score available',
        dataPoints: ['Agency Identified', 'NAICS Code', 'Opportunity Description', 'RFP Document', 'Capability Document'],
        trustLevel: 'No score to trust. Complete the checklist first.',
        action: 'Fill in required metadata fields and upload documents in the Ingestion Hub.'
      },
      {
        id: 'analysis-required',
        label: 'ANALYSIS REQUIRED',
        color: 'amber',
        meaning: 'Prerequisites met but AI Analysis has not been run — no score yet',
        dataPoints: ['Metadata Complete', 'Documents Uploaded'],
        trustLevel: 'Ready for scoring. No score available until analysis runs.',
        action: 'Run AI Analysis on the Ingestion Hub to calculate the Fit Score.'
      },
      {
        id: 'ai-analyzed',
        label: 'AI ANALYZED',
        color: 'emerald',
        meaning: 'Fit Score calculated from 5-factor AI analysis (0-100%)',
        dataPoints: ['NAICS/PSC Compliance', 'Proposal Similarity', 'Teaming Coverage', 'Agency Experience', 'Requirement Addressability'],
        trustLevel: 'Authoritative. Suitable for Go/No-Go decisions and resource allocation.',
        action: 'Re-run AI Analysis after uploading new documents or updating teaming partners for a refreshed score.'
      }
    ],
    crossComponentSync: [
      { component: 'Pursuit Pipeline', displays: 'Fit: —  or  Fit: X%', behavior: 'Shows dash for unanalyzed pursuits, actual percentage after AI Analysis' },
      { component: 'Go/No-Go Dashboard', displays: 'Ring Chart + Badge', behavior: 'Shows PENDING ANALYSIS until AI Analysis runs, then populates ring chart' },
      { component: 'Risk Widget (Intake)', displays: 'Checklist / Prompt / Gauge', behavior: 'Three states: prerequisite checklist (red), analysis prompt (amber), score gauge (colored by risk)' },
      { component: 'Floating HUD', displays: 'Icon / Gauge', behavior: 'Red shield (prerequisites), amber target (analysis needed), circular gauge (AI score)' }
    ]
  }
};

// ============================================================================
// ARCHITECTURE
// ============================================================================
const ARCHITECTURE = {
  stack: [
    { layer: "Frontend", tech: "React 18 + Vite", description: "SPA with Tailwind CSS, shadcn/ui components" },
    { layer: "Backend", tech: "FastAPI (Python 3.11)", description: "Async REST API with Pydantic validation" },
    { layer: "Database", tech: "MongoDB", description: "Document store for flexible schema, multi-tenant" },
    { layer: "Auth", tech: "JWT + bcrypt", description: "Stateless auth with refresh tokens" },
    { layer: "AI/LLM", tech: "LLM Router (BYOM)", description: "Multi-provider: Anthropic Claude, Google Gemini, OpenAI GPT" },
    { layer: "AI Config", tech: "Pure BYOM", description: "Org's own configured key only — no platform-wide fallback" },
    { layer: "Storage", tech: "Local / S3", description: "Document storage with presigned URLs" },
    { layer: "Scheduler", tech: "APScheduler", description: "Background tasks for Sentinel scans" },
    { layer: "Deployment", tech: "Docker + K8s", description: "Containerized microservices deployment" },
  ],
  dataFlow: [
    { from: "User", to: "React App", action: "Interacts with UI" },
    { from: "React App", to: "FastAPI", action: "REST API calls with JWT" },
    { from: "FastAPI", to: "MongoDB", action: "CRUD operations" },
    { from: "FastAPI", to: "LLM Router", action: "AI analysis requests" },
    { from: "LLM Router", to: "Anthropic/Gemini/OpenAI", action: "Model calls with BYOM keys" },
    { from: "FastAPI", to: "SAM.gov API", action: "Sentinel opportunity fetch" },
    { from: "FastAPI", to: "S3", action: "Document upload/download" },
  ]
};

// ============================================================================
// ZERO-TRUST ARCHITECTURE
// ============================================================================
const ZERO_TRUST = {
  principles: [
    { name: "Never Trust, Always Verify", description: "Every request is authenticated and authorized, regardless of source" },
    { name: "Least Privilege", description: "Users have minimum permissions needed for their role" },
    { name: "Assume Breach", description: "System designed assuming attackers are already inside" },
    { name: "Verify Explicitly", description: "All access decisions based on all available data points" },
  ],
  implementation: [
    { control: "JWT Validation", description: "Every API request validates JWT signature, expiry, and claims" },
    { control: "Organization Scoping", description: "All database queries include organization_id from JWT claims" },
    { control: "Role-Based Access", description: "Endpoints enforce minimum role level from SECURITY.roles" },
    { control: "Audit Logging", description: "All state-changing operations logged with user context" },
    { control: "Input Validation", description: "Pydantic models validate all input data" },
    { control: "Rate Limiting", description: "API endpoints rate-limited to prevent abuse" },
  ],
  dataClassification: [
    { level: "Public", description: "Marketing content, pricing", examples: "Landing page, blog" },
    { level: "Internal", description: "Platform metrics, aggregated data", examples: "Admin dashboard stats" },
    { level: "Confidential", description: "Organization data, pursuits", examples: "Proposals, evaluations" },
    { level: "Restricted", description: "Authentication data, PII", examples: "Passwords, SSN" },
  ]
};

// ============================================================================
// COMPONENT
// ============================================================================
export default function SystemDocs() {
  const { authToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link support: read `?cat=` and `?tab=` from the URL on mount and
  // mirror navigation back into the URL. If only `?tab=` is provided we
  // resolve the parent category via SUBTAB_TO_CATEGORY so external links
  // can stay short (e.g. `/admin/docs?tab=release-notes`).
  const urlTab = searchParams.get('tab');
  const urlCat = searchParams.get('cat');
  const initialSubTab = urlTab && SUBTAB_TO_CATEGORY[urlTab] ? urlTab : 'release-notes';
  const initialCategory =
    (urlCat && NAV_HIERARCHY[urlCat]) ||
    SUBTAB_TO_CATEGORY[initialSubTab] ||
    'release';

  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [activeSubTab, setActiveSubTab] = useState(initialSubTab);
  const [expandedCategories, setExpandedCategories] = useState({ [initialCategory]: true });
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [copiedItem, setCopiedItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Keep the URL in sync when the user navigates inside the page.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (next.get('cat') !== activeCategory) next.set('cat', activeCategory);
    if (next.get('tab') !== activeSubTab) next.set('tab', activeSubTab);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, activeSubTab]);

  // React to URL changes from external navigation (e.g. another component
  // calls `navigate('/admin/docs?tab=database')`).
  useEffect(() => {
    const t = searchParams.get('tab');
    const c = searchParams.get('cat');
    if (t && SUBTAB_TO_CATEGORY[t] && t !== activeSubTab) {
      setActiveSubTab(t);
      const resolvedCat = c && NAV_HIERARCHY[c] ? c : SUBTAB_TO_CATEGORY[t];
      if (resolvedCat !== activeCategory) {
        setActiveCategory(resolvedCat);
        setExpandedCategories((prev) => ({ ...prev, [resolvedCat]: true }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Select sub-tab
  const selectSubTab = (categoryId, subTabId) => {
    setActiveCategory(categoryId);
    setActiveSubTab(subTabId);
    if (!expandedCategories[categoryId]) {
      setExpandedCategories(prev => ({ ...prev, [categoryId]: true }));
    }
  };

  // Toggle content section
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Copy to clipboard
  const copyToClipboard = (text, itemId) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(itemId);
    setTimeout(() => setCopiedItem(null), 2000);
  };

  // Filter navigation based on search
  const filteredNav = useMemo(() => {
    if (!searchQuery.trim()) return NAV_HIERARCHY;
    
    const query = searchQuery.toLowerCase();
    const filtered = {};
    
    Object.entries(NAV_HIERARCHY).forEach(([catId, category]) => {
      const matchingSubTabs = category.subTabs.filter(
        sub => sub.label.toLowerCase().includes(query) || 
               sub.id.toLowerCase().includes(query)
      );
      
      if (matchingSubTabs.length > 0 || 
          category.label.toLowerCase().includes(query)) {
        filtered[catId] = {
          ...category,
          subTabs: matchingSubTabs.length > 0 ? matchingSubTabs : category.subTabs
        };
      }
    });
    
    return filtered;
  }, [searchQuery]);

  // Get color classes
  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: 'text-blue-600' },
      red: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: 'text-red-600' },
      purple: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: 'text-purple-600' },
      teal: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', icon: 'text-teal-600' },
      emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: 'text-emerald-600' },
      amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: 'text-amber-600' },
    };
    return colors[color] || colors.blue;
  };

  const getMethodColor = (method) => {
    switch (method) {
      case "GET": return "bg-emerald-100 text-emerald-700";
      case "POST": return "bg-blue-100 text-blue-700";
      case "PUT": return "bg-amber-100 text-amber-700";
      case "PATCH": return "bg-purple-100 text-purple-700";
      case "DELETE": return "bg-red-100 text-red-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  return (
    <div data-testid="system-docs-page" className="flex min-h-screen">
      {/* Sidebar Navigation */}
      <div className="w-72 bg-slate-50 border-r border-slate-200 flex-shrink-0">
        <div className="p-4 border-b border-slate-200">
          <h2 className="font-semibold text-slate-900 mb-3">Documentation</h2>
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search docs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-white border-slate-200"
              data-testid="doc-search-input"
            />
          </div>
        </div>

        {/* Navigation Categories */}
        <div className="p-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 140px)' }}>
          {Object.entries(filteredNav).map(([categoryId, category]) => {
            const colorClasses = getColorClasses(category.color);
            const CategoryIcon = category.icon;
            const isExpanded = expandedCategories[categoryId];
            
            return (
              <div key={categoryId} className="mb-2">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(categoryId)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-left transition-all",
                    "hover:bg-slate-100",
                    activeCategory === categoryId && `${colorClasses.bg} ${colorClasses.border} border`
                  )}
                  data-testid={`nav-category-${categoryId}`}
                >
                  <div className={cn("p-1.5 rounded-md", colorClasses.bg)}>
                    <CategoryIcon className={cn("w-4 h-4", colorClasses.icon)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={cn("text-sm font-medium", activeCategory === categoryId ? colorClasses.text : 'text-slate-700')}>
                      {category.label}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{category.description}</p>
                  </div>
                  <ChevronRight className={cn(
                    "w-4 h-4 text-slate-400 transition-transform",
                    isExpanded && "rotate-90"
                  )} />
                </button>

                {/* Sub-tabs */}
                {isExpanded && (
                  <div className="ml-4 mt-1 space-y-0.5">
                    {category.subTabs.map((subTab) => {
                      const SubIcon = subTab.icon;
                      const isActive = activeSubTab === subTab.id && activeCategory === categoryId;
                      
                      return (
                        <button
                          key={subTab.id}
                          onClick={() => selectSubTab(categoryId, subTab.id)}
                          className={cn(
                            "w-full flex items-center gap-2 px-3 py-2 rounded-md text-left transition-all",
                            "hover:bg-slate-100",
                            isActive && `${colorClasses.bg} ${colorClasses.text}`
                          )}
                          data-testid={`nav-subtab-${subTab.id}`}
                        >
                          <SubIcon className={cn("w-4 h-4", isActive ? colorClasses.icon : 'text-slate-400')} />
                          <span className={cn("text-sm", isActive ? 'font-medium' : 'text-slate-600')}>
                            {subTab.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <PageHeader 
          title="System Documentation"
          description="Technical reference for FedBD platform architecture, APIs, and AI agents"
          action={
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => copyToClipboard(window.location.href, 'doc-page-link')}
                className="border-slate-200"
                data-testid="copy-doc-link-btn"
                title="Copy a deep link to this exact tab"
              >
                {copiedItem === 'doc-page-link' ? (
                  <Check className="w-4 h-4 mr-2 text-emerald-600" />
                ) : (
                  <Link2 className="w-4 h-4 mr-2" />
                )}
                {copiedItem === 'doc-page-link' ? 'Copied!' : 'Copy link'}
              </Button>
              <Button
                variant="outline"
                disabled={isLoading}
                className="border-slate-200"
              >
                <RefreshCw className={cn("w-4 h-4 mr-2", isLoading && "animate-spin")} />
                Refresh
              </Button>
            </div>
          }
        />

        <div className="p-6">
          {/* Breadcrumb Navigation */}
          <div className="flex items-center gap-2 mb-4 text-sm" data-testid="doc-breadcrumb">
            <span className="text-slate-500">Documentation</span>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <span className={cn(
              "font-medium",
              NAV_HIERARCHY[activeCategory]?.color === 'blue' && 'text-blue-700',
              NAV_HIERARCHY[activeCategory]?.color === 'red' && 'text-red-700',
              NAV_HIERARCHY[activeCategory]?.color === 'purple' && 'text-purple-700',
              NAV_HIERARCHY[activeCategory]?.color === 'teal' && 'text-teal-700',
              NAV_HIERARCHY[activeCategory]?.color === 'emerald' && 'text-emerald-700',
              NAV_HIERARCHY[activeCategory]?.color === 'amber' && 'text-amber-700'
            )}>
              {NAV_HIERARCHY[activeCategory]?.label}
            </span>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <span className="font-semibold text-slate-900">
              {NAV_HIERARCHY[activeCategory]?.subTabs.find(s => s.id === activeSubTab)?.label}
            </span>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">DB Tables</p>
                    <p className="text-xl font-bold text-slate-900 mt-1">30</p>
                  </div>
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Database className="w-4 h-4 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">API Endpoints</p>
                    <p className="text-xl font-bold text-slate-900 mt-1">
                      {Object.values(API_ENDPOINTS).reduce((sum, cat) => sum + cat.endpoints.length, 0)}
                    </p>
                  </div>
                  <div className="p-2 bg-emerald-50 rounded-lg">
                    <Server className="w-4 h-4 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">AI Agents</p>
                    <p className="text-xl font-bold text-slate-900 mt-1">{AI_AGENTS.length}</p>
                  </div>
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <Bot className="w-4 h-4 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Workflows</p>
                    <p className="text-xl font-bold text-slate-900 mt-1">{WORKFLOWS.screens.length}</p>
                  </div>
                  <div className="p-2 bg-amber-50 rounded-lg">
                    <Workflow className="w-4 h-4 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Dynamic Content Based on Active Tab */}
          {activeSubTab === 'architecture' && <ArchitectureContent />}
          {activeSubTab === 'database' && <DatabaseContent toggleSection={toggleSection} expandedSections={expandedSections} copyToClipboard={copyToClipboard} copiedItem={copiedItem} />}
          {activeSubTab === 'api' && <ApiContent toggleSection={toggleSection} expandedSections={expandedSections} getMethodColor={getMethodColor} copyToClipboard={copyToClipboard} copiedItem={copiedItem} />}
          {activeSubTab === 'security' && <SecurityContent />}
          {activeSubTab === 'zero-trust' && <ZeroTrustContent />}
          {activeSubTab === 'rbac-audit' && <RbacAuditContent />}
          {activeSubTab === 'auth-roadmap' && <AuthRoadmapContent />}
          {activeSubTab === 'agents' && <AgentsContent />}
          {activeSubTab === 'byom' && <BYOMContent />}
          {activeSubTab === 'sentinel' && <SentinelContent />}
          {activeSubTab === 'ebuy-sentinel' && <EbuySentinelContent />}
          {activeSubTab === 'collaboration' && <CollaborationContent />}
          {activeSubTab === 'workflows' && <WorkflowsContent />}
          {activeSubTab === 'versioning' && <VersioningPolicyContent />}
          {activeSubTab === 'functional-guide' && <OwnersGuideContent toggleSection={toggleSection} expandedSections={expandedSections} />}
          {activeSubTab === 'signup-flow' && <SignupFlowContent toggleSection={toggleSection} expandedSections={expandedSections} />}
          {activeSubTab === 'consultant-guide' && <ConsultantGuideContent toggleSection={toggleSection} expandedSections={expandedSections} />}
          {activeSubTab === 'contractor-account' && <ContractorAccountContent />}
          {activeSubTab === 'brand-assets' && <BrandAssetsContent />}
          {activeSubTab === 'stripe-overview' && <StripeOverviewContent />}
          {activeSubTab === 'plan-catalog' && <PlanCatalogContent />}
          {activeSubTab === 'ai-credits' && <AiCreditBillingContent />}
          {activeSubTab === 'webhooks-audit' && <WebhooksAuditContent />}
          {activeSubTab === 'release-notes' && <ReleaseNotesAutoFeed />}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CONTENT COMPONENTS
// ============================================================================

function ArchitectureContent() {
  return (
    <div className="space-y-6">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2">
            <Network className="w-5 h-5 text-blue-600" />
            Technology Stack
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100">
            {ARCHITECTURE.stack.map((item, idx) => (
              <div key={idx} className="p-4 hover:bg-slate-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{item.layer}</p>
                    <p className="text-sm text-slate-500">{item.description}</p>
                  </div>
                  <Badge className="bg-blue-100 text-blue-700 border-0">{item.tech}</Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2">
            <ArrowRight className="w-5 h-5 text-emerald-600" />
            Data Flow
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="space-y-3">
            {ARCHITECTURE.dataFlow.map((flow, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Badge className="bg-slate-200 text-slate-700">{flow.from}</Badge>
                <ArrowRight className="w-4 h-4 text-slate-400" />
                <Badge className="bg-slate-200 text-slate-700">{flow.to}</Badge>
                <span className="text-sm text-slate-600 ml-2">{flow.action}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function DatabaseContent({ toggleSection, expandedSections, copyToClipboard, copiedItem }) {
  return (
    <div className="space-y-4">
      {Object.entries(DATABASE_TABLES).map(([key, category]) => (
        <Card key={key} className="border-slate-200 shadow-sm">
          <CardHeader 
            className="cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
            onClick={() => toggleSection(`db-${key}`)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Table className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-base">{category.title}</CardTitle>
                  <p className="text-sm text-slate-500">{category.tables.length} tables</p>
                </div>
              </div>
              {expandedSections[`db-${key}`] ? 
                <ChevronDown className="w-5 h-5 text-slate-400" /> : 
                <ChevronRight className="w-5 h-5 text-slate-400" />
              }
            </div>
          </CardHeader>
          {expandedSections[`db-${key}`] && (
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {category.tables.map((table, idx) => (
                  <div key={idx} className="p-4 hover:bg-slate-50">
                    <div className="flex items-start justify-between mb-2">
                      <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded text-blue-700">
                        {table.name}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(table.columns, `table-${table.name}`)}
                        className="h-7"
                      >
                        {copiedItem === `table-${table.name}` ? (
                          <Check className="w-3 h-3 text-emerald-600" />
                        ) : (
                          <Copy className="w-3 h-3 text-slate-400" />
                        )}
                      </Button>
                    </div>
                    <p className="text-sm text-slate-600 mb-2">{table.description}</p>
                    <p className="text-xs text-slate-400 font-mono">{table.columns}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
}

function ApiContent({ toggleSection, expandedSections, getMethodColor, copyToClipboard, copiedItem }) {
  return (
    <div className="space-y-4">
      {Object.entries(API_ENDPOINTS).map(([key, category]) => (
        <Card key={key} className="border-slate-200 shadow-sm">
          <CardHeader 
            className="cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
            onClick={() => toggleSection(`api-${key}`)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Server className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-base">{category.title}</CardTitle>
                  <p className="text-sm text-slate-500">{category.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-slate-100 text-slate-600">{category.endpoints.length} endpoints</Badge>
                {expandedSections[`api-${key}`] ? 
                  <ChevronDown className="w-5 h-5 text-slate-400" /> : 
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                }
              </div>
            </div>
          </CardHeader>
          {expandedSections[`api-${key}`] && (
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {category.endpoints.map((endpoint, idx) => (
                  <div key={idx} className="p-4 hover:bg-slate-50">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge className={cn("font-mono text-xs", getMethodColor(endpoint.method))}>
                        {endpoint.method}
                      </Badge>
                      <code className="text-sm font-mono text-slate-700">{endpoint.path}</code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(endpoint.path, `api-${key}-${idx}`)}
                        className="h-6 ml-auto"
                      >
                        {copiedItem === `api-${key}-${idx}` ? (
                          <Check className="w-3 h-3 text-emerald-600" />
                        ) : (
                          <Copy className="w-3 h-3 text-slate-400" />
                        )}
                      </Button>
                    </div>
                    <p className="text-sm text-slate-600">{endpoint.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
}

function SecurityContent() {
  return (
    <div className="space-y-6">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            User Roles
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100">
            {SECURITY.roles.map((role, idx) => (
              <div key={idx} className="p-4 hover:bg-slate-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-sm font-medium text-slate-900">{role.name}</p>
                    <p className="text-sm text-slate-500">{role.description}</p>
                  </div>
                  <Badge className="bg-purple-100 text-purple-700">Level {role.level}</Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-600" />
            Data Isolation Rules
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="space-y-3">
            {SECURITY.dataIsolation.map((rule, idx) => (
              <div key={idx} className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                <p className="font-medium text-emerald-800">{rule.rule}</p>
                <p className="text-sm text-emerald-600">{rule.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Known Security Gaps
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100">
            {SECURITY.knownGaps.map((gap, idx) => (
              <div key={idx} className="p-4 hover:bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-slate-100 text-slate-600 font-mono">{gap.id}</Badge>
                    <Badge className={cn(
                      gap.severity === 'High' && 'bg-red-100 text-red-700',
                      gap.severity === 'Medium' && 'bg-amber-100 text-amber-700',
                      gap.severity === 'Low' && 'bg-blue-100 text-blue-700'
                    )}>{gap.severity}</Badge>
                  </div>
                  <Badge className={cn(
                    gap.status === 'Fixed' && 'bg-emerald-100 text-emerald-700',
                    gap.status === 'In Progress' && 'bg-blue-100 text-blue-700',
                    gap.status.includes('Planned') && 'bg-purple-100 text-purple-700',
                    gap.status === 'Backlog' && 'bg-slate-100 text-slate-600'
                  )}>{gap.status}</Badge>
                </div>
                <p className="text-sm text-slate-600">{gap.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ZeroTrustContent() {
  return (
    <div className="space-y-6" data-testid="zero-trust-tab-content">
      <Card className="border-red-200 shadow-sm bg-gradient-to-r from-red-50 to-white">
        <CardHeader className="border-b border-red-100">
          <CardTitle className="flex items-center gap-2 text-red-800">
            <Eye className="w-5 h-5" />
            Zero-Trust Architecture
          </CardTitle>
          <p className="text-sm text-red-600">Never trust, always verify - security by design</p>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {ZERO_TRUST.principles.map((principle, idx) => (
              <div key={idx} className="p-4 bg-white rounded-lg border border-red-100">
                <p className="font-semibold text-red-800 mb-1">{principle.name}</p>
                <p className="text-sm text-slate-600">{principle.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-slate-600" />
            Implementation Controls
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="space-y-3">
            {ZERO_TRUST.implementation.map((control, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <Check className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900">{control.control}</p>
                  <p className="text-sm text-slate-600">{control.description}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-blue-600" />
            Data Classification
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100">
            {ZERO_TRUST.dataClassification.map((level, idx) => (
              <div key={idx} className="p-4 hover:bg-slate-50">
                <div className="flex items-center justify-between mb-1">
                  <Badge className={cn(
                    idx === 0 && 'bg-emerald-100 text-emerald-700',
                    idx === 1 && 'bg-blue-100 text-blue-700',
                    idx === 2 && 'bg-amber-100 text-amber-700',
                    idx === 3 && 'bg-red-100 text-red-700'
                  )}>{level.level}</Badge>
                </div>
                <p className="text-sm text-slate-600">{level.description}</p>
                <p className="text-xs text-slate-400 mt-1">Examples: {level.examples}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function RbacAuditContent() {
  return (
    <div className="space-y-6">
      <Card className="border-amber-200 shadow-sm bg-gradient-to-r from-amber-50 to-white">
        <CardHeader className="border-b border-amber-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-xl">
                <Shield className="w-6 h-6 text-amber-700" />
              </div>
              <div>
                <CardTitle className="text-xl text-amber-900">RBAC & Security Audit Report</CardTitle>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className="bg-emerald-100 text-emerald-700">19/19 PASSED</Badge>
                  <p className="text-sm text-amber-600">Permission mapping and data isolation verification | March 5, 2026</p>
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 bg-white rounded-lg border border-emerald-200">
              <p className="text-3xl font-bold text-emerald-600">19</p>
              <p className="text-sm text-slate-600">Items Passed</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border border-amber-200">
              <p className="text-3xl font-bold text-amber-600">1</p>
              <p className="text-sm text-slate-600">Minor Enhancement</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border border-emerald-200">
              <p className="text-3xl font-bold text-emerald-600">0</p>
              <p className="text-sm text-slate-600">Critical Issues</p>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900">1. Role Permission Verification</h3>
            
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <p className="font-medium text-slate-900">Contractor Admin</p>
                <Badge className="bg-emerald-100 text-emerald-700">VERIFIED</Badge>
              </div>
              <ul className="text-sm text-slate-600 space-y-1">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Team management (Add/Remove users)</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> View all pursuits in org</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Full Knowledge Base access</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Delete pursuits</li>
              </ul>
            </div>

            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <p className="font-medium text-slate-900">Bid Manager</p>
                <Badge className="bg-emerald-100 text-emerald-700">VERIFIED</Badge>
              </div>
              <ul className="text-sm text-slate-600 space-y-1">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Create/Edit pursuits</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Upload to Knowledge Base</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Run AI analysis</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-slate-400" /> Cannot delete pursuits</li>
              </ul>
            </div>

            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <p className="font-medium text-slate-900">Reviewer</p>
                <Badge className="bg-emerald-100 text-emerald-700">VERIFIED</Badge>
              </div>
              <ul className="text-sm text-slate-600 space-y-1">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Read-only access to assigned pursuits</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Add comments/annotations</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-slate-400" /> No Knowledge Base access</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-slate-400" /> Cannot create/edit pursuits</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AgentsContent() {
  return (
    <div className="space-y-4">
      <Card className="border-purple-200 shadow-sm bg-gradient-to-r from-purple-50 to-white mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-purple-800">
            <Bot className="w-5 h-5" />
            AI Agent Registry
          </CardTitle>
          <p className="text-sm text-purple-600">Autonomous agents powering pursuit intelligence</p>
        </CardHeader>
      </Card>

      <div className="grid gap-4">
        {AI_AGENTS.map((agent, idx) => (
          <Card key={idx} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "p-2 rounded-lg",
                    agent.status === 'active' ? 'bg-emerald-100' : 'bg-amber-100'
                  )}>
                    <Bot className={cn(
                      "w-5 h-5",
                      agent.status === 'active' ? 'text-emerald-600' : 'text-amber-600'
                    )} />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{agent.name}</p>
                    <p className="text-sm text-slate-500">{agent.description}</p>
                  </div>
                </div>
                <Badge className={cn(
                  agent.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                )}>
                  {agent.status === 'active' ? 'Active' : 'Planned'}
                </Badge>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Triggers</p>
                  <div className="flex flex-wrap gap-1">
                    {agent.triggers.map((trigger, tIdx) => (
                      <Badge key={tIdx} variant="outline" className="text-xs">{trigger}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Outputs</p>
                  <div className="flex flex-wrap gap-1">
                    {agent.outputs.map((output, oIdx) => (
                      <Badge key={oIdx} className="bg-purple-50 text-purple-700 text-xs">{output}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// BYOM CONTENT
// ============================================================================
function BYOMContent() {
  return (
    <div className="space-y-6">
      <Card className="border-violet-200 shadow-sm bg-gradient-to-r from-violet-50 to-white mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-violet-800">
            <Zap className="w-5 h-5" />
            LLM Router & BYOM (Bring Your Own Model)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-600 mb-4">
            The LLM Router provides model-agnostic AI capabilities. Every AI call resolves its
            provider key from the calling organization's own BYOM configuration — there is no
            platform-wide fallback key.
          </p>
        </CardContent>
      </Card>

      {/* Key Hierarchy */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-slate-800">API Key Resolution</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-violet-50 rounded-lg border border-violet-200">
            <div className="w-8 h-8 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold">1</div>
            <div className="flex-1">
              <h4 className="font-semibold text-violet-900">Organization Key (BYOM) — required</h4>
              <p className="text-sm text-violet-700">The org must have BYOM enabled with a valid API key configured under Settings → AI Config. See <code className="bg-slate-100 px-1 rounded">resolve_llm_config()</code> in <code className="bg-slate-100 px-1 rounded">app/routes/llm_router.py</code>.</p>
            </div>
            <Badge className="bg-violet-600">Only Source</Badge>
          </div>
          <div className="flex justify-center"><div className="w-0.5 h-4 bg-slate-300" /></div>
          <div className="flex items-center gap-4 p-4 bg-rose-50 rounded-lg border border-rose-200">
            <div className="w-8 h-8 rounded-full bg-rose-600 text-white flex items-center justify-center font-bold">✕</div>
            <div className="flex-1">
              <h4 className="font-semibold text-rose-900">No org key configured</h4>
              <p className="text-sm text-rose-700">Raises <code className="bg-slate-100 px-1 rounded">BYOM_NOT_CONFIGURED</code>. There is no system-wide or vendor-hosted fallback key — callers without an org key (e.g. anonymous invocations) get a graceful per-feature fallback (static response) rather than an LLM call.</p>
            </div>
            <Badge className="bg-rose-600">Error / Fallback</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Supported Providers */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-slate-800">Supported LLM Providers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <h4 className="font-semibold text-orange-800">Anthropic Claude</h4>
              <ul className="text-sm text-orange-700 mt-2 space-y-1">
                <li>• Claude Sonnet 4.6 ✨</li>
                <li>• Claude Opus 4.6 ✨</li>
                <li>• Claude 4 Sonnet ✨</li>
              </ul>
              <p className="text-xs text-orange-600 mt-2">✨ Extended Thinking supported</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-800">Google Gemini</h4>
              <ul className="text-sm text-blue-700 mt-2 space-y-1">
                <li>• Gemini 2.5 Flash</li>
                <li>• Gemini 2.5 Pro</li>
                <li>• Gemini 3 Flash Preview</li>
              </ul>
              <p className="text-xs text-blue-600 mt-2">Default provider (Universal Key)</p>
            </div>
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <h4 className="font-semibold text-emerald-800">OpenAI GPT</h4>
              <ul className="text-sm text-emerald-700 mt-2 space-y-1">
                <li>• GPT-5.2</li>
                <li>• GPT-5.1</li>
                <li>• O3 ✨</li>
              </ul>
              <p className="text-xs text-emerald-600 mt-2">✨ O3 supports reasoning</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* pWin Oracle */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-slate-800">pWin Oracle (Go/No-Go Engine)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-slate-600">
            The pWin Oracle uses your configured LLM to analyze pursuit data and provide strategic Go/No-Go recommendations.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-slate-800 mb-2">Inputs</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Opportunity metadata (agency, NAICS, set-aside)</li>
                <li>• Estimated value and contract type</li>
                <li>• Evaluation dimension scores (1-5 scale)</li>
                <li>• Additional context from user</li>
              </ul>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-slate-800 mb-2">Outputs</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Decision: GO / NO_GO / CONDITIONAL</li>
                <li>• Confidence score (0-100%)</li>
                <li>• pWin percentage estimate</li>
                <li>• Factor analysis with rationale</li>
                <li>• Claude's thinking trace (if BYOM)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// SENTINEL CONTENT
// ============================================================================
function SentinelContent() {
  return (
    <div className="space-y-6">
      <Card className="border-teal-200 shadow-sm bg-gradient-to-r from-teal-50 to-white mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-teal-800">
            <Target className="w-5 h-5" />
            SAM.gov Sentinel - Autonomous Opportunity Scouting
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-600 mb-4">
            Sentinel is an AI agent that continuously monitors SAM.gov for new contract opportunities 
            matching your organization's NAICS/PSC codes and automatically surfaces relevant matches.
          </p>
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-slate-800">How Sentinel Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold shrink-0">1</div>
              <div>
                <h4 className="font-semibold text-slate-800">Configure Preferences</h4>
                <p className="text-sm text-slate-600">Set your NAICS codes, PSC codes, value range, and eligible set-asides in Settings → Integrations</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold shrink-0">2</div>
              <div>
                <h4 className="font-semibold text-slate-800">Automated Scanning</h4>
                <p className="text-sm text-slate-600">Sentinel queries SAM.gov API every 4 hours (or on-demand via "Run Scan")</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold shrink-0">3</div>
              <div>
                <h4 className="font-semibold text-slate-800">Match Scoring</h4>
                <p className="text-sm text-slate-600">Each opportunity is scored based on NAICS/PSC alignment, value range, and set-aside eligibility</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold shrink-0">4</div>
              <div>
                <h4 className="font-semibold text-slate-800">Review & Ingest</h4>
                <p className="text-sm text-slate-600">Review matches on the Sentinel dashboard, then one-click ingest to your pursuit pipeline</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Match Tiers */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-slate-800">Match Tier Classification</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-300">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                <h4 className="font-semibold text-yellow-800">Gold Match (80-100%)</h4>
              </div>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Exact NAICS match</li>
                <li>• Set-aside eligibility confirmed</li>
                <li>• Within value range</li>
                <li>• High priority - review immediately</li>
              </ul>
            </div>
            <div className="p-4 bg-slate-100 rounded-lg border border-slate-300">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-4 h-4 rounded-full bg-slate-400"></div>
                <h4 className="font-semibold text-slate-700">Silver Match (50-79%)</h4>
              </div>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Related NAICS code</li>
                <li>• Partial criteria match</li>
                <li>• May need manual review</li>
                <li>• Consider for pipeline</li>
              </ul>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-4 h-4 rounded-full bg-orange-400"></div>
                <h4 className="font-semibold text-orange-700">Bronze Match (25-49%)</h4>
              </div>
              <ul className="text-sm text-orange-600 space-y-1">
                <li>• Weak alignment</li>
                <li>• Edge case opportunity</li>
                <li>• Low priority</li>
                <li>• Review if capacity allows</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* FAR Lifecycle Integration */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-slate-800">FAR Lifecycle Phase Tracker</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-slate-600 mb-4">
            Pursuits are tracked through the 6 FAR procurement phases from initial forecast to final solicitation.
          </p>
          <div className="flex items-center justify-between bg-slate-50 p-4 rounded-lg overflow-x-auto">
            {['FORECAST', 'SPECIAL_NOTICE', 'SOURCES_SOUGHT', 'PRE_SOLICITATION', 'SOLICITATION', 'COMBINED'].map((phase, idx) => (
              <div key={phase} className="flex items-center">
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${idx === 4 ? 'bg-violet-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
                  {phase.replace('_', ' ')}
                </div>
                {idx < 5 && <ChevronRight className="w-4 h-4 text-slate-400 mx-1" />}
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-500">
            Sentinel auto-detects notice type and suggests the appropriate FAR phase when ingesting opportunities.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function WorkflowsContent() {
  const lifecycle = WORKFLOWS.pursuitLifecycle;
  const [searchParams, setSearchParams] = useSearchParams();
  const stepColors = {
    slate: { bg: 'bg-slate-100', border: 'border-slate-300', text: 'text-slate-700', dot: 'bg-slate-600', light: 'bg-slate-50' },
    blue: { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-700', dot: 'bg-blue-600', light: 'bg-blue-50' },
    violet: { bg: 'bg-violet-100', border: 'border-violet-300', text: 'text-violet-700', dot: 'bg-violet-600', light: 'bg-violet-50' },
    amber: { bg: 'bg-amber-100', border: 'border-amber-300', text: 'text-amber-700', dot: 'bg-amber-600', light: 'bg-amber-50' },
    orange: { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-700', dot: 'bg-orange-600', light: 'bg-orange-50' },
    teal: { bg: 'bg-teal-100', border: 'border-teal-300', text: 'text-teal-700', dot: 'bg-teal-600', light: 'bg-teal-50' },
    indigo: { bg: 'bg-indigo-100', border: 'border-indigo-300', text: 'text-indigo-700', dot: 'bg-indigo-600', light: 'bg-indigo-50' },
    emerald: { bg: 'bg-emerald-100', border: 'border-emerald-300', text: 'text-emerald-700', dot: 'bg-emerald-600', light: 'bg-emerald-50' },
  };

  // Active lifecycle step driven by `?step=<n>` so it survives reloads and
  // is shareable. Default = Step 1 (Opportunity Intake) on first visit;
  // Step 0 is the Hub and always reachable, just not the default. Invalid
  // values fall back to Step 1.
  const validSteps = lifecycle.steps.map((s) => s.step);
  const stepFromUrl = parseInt(searchParams.get('step'), 10);
  const initialStep = validSteps.includes(stepFromUrl) ? stepFromUrl : 1;
  const [activeStep, setActiveStep] = useState(initialStep);
  const [linkCopied, setLinkCopied] = useState(false);

  // Mirror the active step back into the URL alongside the existing
  // `?cat=engineering&tab=workflows` params from the parent shell.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (parseInt(next.get('step'), 10) !== activeStep) {
      next.set('step', String(activeStep));
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  // React to URL changes from outside (e.g., Copy-link paste).
  useEffect(() => {
    const fromUrl = parseInt(searchParams.get('step'), 10);
    if (validSteps.includes(fromUrl) && fromUrl !== activeStep) {
      setActiveStep(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const activeStepObj = lifecycle.steps.find((s) => s.step === activeStep) || lifecycle.steps[0];
  const activeIdx = lifecycle.steps.findIndex((s) => s.step === activeStep);
  const prevStep = activeIdx > 0 ? lifecycle.steps[activeIdx - 1] : null;
  const nextStep = activeIdx < lifecycle.steps.length - 1 ? lifecycle.steps[activeIdx + 1] : null;

  const copyStepLink = () => {
    const url = `${window.location.origin}${window.location.pathname}?cat=engineering&tab=workflows&step=${activeStep}`;
    Promise.resolve(navigator.clipboard.writeText(url))
      .then(() => {
        setLinkCopied(true);
        toast.success(`Link to Step ${activeStep} copied`, { id: 'lifecycle-step-link' });
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch(() => toast.error('Could not copy link', { id: 'lifecycle-step-link' }));
  };

  return (
    <div className="space-y-6">
      <Card className="border-amber-200 shadow-sm bg-gradient-to-r from-amber-50 to-white mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <Workflow className="w-5 h-5" />
            Application Workflows
          </CardTitle>
          <p className="text-sm text-amber-600">Screen-by-screen functionality map</p>
        </CardHeader>
      </Card>

      {/* ── PURSUIT LIFECYCLE FLOW ────────────────────────────────── */}
      <Card className="border-slate-300 shadow-md" data-testid="pursuit-lifecycle-section">
        <CardHeader className="border-b border-slate-200 bg-slate-900 rounded-t-lg">
          <CardTitle className="flex items-center gap-2 text-white text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Workflow className="w-5 h-5" />
            {lifecycle.title}
          </CardTitle>
          <p className="text-sm text-slate-300">{lifecycle.subtitle}</p>
        </CardHeader>
        <CardContent className="p-6">
          {/* Flow Note */}
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800">{lifecycle.flowNote}</p>
            </div>
          </div>

          {/* Step Tabs (clickable, wrap-friendly, no horizontal scroll) */}
          <div className="mb-6" data-testid="lifecycle-step-tabs">
            <div className="flex flex-wrap gap-2">
              {lifecycle.steps.map((step) => {
                const c = stepColors[step.color] || stepColors.slate;
                const isActive = step.step === activeStep;
                const isHub = step.step === 0;
                return (
                  <button
                    key={step.step}
                    type="button"
                    onClick={() => setActiveStep(step.step)}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-all",
                      isActive
                        ? cn(c.bg, c.border, "ring-2 ring-offset-1 ring-slate-400 shadow-sm")
                        : cn("bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm")
                    )}
                    data-testid={`lifecycle-tab-${step.step}`}
                    aria-pressed={isActive}
                  >
                    {isHub ? (
                      <span
                        className={cn(
                          "px-1.5 py-0.5 rounded text-[10px] font-bold tracking-widest",
                          isActive ? cn(c.dot, "text-white") : "bg-slate-200 text-slate-600"
                        )}
                      >
                        HUB
                      </span>
                    ) : (
                      <div
                        className={cn(
                          "w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0",
                          isActive ? c.dot : "bg-slate-300"
                        )}
                      >
                        {step.step}
                      </div>
                    )}
                    <span
                      className={cn(
                        "text-xs font-semibold whitespace-nowrap",
                        isActive ? c.text : "text-slate-600"
                      )}
                    >
                      {step.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Active Step Detail (filtered to one card) */}
          <div className="space-y-4">
            {[activeStepObj].map((step) => {
              const c = stepColors[step.color] || stepColors.slate;
              return (
                <Card key={step.step} className={cn("border-l-4 shadow-sm", c.border)} data-testid={`lifecycle-step-${step.step}`}>
                  <CardContent className="p-5">
                    {/* Step Header */}
                    <div className="flex items-start gap-3 mb-4">
                      {step.step === 0 ? (
                        <div className={cn("h-9 px-2 rounded-lg flex items-center justify-center text-white font-bold text-[11px] tracking-widest shrink-0", c.dot)}>
                          HUB
                        </div>
                      ) : (
                        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0", c.dot)}>
                          {step.step}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {step.name}
                          </h3>
                          <code className="text-xs text-slate-500 font-mono bg-slate-100 px-2 py-0.5 rounded">{step.path}</code>
                          <button
                            type="button"
                            onClick={copyStepLink}
                            className="ml-1 p-1 rounded text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                            title={`Copy link to ${step.name}`}
                            data-testid={`copy-step-link-${step.step}`}
                          >
                            {linkCopied ? <Check className="w-4 h-4 text-emerald-600" /> : <Link2 className="w-4 h-4" />}
                          </button>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">{step.purpose}</p>
                      </div>
                      {/* Prev / Next nav */}
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!prevStep}
                          onClick={() => prevStep && setActiveStep(prevStep.step)}
                          className="border-slate-200 px-2"
                          title={prevStep ? `Previous: ${prevStep.name}` : 'No previous step'}
                          data-testid="lifecycle-prev-step"
                        >
                          <ArrowLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!nextStep}
                          onClick={() => nextStep && setActiveStep(nextStep.step)}
                          className="border-slate-200 px-2"
                          title={nextStep ? `Next: ${nextStep.name}` : 'No next step'}
                          data-testid="lifecycle-next-step"
                        >
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Entry Point */}
                    <div className="mb-4 px-3 py-2 bg-slate-50 rounded border border-slate-200">
                      <span className="text-xs font-semibold text-slate-500 uppercase">Entry Point: </span>
                      <span className="text-sm text-slate-700">{step.entryPoint}</span>
                    </div>

                    {/* Two-column: Actions & Outputs */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Actions */}
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">User Actions</p>
                        <ul className="space-y-1.5">
                          {step.actions.map((action, aIdx) => (
                            <li key={aIdx} className="flex items-start gap-2 text-sm text-slate-700">
                              <ChevronRight className="w-3 h-3 text-slate-400 mt-1 shrink-0" />
                              {action}
                            </li>
                          ))}
                        </ul>
                      </div>
                      {/* Outputs */}
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">What This Produces</p>
                        <ul className="space-y-1.5">
                          {step.outputs.map((output, oIdx) => (
                            <li key={oIdx} className="flex items-start gap-2 text-sm text-slate-700">
                              <Check className="w-3 h-3 text-emerald-500 mt-1 shrink-0" />
                              {output}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Data Created */}
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <span className="text-xs font-semibold text-slate-400 uppercase">Data Created: </span>
                      <code className="text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded">{step.dataCreated}</code>
                    </div>

                    {/* Planned Enhancements */}
                    {step.plannedEnhancements && step.plannedEnhancements.length > 0 && (
                      <div className="mt-5 pt-4 border-t-2 border-dashed border-amber-300">
                        {step.plannedEnhancements.map((enh) => (
                          <div key={enh.id} className="bg-amber-50 border border-amber-200 rounded-lg p-5" data-testid={`planned-${enh.id}`}>
                            {/* Enhancement Header */}
                            <div className="flex items-center gap-2 mb-3">
                              <Badge className="bg-amber-500 text-white text-xs font-bold tracking-wider hover:bg-amber-500">
                                {enh.status}
                              </Badge>
                              <h4 className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {enh.title}
                              </h4>
                            </div>
                            <p className="text-sm text-slate-700 mb-4">{enh.summary}</p>

                            {enh.reference && (
                              <div className="mb-4 px-3 py-2 bg-white rounded border border-amber-100">
                                <span className="text-xs font-semibold text-slate-500 uppercase">Reference: </span>
                                {enh.reference.startsWith('http') ? (
                                  <a href={enh.reference} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline font-mono break-all">{enh.reference}</a>
                                ) : (
                                  <span className="text-xs text-slate-700 italic">{enh.reference}</span>
                                )}
                              </div>
                            )}

                            {/* ── 1. WHAT IS IT? ─────────────────────────── */}
                            {(enh.capabilityMatrixSchema || enh.schema || enh.evaluationFramework || enh.taskAreaReference || enh.domains) && (
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-5 mb-2">What is it?</p>
                            )}

                            {/* Capability Matrix Schema */}
                            {enh.capabilityMatrixSchema && (
                              <div className="mb-4 bg-white rounded-lg border border-slate-200 p-4">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{enh.capabilityMatrixSchema.title}</p>
                                <p className="text-xs text-slate-500 mb-4">{enh.capabilityMatrixSchema.description}</p>
                                <div className="grid md:grid-cols-3 gap-3">
                                  {/* Rows */}
                                  <div className="md:col-span-2 bg-orange-50 rounded-lg border border-orange-200 p-3">
                                    <p className="text-xs font-semibold text-orange-700 uppercase mb-2">Rows (Requirements Hierarchy)</p>
                                    <div className="space-y-2">
                                      {Object.entries(enh.capabilityMatrixSchema.rows).map(([key, val]) => (
                                        <div key={key} className="flex items-start gap-2 text-sm">
                                          <Badge variant="outline" className="text-xs shrink-0 font-mono capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</Badge>
                                          <span className="text-slate-600 text-xs">{val}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  {/* Columns + Cells */}
                                  <div className="space-y-3">
                                    <div className="bg-blue-50 rounded-lg border border-blue-200 p-3">
                                      <p className="text-xs font-semibold text-blue-700 uppercase mb-1">Columns</p>
                                      <p className="text-xs text-blue-600">{enh.capabilityMatrixSchema.columns}</p>
                                    </div>
                                    <div className="bg-violet-50 rounded-lg border border-violet-200 p-3">
                                      <p className="text-xs font-semibold text-violet-700 uppercase mb-1">Cells</p>
                                      <p className="text-xs text-violet-600">{enh.capabilityMatrixSchema.cells}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Compliance Matrix Schema */}
                            {enh.schema && (
                              <div className="mb-4 bg-white rounded-lg border border-slate-200 p-4">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{enh.schema.title}</p>
                                <div className="space-y-2">
                                  {enh.schema.fields.map((field, fIdx) => (
                                    <div key={fIdx} className="flex items-start gap-3 text-sm border-b border-slate-50 pb-2 last:border-0 last:pb-0">
                                      <Badge variant="outline" className="text-xs shrink-0 font-mono min-w-[140px] justify-center">{field.name}</Badge>
                                      <span className="text-slate-600">{field.description}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Evaluation Framework */}
                            {enh.evaluationFramework && (
                              <div className="mb-4 bg-white rounded-lg border border-slate-200 p-4">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{enh.evaluationFramework.title}</p>
                                <p className="text-xs text-slate-500 mb-4">{enh.evaluationFramework.description}</p>

                                <div className="grid md:grid-cols-2 gap-4">
                                  {/* Technical Ratings */}
                                  <div className="bg-teal-50 rounded-lg border border-teal-200 p-3">
                                    <p className="text-xs font-semibold text-teal-700 uppercase mb-2">Technical & Transition (Factors 1-2)</p>
                                    <div className="space-y-2">
                                      {enh.evaluationFramework.technicalRatings.map((r, rIdx) => (
                                        <div key={rIdx} className="text-sm">
                                          <Badge className={cn("text-xs mb-1", 
                                            r.rating === 'High Confidence' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' :
                                            r.rating === 'Some Confidence' ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' :
                                            'bg-red-100 text-red-700 hover:bg-red-100'
                                          )}>{r.rating}</Badge>
                                          <p className="text-xs text-slate-600">{r.definition}</p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  {/* Past Performance Ratings */}
                                  <div className="bg-blue-50 rounded-lg border border-blue-200 p-3">
                                    <p className="text-xs font-semibold text-blue-700 uppercase mb-2">Past Performance (Factor 3)</p>
                                    <div className="space-y-2">
                                      {enh.evaluationFramework.pastPerformanceRatings.map((r, rIdx) => (
                                        <div key={rIdx} className="text-sm">
                                          <Badge className={cn("text-xs mb-1",
                                            r.rating === 'Acceptable' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' :
                                            r.rating === 'Unacceptable' ? 'bg-red-100 text-red-700 hover:bg-red-100' :
                                            'bg-slate-100 text-slate-600 hover:bg-slate-100'
                                          )}>{r.rating}</Badge>
                                          <p className="text-xs text-slate-600">{r.definition}</p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Task Area Reference */}
                            {enh.taskAreaReference && (
                              <div className="mb-4 bg-white rounded-lg border border-slate-200 p-4">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{enh.taskAreaReference.title}</p>
                                <p className="text-xs text-slate-500 mb-3">{enh.taskAreaReference.description}</p>
                                <div className="grid sm:grid-cols-2 gap-1">
                                  {enh.taskAreaReference.areas.map((area, aIdx) => (
                                    <div key={aIdx} className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-slate-50">
                                      <ChevronRight className="w-3 h-3 text-orange-400 mt-0.5 shrink-0" />
                                      <span className="text-xs text-slate-700">{area}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Three Domains — describes the structural breakdown of the scorecard, so it belongs in WHAT IS IT?, not HOW IS IT SCORED? */}
                            {enh.domains && (
                              <div className="mb-4">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Scoring Domains (35 Questions)</p>
                                <div className="grid gap-3">
                                  {enh.domains.map((domain, dIdx) => (
                                    <div key={dIdx} className="bg-white rounded-lg border border-slate-200 p-4">
                                      <div className="flex items-center gap-2 mb-2">
                                        <Badge variant="outline" className="text-xs font-mono">{domain.questionRange}</Badge>
                                        <span className="font-semibold text-sm text-slate-900">{domain.name}</span>
                                        <Badge className="bg-slate-100 text-slate-600 text-xs hover:bg-slate-100">{domain.focus}</Badge>
                                      </div>
                                      <p className="text-sm text-slate-600 mb-3">{domain.description}</p>
                                      <div className="grid sm:grid-cols-2 gap-2">
                                        <div className="px-3 py-2 bg-emerald-50 rounded border border-emerald-100">
                                          <p className="text-xs font-semibold text-emerald-700 mb-1">AI Auto-Scoreable</p>
                                          <p className="text-xs text-emerald-600">{domain.aiAutoScoreable}</p>
                                        </div>
                                        <div className="px-3 py-2 bg-blue-50 rounded border border-blue-100">
                                          <p className="text-xs font-semibold text-blue-700 mb-1">Human Judgment Required</p>
                                          <p className="text-xs text-blue-600">{domain.humanRequired}</p>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* ── 2. HOW IS IT SCORED? ───────────────────── */}
                            {(enh.scoringSystem || enh.scoringLogic || enh.lcatOverlay) && (
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-5 mb-2">How is it scored?</p>
                            )}

                            {/* 4-Level Scoring System */}
                            {enh.scoringSystem && (
                              <div className="mb-4 bg-white rounded-lg border border-slate-200 p-4">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{enh.scoringSystem.title}</p>
                                <p className="text-xs text-slate-500 mb-4">{enh.scoringSystem.description}</p>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                  {enh.scoringSystem.levels.map((level) => {
                                    const colorMap = {
                                      RED: { bg: 'bg-red-50', border: 'border-red-300', badge: 'bg-red-500', text: 'text-red-700' },
                                      YELLOW: { bg: 'bg-yellow-50', border: 'border-yellow-300', badge: 'bg-yellow-500', text: 'text-yellow-700' },
                                      GREEN: { bg: 'bg-emerald-50', border: 'border-emerald-300', badge: 'bg-emerald-500', text: 'text-emerald-700' },
                                      BLUE: { bg: 'bg-blue-50', border: 'border-blue-300', badge: 'bg-blue-500', text: 'text-blue-700' },
                                    };
                                    const c = colorMap[level.color] || colorMap.RED;
                                    return (
                                      <div key={level.score} className={cn("rounded-lg border p-3", c.bg, c.border)}>
                                        <div className="flex items-center gap-2 mb-2">
                                          <div className={cn("w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-sm", c.badge)}>
                                            {level.score}
                                          </div>
                                          <span className={cn("text-xs font-bold uppercase", c.text)}>{level.color}</span>
                                        </div>
                                        <p className="text-xs font-semibold text-slate-800 mb-1">{level.label}</p>
                                        <p className="text-xs text-slate-600">{level.meaning}</p>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Scoring Logic */}
                            {enh.scoringLogic && (
                              <div className="mb-4 bg-white rounded-lg border border-slate-200 p-4">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Scoring Mechanics</p>
                                <div className="space-y-2">
                                  <div className="flex items-start gap-2 text-sm">
                                    <Badge variant="outline" className="text-xs shrink-0">Scale</Badge>
                                    <span className="text-slate-700">{enh.scoringLogic.scale}</span>
                                  </div>
                                  <div className="flex items-start gap-2 text-sm">
                                    <Badge variant="outline" className="text-xs shrink-0">Max</Badge>
                                    <span className="text-slate-700">{enh.scoringLogic.maxScore}</span>
                                  </div>
                                  <div className="flex items-start gap-2 text-sm">
                                    <Badge className="bg-red-100 text-red-700 text-xs shrink-0 hover:bg-red-100">Deal Killer</Badge>
                                    <span className="text-slate-700">{enh.scoringLogic.dealKillerRule}</span>
                                  </div>
                                  <div className="flex items-start gap-2 text-sm">
                                    <Badge className="bg-slate-200 text-slate-700 text-xs shrink-0 hover:bg-slate-200">Unknown</Badge>
                                    <span className="text-slate-700">{enh.scoringLogic.unknownAsRisk}</span>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* LCAT Overlay */}
                            {enh.lcatOverlay && (
                              <div className="mb-4 bg-white rounded-lg border border-slate-200 p-4">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{enh.lcatOverlay.title}</p>
                                <p className="text-xs text-slate-500 mb-4">{enh.lcatOverlay.description}</p>
                                <div className="space-y-2 mb-3">
                                  {enh.lcatOverlay.fields.map((field, fIdx) => (
                                    <div key={fIdx} className="flex items-start gap-3 text-sm border-b border-slate-50 pb-2 last:border-0 last:pb-0">
                                      <Badge variant="outline" className="text-xs shrink-0 font-mono min-w-[150px] justify-center">{field.name}</Badge>
                                      <span className="text-slate-600 text-xs">{field.description}</span>
                                    </div>
                                  ))}
                                </div>
                                {enh.lcatOverlay.complianceNote && (
                                  <div className="px-3 py-2 bg-red-50 rounded border border-red-200">
                                    <p className="text-xs text-red-700"><strong>Compliance Gate:</strong> {enh.lcatOverlay.complianceNote}</p>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* ── 3. WHAT DOES IT PRODUCE? ──────────────── */}
                            {enh.aggregateOutputs && (
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-5 mb-2">What does it produce?</p>
                            )}

                            {/* Aggregate Outputs */}
                            {enh.aggregateOutputs && (
                              <div className="mb-4 bg-white rounded-lg border border-slate-200 p-4">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Aggregate Metrics (Feed into pWin Oracle)</p>
                                <div className="grid sm:grid-cols-2 gap-3">
                                  {enh.aggregateOutputs.map((output, oIdx) => (
                                    <div key={oIdx} className="px-3 py-2 bg-slate-50 rounded border border-slate-200">
                                      <p className="text-xs font-semibold text-slate-800">{output.metric}</p>
                                      <p className="text-xs text-slate-600">{output.description}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* ── 4. HOW DOES IT WORK? ──────────────────── */}
                            {(enh.hybridApproach || enh.keyDesignPrinciples || enh.dataSources) && (
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-5 mb-2">How does it work?</p>
                            )}

                            {/* Hybrid Approach */}
                            {enh.hybridApproach && (
                              <div className="mb-4 px-4 py-3 bg-blue-50 rounded-lg border border-blue-200">
                                <p className="text-xs font-semibold text-blue-700 uppercase mb-1">Hybrid AI + Human Approach</p>
                                <p className="text-sm text-blue-800">{enh.hybridApproach}</p>
                              </div>
                            )}

                            {/* Key Design Principles */}
                            {enh.keyDesignPrinciples && (
                              <div className="mb-4">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Key Design Principles</p>
                                <div className="grid gap-2">
                                  {enh.keyDesignPrinciples.map((p, pIdx) => (
                                    <div key={pIdx} className="bg-white rounded-lg border border-slate-200 p-3">
                                      <p className="font-semibold text-sm text-slate-900 mb-1">{p.principle}</p>
                                      <p className="text-xs text-slate-600">{p.detail}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Data Sources */}
                            {enh.dataSources && (
                              <div className="mb-4">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Data Sources for AI Pre-Fill</p>
                                <div className="flex flex-wrap gap-1">
                                  {enh.dataSources.map((ds, dsIdx) => (
                                    <Badge key={dsIdx} variant="outline" className="text-xs">{ds}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* ── 5. WHERE DOES IT LIVE? ────────────────── */}
                            {enh.uiIntegration && (
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-5 mb-2">Where does it live in the app?</p>
                            )}

                            {/* UI Integration */}
                            {enh.uiIntegration && (
                              <div className="mb-4 px-3 py-2 bg-white rounded border border-slate-200">
                                <span className="text-xs font-semibold text-slate-500 uppercase">UI Placement: </span>
                                <span className="text-sm text-slate-700">{enh.uiIntegration}</span>
                              </div>
                            )}

                            {/* ── 6. HOW DOES IT CONNECT? ───────────────── */}
                            {enh.connectionToOtherSteps && (
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-5 mb-2">How does it connect to other steps?</p>
                            )}

                            {/* Connection to Other Steps */}
                            {enh.connectionToOtherSteps && (
                              <div className="mb-4 bg-white rounded-lg border border-slate-200 p-4">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Cross-Step Data Flow</p>
                                <div className="space-y-2">
                                  {enh.connectionToOtherSteps.map((conn, cIdx) => (
                                    <div key={cIdx} className="flex items-start gap-3 text-sm border-b border-slate-50 pb-2 last:border-0 last:pb-0">
                                      <Badge className="bg-teal-100 text-teal-700 text-xs shrink-0 hover:bg-teal-100 min-w-[110px] justify-center">{conn.step}</Badge>
                                      <span className="text-slate-600">{conn.connection}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* ── 7. WHY IT MATTERS ─────────────────────── */}
                            {(enh.aiIntegration || enh.pipelineUseCase) && (
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-5 mb-2">Why it matters</p>
                            )}

                            {/* AI Integration */}
                            {enh.aiIntegration && (
                              <div className="mb-4 px-4 py-3 bg-violet-50 rounded-lg border border-violet-200">
                                <p className="text-xs font-semibold text-violet-700 uppercase mb-1">AI Integration</p>
                                <p className="text-sm text-violet-800">{enh.aiIntegration}</p>
                              </div>
                            )}

                            {/* Pipeline Use Case */}
                            {enh.pipelineUseCase && (
                              <div className="px-4 py-3 bg-emerald-50 rounded-lg border border-emerald-200">
                                <p className="text-xs font-semibold text-emerald-700 uppercase mb-1">Business Impact</p>
                                <p className="text-sm text-emerald-800">{enh.pipelineUseCase}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ── SUPPORTING SCREENS (lifted out of lifecycle card) ──────── */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-800 text-base">
            <Globe className="w-4 h-4" />
            Supporting Screens (Non-Sequential)
          </CardTitle>
          <p className="text-xs text-slate-500">Utilities and shared screens that orbit the pursuit lifecycle but aren't part of the linear flow.</p>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid md:grid-cols-3 gap-3">
            {lifecycle.supportingScreens.map((screen, idx) => (
              <Card key={idx} className="border-slate-200 shadow-sm">
                <CardContent className="p-4">
                  <p className="font-semibold text-slate-900 text-sm">{screen.name}</p>
                  <code className="text-xs text-slate-500 font-mono">{screen.path}</code>
                  <p className="text-xs text-slate-600 mt-2">{screen.purpose}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {screen.actions.map((a, aIdx) => (
                      <Badge key={aIdx} variant="outline" className="text-xs">{a}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── FIT SCORE LIFECYCLE ───────────────────────────────────── */}
      <Card className="border-blue-200 shadow-sm bg-gradient-to-r from-blue-50 to-white">
        <CardHeader className="border-b border-blue-100">
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <Target className="w-5 h-5" />
            The Fit Score Lifecycle
          </CardTitle>
          <p className="text-sm text-blue-600">How opportunity fit percentage evolves from creation to submission</p>
        </CardHeader>
        <CardContent className="p-6">
          {/* Visual Timeline */}
          <div className="relative mb-8">
            <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-300 via-blue-300 to-emerald-300 transform -translate-x-1/2"></div>
            
            {WORKFLOWS.fitScoreLifecycle.phases.map((phase, idx) => (
              <div key={phase.id} className={cn(
                "relative flex items-start gap-4 mb-8",
                idx % 2 === 0 ? "flex-row" : "flex-row-reverse"
              )}>
                <div className={cn(
                  "flex-1 p-4 rounded-lg border",
                  idx === 0 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"
                )}>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={cn(
                      idx === 0 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
                    )}>
                      {phase.range}
                    </Badge>
                    <span className="text-xs text-slate-500">Triggered by: {phase.trigger}</span>
                  </div>
                  <p className="font-semibold text-slate-900 mb-2">{phase.name}</p>
                  <p className="text-sm text-slate-600 mb-4">{phase.description}</p>
                  
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase">Scoring Factors</p>
                    {phase.factors.map((factor, fIdx) => (
                      <div key={fIdx} className="flex items-start gap-2 text-sm">
                        <Badge variant="outline" className="text-xs shrink-0">{factor.weight}</Badge>
                        <div>
                          <span className="font-medium text-slate-700">{factor.name}:</span>
                          <span className="text-slate-600 ml-1">{factor.description}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Timeline Node */}
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center shrink-0 z-10",
                  idx === 0 ? "bg-amber-500" : "bg-emerald-500"
                )}>
                  <span className="text-white font-bold">{idx + 1}</span>
                </div>
                
                <div className="flex-1"></div>
              </div>
            ))}
          </div>

          {/* Tooltip Preview Box */}
          <div className="bg-slate-100 rounded-lg p-4 border border-slate-200">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-800">UI Tooltip Text (Risk% Widget)</p>
                <p className="text-sm text-slate-600 mt-1 italic">"{WORKFLOWS.fitScoreLifecycle.tooltipText}"</p>
              </div>
            </div>
          </div>

          {/* Common Questions */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="font-semibold text-blue-800 mb-2">Common Questions</p>
            <div className="space-y-2 text-sm text-blue-700">
              <p><strong>Q: Why is my fit score below 50% before I uploaded anything?</strong></p>
              <p className="pl-4">A: Phase 1 scores metadata completeness. Fill in Agency, NAICS, Set-Aside, and Prime Bidder details to maximize this baseline (up to 50%).</p>
              <p className="mt-2"><strong>Q: Does uploading documents automatically increase my score?</strong></p>
              <p className="pl-4">A: Yes. Each document group covered adds +5% (max +30%). The score updates automatically after each upload.</p>
              <p className="mt-2"><strong>Q: What does "Run AI Analysis" do beyond uploads?</strong></p>
              <p className="pl-4">A: It extracts NAICS/PSC codes, runs semantic similarity checks against your winning proposals, evaluates teaming coverage, and scores agency experience. This can push scores above 80%.</p>
              <p className="mt-2"><strong>Q: Can a score decrease?</strong></p>
              <p className="pl-4">A: The score never drops below your metadata baseline. AI analysis may adjust the score if gaps are found, but it will always be at least as high as what your metadata justifies.</p>
            </div>
          </div>

          {/* ── IMPLEMENTED CHANGE ────────────────────────────────── */}
          {WORKFLOWS.fitScoreLifecycle.implementedChange && (() => {
            const ic = WORKFLOWS.fitScoreLifecycle.implementedChange;
            return (
              <div className="mt-6 border-t-2 border-solid border-emerald-300 pt-6" data-testid="fit-score-implemented-change">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5">
                  {/* Header */}
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className="bg-emerald-600 text-white text-xs font-bold tracking-wider hover:bg-emerald-600">
                      {ic.status}
                    </Badge>
                    <span className="text-xs text-slate-500">{ic.implementedDate}</span>
                    <h4 className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {ic.title}
                    </h4>
                  </div>

                  {/* Rationale */}
                  <div className="mb-4 px-4 py-3 bg-white rounded-lg border border-emerald-100">
                    <p className="text-xs font-semibold text-slate-600 uppercase mb-1">Why This Changed</p>
                    <p className="text-sm text-slate-700">{ic.rationale}</p>
                  </div>

                  {/* Core Change */}
                  <div className="mb-4 px-4 py-3 bg-emerald-100 rounded-lg border border-emerald-200">
                    <p className="text-xs font-semibold text-emerald-800 uppercase mb-1">What Changed</p>
                    <p className="text-sm text-emerald-900 font-medium">{ic.coreChange}</p>
                  </div>

                  {/* UI States */}
                  <div className="mb-4 bg-white rounded-lg border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Three UI States</p>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {ic.uiStates.map((s, sIdx) => {
                        const colorMap = {
                          red: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-500', text: 'text-red-700' },
                          amber: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-500', text: 'text-amber-700' },
                          emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-500', text: 'text-emerald-700' }
                        };
                        const c = colorMap[s.color] || colorMap.red;
                        return (
                          <div key={sIdx} className={cn("rounded-lg border p-3", c.bg, c.border)}>
                            <Badge className={cn("text-xs text-white mb-2", c.badge, `hover:${c.badge}`)}>{s.state}</Badge>
                            <p className={cn("text-xs", c.text)}>{s.description}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Affected Components */}
                  <div className="mb-4 bg-white rounded-lg border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Affected Components</p>
                    <div className="space-y-2">
                      {ic.affectedComponents.map((item, iIdx) => (
                        <div key={iIdx} className="flex items-start gap-3 text-sm border-b border-slate-50 pb-2 last:border-0 last:pb-0">
                          <Badge variant="outline" className="text-xs shrink-0 font-mono min-w-[170px] justify-center">{item.component}</Badge>
                          <span className="text-slate-600 text-xs">{item.change}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Future Score Inputs */}
                  <div className="bg-white rounded-lg border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Future Score Inputs (Planned Enhancements)</p>
                    <div className="space-y-2">
                      {ic.futureScoreInputs.map((input, iIdx) => (
                        <div key={iIdx} className="flex items-start gap-3 text-sm border-b border-slate-50 pb-2 last:border-0 last:pb-0">
                          <Badge className="bg-teal-100 text-teal-700 text-xs shrink-0 hover:bg-teal-100 min-w-[140px] justify-center">{input.source}</Badge>
                          <span className="text-slate-600 text-xs">{input.factor}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* ── SCORE FIDELITY & INDICATORS ───────────────────────────── */}
      <Card className="border-purple-200 shadow-sm bg-gradient-to-r from-purple-50 to-white">
        <CardHeader className="border-b border-purple-100">
          <CardTitle className="flex items-center gap-2 text-purple-800">
            <Shield className="w-5 h-5" />
            {WORKFLOWS.scoreFidelity.title}
          </CardTitle>
          <p className="text-sm text-purple-600">{WORKFLOWS.scoreFidelity.description}</p>
        </CardHeader>
        <CardContent className="p-6">
          {/* Badge Comparison */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {WORKFLOWS.scoreFidelity.badges.map((badge) => {
              const colorMap = {
                red: { bg: 'bg-red-50', border: 'border-red-200', badgeBg: 'bg-red-100 text-red-700 border-red-300', trust: 'bg-red-100 text-red-800' },
                amber: { bg: 'bg-amber-50', border: 'border-amber-200', badgeBg: 'bg-amber-100 text-amber-700 border-amber-300', trust: 'bg-amber-100 text-amber-800' },
                emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', badgeBg: 'bg-emerald-100 text-emerald-700 border-emerald-300', trust: 'bg-emerald-100 text-emerald-800' }
              };
              const c = colorMap[badge.color] || colorMap.amber;
              return (
                <div key={badge.id} className={cn("p-4 rounded-lg border", c.bg, c.border)}>
                  <Badge className={cn("mb-3", c.badgeBg)}>{badge.label}</Badge>
                  <p className="font-medium text-slate-900 mb-2 text-sm">{badge.meaning}</p>
                  <div className="mb-3">
                    <p className="text-xs text-slate-500 uppercase mb-1">Data Points</p>
                    <div className="flex flex-wrap gap-1">
                      {badge.dataPoints.map((dp, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">{dp}</Badge>
                      ))}
                    </div>
                  </div>
                  <p className={cn("text-xs p-2 rounded", c.trust)}>
                    <strong>Trust:</strong> {badge.trustLevel}
                  </p>
                  <p className="text-xs text-slate-600 mt-2 italic">{badge.action}</p>
                </div>
              );
            })}
          </div>

          {/* Cross-Component Sync Table */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <p className="text-sm font-semibold text-slate-800 mb-3">Cross-Component Sync</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-slate-700">Component</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-700">Displays</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-700">Behavior</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {WORKFLOWS.scoreFidelity.crossComponentSync.map((sync, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-2 font-medium text-slate-900">{sync.component}</td>
                      <td className="px-3 py-2 text-slate-600">{sync.displays}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{sync.behavior}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── ALL SCREENS REFERENCE ─────────────────────────────────── */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base text-slate-700">All Application Screens</CardTitle>
          <p className="text-xs text-slate-500">Complete reference of every screen and available actions</p>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid gap-3">
            {WORKFLOWS.screens.map((screen, idx) => (
              <div key={idx} className="p-3 border border-slate-100 rounded-lg hover:bg-slate-50 transition-colors">
                <div className="flex items-start justify-between mb-1">
                  <p className="font-semibold text-sm text-slate-900">{screen.name}</p>
                  <code className="text-xs text-slate-500 font-mono">{screen.path}</code>
                </div>
                <p className="text-xs text-slate-600 mb-2">{screen.description}</p>
                <div className="flex flex-wrap gap-1">
                  {screen.actions.map((action, aIdx) => (
                    <Badge key={aIdx} variant="outline" className="text-xs">{action}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function OwnersGuideContent({ toggleSection, expandedSections }) {
  return (
    <div className="space-y-6">
      {/* Introduction Card */}
      <Card className="border-teal-200 bg-teal-50/50 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <BookOpen className="w-5 h-5 text-teal-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-teal-800">Functional Owner's Guide (Super Admin Constitution)</p>
              <p className="text-sm text-teal-700 mt-1">
                This is the non-technical business manual for platform owners and executives. 
                It explains what the platform does and why, without going into implementation details.
              </p>
              <p className="text-xs text-teal-600 mt-2">
                <strong>Platform Owner:</strong> MNGC Group, Inc. | <strong>Master Accounts:</strong> thepkg@gmail.com, pk@fedbd.ai
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Entry & Pricing */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
          onClick={() => toggleSection('guide-pricing')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <DollarSign className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">Entry & Pricing</CardTitle>
                <p className="text-sm text-slate-500">How users join and what they pay</p>
              </div>
            </div>
            {expandedSections['guide-pricing'] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections['guide-pricing'] && (
          <CardContent className="p-6 space-y-6">
            <div>
              <h4 className="font-semibold text-slate-900 mb-3">Onboarding Model</h4>
              <p className="text-sm text-slate-600 mb-4">
                FedBD uses an <strong>invitation-based onboarding model</strong>. There is no public registration form.
                Contractors can invite external consultants using a "Copy Link" approach, giving them full control over communication.
              </p>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Invite Flow</p>
                <p className="text-sm font-mono text-slate-700">
                  Contractor Admin → Generate Token → Copy Link → Share Privately → Consultant Registers → Auto-Link Created
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Consultant Plans</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                    <span className="text-sm font-medium">INDEPENDENT</span>
                    <span className="text-sm text-slate-600">$149/mo • 2 clients</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                    <span className="text-sm font-medium">AGENCY</span>
                    <span className="text-sm text-slate-600">$499/mo • 5 clients • 3 associates</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-teal-50 rounded">
                    <span className="text-sm font-medium">ENTERPRISE</span>
                    <span className="text-sm text-teal-700">$999/mo • Unlimited</span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Contractor Plans</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                    <span className="text-sm font-medium">SMALL_BUSINESS</span>
                    <span className="text-sm text-slate-600">$99/mo • 2 pursuits</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                    <span className="text-sm font-medium">GROWTH</span>
                    <span className="text-sm text-slate-600">$299/mo • 10 pursuits</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-teal-50 rounded">
                    <span className="text-sm font-medium">ENTERPRISE</span>
                    <span className="text-sm text-teal-700">$999/mo • Unlimited</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-amber-50 rounded-lg p-4">
              <p className="text-sm font-medium text-amber-800">Extra pursuit slots</p>
              <p className="text-sm text-amber-700">Contractors can add pursuit slots beyond their plan's included count. Extra slots are billed as a recurring per-pursuit line item on the Stripe subscription (see <code className="bg-amber-100 px-1 rounded">stripe_billing.py</code> / <code className="bg-amber-100 px-1 rounded">stripe_prices.py</code>) — not a separate one-time credit purchase.</p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Roles & Permissions */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
          onClick={() => toggleSection('guide-roles')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">Roles & Permissions</CardTitle>
                <p className="text-sm text-slate-500">Who can do what on the platform</p>
              </div>
            </div>
            {expandedSections['guide-roles'] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections['guide-roles'] && (
          <CardContent className="p-6 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-red-800">God Mode (Super Admin)</p>
              <p className="text-sm text-red-700">
                Super Admin is a permission tier, not a single user. Two accounts currently hold this tier and
                see identical global data — same dashboard, same support queue, same audit log, same
                Feature Release Console.
              </p>
              <table className="w-full text-xs mt-3">
                <thead>
                  <tr className="text-red-800">
                    <th className="text-left font-semibold py-1 pr-4">Email</th>
                    <th className="text-left font-semibold py-1 pr-4">Display Name</th>
                    <th className="text-left font-semibold py-1">Org Attached</th>
                  </tr>
                </thead>
                <tbody className="text-red-700">
                  <tr>
                    <td className="py-1 pr-4 font-mono">thepkg@gmail.com</td>
                    <td className="py-1 pr-4">The PKG</td>
                    <td className="py-1">MNGC Group, Inc.</td>
                  </tr>
                  <tr>
                    <td className="py-1 pr-4 font-mono">pk@fedbd.ai</td>
                    <td className="py-1 pr-4">PK FedBD</td>
                    <td className="py-1">MNGC Group, Inc.</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-xs text-red-700 mt-3">
                Both bypass all plan limits — client slots, associate seats, pursuit limits, and feature gates.
                Both are seeded idempotently on backend startup via <code className="bg-red-100 px-1 rounded">seed_super_admin()</code> +
                {' '}<code className="bg-red-100 px-1 rounded">seed_extra_super_admins()</code> in <code className="bg-red-100 px-1 rounded">app/mongo_models.py</code>.
                To add another, append to the <code className="bg-red-100 px-1 rounded">extra_admins</code> list and redeploy.
              </p>
              <p className="text-xs text-red-700 mt-2">
                <strong>Differences between the two:</strong> profile/password are per-account, audit log attributes
                actions to the actor, and "view as role" sandbox mode (<code className="bg-red-100 px-1 rounded">?as_role=CONSULTANT</code>)
                shows only that admin&apos;s personally-created tickets. Otherwise they are interchangeable peers — last-write-wins
                on shared edits (feature flag toggles, consultant edits, ticket replies). Neither can demote or delete the other
                ({' '}<code className="bg-red-100 px-1 rounded">PUT /api/admin/users/&#123;id&#125;</code> blocks role changes for
                any <code className="bg-red-100 px-1 rounded">SUPER_ADMIN</code> row).
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-slate-700">Role</th>
                    <th className="px-4 py-2 text-left font-medium text-slate-700">Org Type</th>
                    <th className="px-4 py-2 text-left font-medium text-slate-700">Access Level</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr><td className="px-4 py-2 font-medium">SUPER_ADMIN</td><td className="px-4 py-2">CONSULTANCY</td><td className="px-4 py-2 text-slate-600">Everything (God Mode)</td></tr>
                  <tr><td className="px-4 py-2 font-medium">CONSULTANT</td><td className="px-4 py-2">CONSULTANCY</td><td className="px-4 py-2 text-slate-600">Own practice + linked clients</td></tr>
                  <tr><td className="px-4 py-2 font-medium">CONSULTANT_ASSOCIATE</td><td className="px-4 py-2">CONSULTANCY</td><td className="px-4 py-2 text-slate-600">Only assigned pursuits</td></tr>
                  <tr><td className="px-4 py-2 font-medium">CONTRACTOR_ADMIN</td><td className="px-4 py-2">CLIENT</td><td className="px-4 py-2 text-slate-600">Full org access, can invite</td></tr>
                  <tr><td className="px-4 py-2 font-medium">BID_MANAGER</td><td className="px-4 py-2">CLIENT</td><td className="px-4 py-2 text-slate-600">Manage pursuits, no invites</td></tr>
                  <tr><td className="px-4 py-2 font-medium">REVIEWER</td><td className="px-4 py-2">CLIENT</td><td className="px-4 py-2 text-slate-600">Assigned pursuits only (read)</td></tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Workspace Silos */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
          onClick={() => toggleSection('guide-silos')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building2 className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">Workspace Switching & Silos</CardTitle>
                <p className="text-sm text-slate-500">How data isolation protects clients</p>
              </div>
            </div>
            {expandedSections['guide-silos'] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections['guide-silos'] && (
          <CardContent className="p-6 space-y-4">
            <p className="text-sm text-slate-600">
              FedBD uses a <strong>strict data isolation model</strong> called "Silos". 
              Each contractor's data is completely isolated from other contractors, even if the same consultant works for multiple clients.
            </p>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Workspace Switcher UI</p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-purple-500 rounded"></div>
                  <span><strong>HOME</strong> workspace: Your consultancy (purple accent)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-teal-500 rounded"></div>
                  <span><strong>CLIENT</strong> workspaces: Each linked contractor (teal accent)</span>
                </div>
              </div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <p className="text-sm font-medium text-emerald-800">Data Protection Guarantee</p>
              <ul className="text-sm text-emerald-700 mt-2 space-y-1">
                <li>• When viewing Client A's workspace, you only see Client A's data</li>
                <li>• Client A cannot see Client B's data (different organizations)</li>
                <li>• Super Admin can see all data (God Mode only)</li>
              </ul>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Pursuit Lifecycle */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
          onClick={() => toggleSection('guide-pursuits')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Target className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">Pursuit & Collaboration</CardTitle>
                <p className="text-sm text-slate-500">How opportunities move through the pipeline</p>
              </div>
            </div>
            {expandedSections['guide-pursuits'] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections['guide-pursuits'] && (
          <CardContent className="p-6 space-y-4">
            <p className="text-sm text-slate-600">
              A <strong>Pursuit</strong> is a tracked federal contracting opportunity. It represents a potential bid that moves through defined stages.
            </p>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Pipeline Stages</p>
              <div className="flex flex-wrap gap-2">
                {['INTAKE', 'GO/NO-GO', 'DRAFTING', 'SUBMITTED'].map((stage, idx) => (
                  <div key={stage} className="flex items-center gap-2">
                    <Badge className={cn(
                      "border-0",
                      idx === 0 ? "bg-slate-200 text-slate-700" :
                      idx === 1 ? "bg-blue-100 text-blue-700" :
                      idx === 2 ? "bg-amber-100 text-amber-700" :
                      "bg-emerald-100 text-emerald-700"
                    )}>
                      {stage}
                    </Badge>
                    {idx < 3 && <ArrowRight className="w-4 h-4 text-slate-300" />}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-2">Go/No-Go Evaluation</h4>
              <p className="text-sm text-slate-600 mb-3">Uses a 5-criteria scoring model (1-5 scale):</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {['Technical Capability', 'Past Performance Fit', 'Financial Risk', 'Management Approach', 'Competitive Positioning'].map(criterion => (
                  <div key={criterion} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-sm">
                    <Check className="w-4 h-4 text-emerald-500" />
                    {criterion}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* 6-Group Ingestion Hub */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
          onClick={() => toggleSection('guide-6group')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Layers className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base font-semibold text-slate-900">6-Group Ingestion Hub</CardTitle>
                  <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">ACTIVE</Badge>
                </div>
                <p className="text-sm text-slate-500">Document classification system for AI analysis</p>
              </div>
            </div>
            {expandedSections['guide-6group'] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections['guide-6group'] && (
          <CardContent className="p-6 space-y-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                <div className="w-8 h-8 bg-red-500 rounded flex items-center justify-center text-white font-bold text-sm">1</div>
                <div>
                  <p className="font-semibold text-slate-900">RFP & Solicitation</p>
                  <p className="text-sm text-slate-600">Core solicitation documents: RFP, amendments, Q&A, evaluation criteria</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                <div className="w-8 h-8 bg-amber-500 rounded flex items-center justify-center text-white font-bold text-sm">2</div>
                <div>
                  <p className="font-semibold text-slate-900">Strategy & Themes</p>
                  <p className="text-sm text-slate-600">Win themes, capture strategy, discriminators, competitive analysis</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                <div className="w-8 h-8 bg-emerald-500 rounded flex items-center justify-center text-white font-bold text-sm">3</div>
                <div>
                  <p className="font-semibold text-slate-900">Past Performance</p>
                  <p className="text-sm text-slate-600">Contract references, CPARS ratings, performance narratives</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center text-white font-bold text-sm">4</div>
                <div>
                  <p className="font-semibold text-slate-900">Personnel & Resumes</p>
                  <p className="text-sm text-slate-600">Key personnel bios, org charts, certifications, clearances</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="w-8 h-8 bg-purple-500 rounded flex items-center justify-center text-white font-bold text-sm">5</div>
                <div>
                  <p className="font-semibold text-slate-900">Winning Proposals (Gold Source)</p>
                  <p className="text-sm text-slate-600">Previous winning proposals for semantic similarity matching</p>
                  <p className="text-xs text-purple-600 mt-1">Used by AI Similarity Engine to match against RFP requirements</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-100 rounded-lg">
                <div className="w-8 h-8 bg-slate-500 rounded flex items-center justify-center text-white font-bold text-sm">6</div>
                <div>
                  <p className="font-semibold text-slate-900">Other / Supporting</p>
                  <p className="text-sm text-slate-600">Pricing templates, subcontractor agreements, miscellaneous</p>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Development / Infrastructure Cost Tracking */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader
          className="cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
          onClick={() => toggleSection('guide-prev-platform-cost')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <DollarSign className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">Development / Infrastructure Cost Tracking</CardTitle>
                <p className="text-sm text-slate-500">Super Admin → Development Costs</p>
              </div>
            </div>
            {expandedSections['guide-prev-platform-cost'] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections['guide-prev-platform-cost'] && (
          <CardContent className="p-6 space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <p className="text-sm font-medium text-emerald-800">Current cost tracker</p>
              <p className="text-sm text-emerald-700 mt-1">
                Development spend is tracked via <code className="bg-emerald-100 px-1 rounded">app/routes/admin_costs.py</code> and
                displayed on the Super Admin → Development Costs page: a frozen historical
                "Prev. Platform Cost" rollup (closed ledger, no new entries) plus live recurring
                infra costs (AWS Lightsail, MongoDB Atlas, Claude subscription) and labor hours.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Pursuit Slots</p>
                <p className="text-sm text-slate-600">Sold as a recurring per-pursuit Stripe line item beyond a plan's included count — not a credit purchase.</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">AI Cost Tracking</p>
                <p className="text-sm text-slate-600">Each org pays its own configured LLM provider directly via BYOM — the platform does not intermediate or markup AI usage cost.</p>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-800">Manual Ledger Rules</p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1">
                <li>• All invoices must be reconciled through AI Reconciliation tool</li>
                <li>• Standard credit purchase amount: $1,700</li>
                <li>• Project start date: Feb 6, 2026 (invoices before this date rejected)</li>
                <li>• Date from invoice is used, not system-generated date</li>
              </ul>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Data Governance & Retention */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
          onClick={() => toggleSection('guide-retention')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">Data Governance & Retention</CardTitle>
                <p className="text-sm text-slate-500">10-day purge and Gold Source rules</p>
              </div>
            </div>
            {expandedSections['guide-retention'] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections['guide-retention'] && (
          <CardContent className="p-6 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm font-medium text-amber-800">10-Day Retention Policy</p>
              <p className="text-sm text-amber-700 mt-1">
                Documents auto-purge after <strong>10 days of inactivity</strong> unless promoted to Gold Source.
              </p>
              <ul className="text-sm text-amber-700 mt-2 space-y-1">
                <li>• Warning emails sent at 2 days and 1 day before purge</li>
                <li>• <strong>Promoted (Gold Source)</strong> documents never auto-purge</li>
                <li>• Toggle auto-purge in Settings → Retention</li>
              </ul>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm font-medium text-purple-800">Gold Source Promotion</p>
              <p className="text-sm text-purple-700 mt-1">
                Documents can be promoted to "Gold Source" status which:
              </p>
              <ul className="text-sm text-purple-700 mt-2 space-y-1">
                <li>• Exempts them from auto-purge cycles</li>
                <li>• Makes them available for AI similarity matching (Group 5)</li>
                <li>• Stores them in the Global Knowledge Base for reuse</li>
              </ul>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 bg-emerald-50 rounded-lg">
                <p className="font-medium text-emerald-800 text-sm">Consultant Visibility</p>
                <p className="text-xs text-emerald-600">Metadata only, never proposal content</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="font-medium text-red-800 text-sm">AI Training</p>
                <p className="text-xs text-red-600">Customer data NEVER used for model training</p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* SBA Compliance */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
          onClick={() => toggleSection('guide-sba')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Shield className="w-4 h-4 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">SBA 51% Validation & Soft-Block</CardTitle>
                <p className="text-sm text-slate-500">Set-aside compliance rules</p>
              </div>
            </div>
            {expandedSections['guide-sba'] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections['guide-sba'] && (
          <CardContent className="p-6 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-medium text-red-800">Set-Aside Compliance Check</p>
              <p className="text-sm text-red-700 mt-1">
                The system validates Prime's socioeconomic status against the RFP's set-aside type.
              </p>
              <ul className="text-sm text-red-700 mt-2 space-y-1">
                <li>• <strong>RED ALERT</strong>: Triggered if Prime doesn't match (e.g., SDVOSB RFP + Large Business Prime)</li>
                <li>• Alert appears in Pursuit Brain widget and Go/No-Go dashboard</li>
                <li>• Soft-blocks GO decision until resolved or acknowledged</li>
              </ul>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Workshare Validation</p>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Maximum <strong>5 teaming partners</strong> per pursuit</li>
                <li>• Total workshare cannot exceed <strong>100%</strong></li>
                <li>• Prime must maintain 51% for small business set-asides</li>
              </ul>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Legacy/Reference Info Section */}
      <Card className="border-slate-300 shadow-sm bg-slate-50">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-100 transition-colors border-b border-slate-200"
          onClick={() => toggleSection('guide-legacy')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-200 rounded-lg">
                <FileText className="w-4 h-4 text-slate-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-700">Legacy/Reference Info</CardTitle>
                <p className="text-sm text-slate-500">Historical decisions and archived documentation</p>
              </div>
            </div>
            {expandedSections['guide-legacy'] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections['guide-legacy'] && (
          <CardContent className="p-6 space-y-4">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Architecture Decision: MongoDB over PostgreSQL</p>
              <p className="text-sm text-slate-600">
                Chosen for flexible schema, document-oriented storage for proposals, and multi-tenant data isolation.
                Legacy PostgreSQL code is being systematically removed.
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">AI Integration: Gemini Pro (Free Tier)</p>
              <p className="text-sm text-slate-600">
                mngcgroup is registered as a Google business customer, providing Gemini Pro access at $0.00 cost.
                This is tracked in Super Admin → Development Costs.
              </p>
            </div>
            <div className="text-xs text-slate-400 mt-4">
              Note: This section follows the "Persistence Protocol" - content is appended/edited, never deleted without explicit authorization.
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}

function ConsultantGuideContent({ toggleSection, expandedSections }) {
  return (
    <div className="space-y-6">
      {/* Introduction Card */}
      <Card className="border-purple-200 shadow-sm bg-gradient-to-r from-purple-50 to-white">
        <CardHeader className="border-b border-purple-100">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Briefcase className="w-6 h-6 text-purple-700" />
            </div>
            <div>
              <CardTitle className="text-xl text-purple-900">Consultant User Guide</CardTitle>
              <p className="text-sm text-purple-600">Multi-client management and expertise delivery for Federal BD consultants</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-100 mb-6">
            <p className="text-sm text-purple-800">
              <strong>Consultant accounts</strong> are designed for Federal BD consultants who manage multiple contractor clients.
              This guide explains the multi-tenant functionality that makes FedBD.ai your "Operating System" for client management.
            </p>
          </div>

          {/* Feature Comparison Table */}
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-slate-700">Feature</th>
                  <th className="px-4 py-2 text-left font-medium text-slate-700">Contractor Account</th>
                  <th className="px-4 py-2 text-left font-medium text-purple-700">Consultant Account</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr><td className="px-4 py-2 font-medium">Primary Goal</td><td className="px-4 py-2 text-slate-600">Win the Contract</td><td className="px-4 py-2 text-purple-600">Deliver Expertise to Clients</td></tr>
                <tr><td className="px-4 py-2 font-medium">Data Scope</td><td className="px-4 py-2 text-slate-600">Single Org (Internal)</td><td className="px-4 py-2 text-purple-600">Multi-Org (Cross-Client)</td></tr>
                <tr><td className="px-4 py-2 font-medium">Team Management</td><td className="px-4 py-2 text-slate-600">Invites External Reviewers</td><td className="px-4 py-2 text-purple-600">Deploys Staff to Clients</td></tr>
                <tr><td className="px-4 py-2 font-medium">Knowledge Base</td><td className="px-4 py-2 text-slate-600">Corporate Assets (Certs/PP)</td><td className="px-4 py-2 text-purple-600">Methodology & Strategy</td></tr>
                <tr><td className="px-4 py-2 font-medium">Pipeline View</td><td className="px-4 py-2 text-slate-600">Own Pursuits Only</td><td className="px-4 py-2 text-purple-600">All Client Pursuits</td></tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Role Hierarchy */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
          onClick={() => toggleSection('consultant-roles')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">Consultant Role Hierarchy</CardTitle>
                <p className="text-sm text-slate-500">Admin vs Staff Consultant permissions</p>
              </div>
            </div>
            {expandedSections['consultant-roles'] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections['consultant-roles'] && (
          <CardContent className="p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <Badge className="bg-purple-100 text-purple-700 mb-2">CONSULTANT ADMIN</Badge>
                <p className="text-sm font-medium text-slate-900 mb-2">Practice Owner / Leader</p>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Full control over consultancy org</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> View ALL linked clients</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Assign staff to clients</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Manage consultant KB</li>
                </ul>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <Badge className="bg-slate-200 text-slate-700 mb-2">CONSULTANT ASSOCIATE</Badge>
                <p className="text-sm font-medium text-slate-900 mb-2">Staff Consultant</p>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Access assigned clients only</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Edit assigned pursuits</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Use consultant KB (read)</li>
                  <li className="flex items-center gap-2"><Lock className="w-4 h-4 text-slate-400" /> Cannot manage team/clients</li>
                </ul>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Client Switching */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
          onClick={() => toggleSection('consultant-switching')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building2 className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">The Client Switcher Interface</CardTitle>
                <p className="text-sm text-slate-500">How to navigate between multiple clients</p>
              </div>
            </div>
            {expandedSections['consultant-switching'] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections['consultant-switching'] && (
          <CardContent className="p-6 space-y-4">
            <p className="text-sm text-slate-600">
              Consultants often work for 3-4 contractors at once. The <strong>Client Switcher</strong> lets you context-switch
              into specific client environments while maintaining data privacy.
            </p>
            
            <div className="space-y-3">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900 mb-1">Method 1: From Dashboard</p>
                <p className="text-sm text-slate-600">Click any pursuit → Auto-switch to that client's workspace</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900 mb-1">Method 2: From Client Directory</p>
                <p className="text-sm text-slate-600">Click client card → Enter their workspace context</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900 mb-1">Method 3: From Master Pipeline</p>
                <p className="text-sm text-slate-600">Filter by client → Click "View All" → Client-specific view</p>
              </div>
            </div>

            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm font-medium text-emerald-800">Data Isolation Guarantee</p>
              <p className="text-sm text-emerald-700 mt-1">
                When in a client's context, you ONLY see that client's pursuits, KB, and settings.
                Your proprietary methodology is NOT visible to the client.
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Consultant Knowledge Base */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
          onClick={() => toggleSection('consultant-kb')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <BookOpen className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">Consultant Knowledge Base</CardTitle>
                <p className="text-sm text-slate-500">Proprietary IP protection and usage</p>
              </div>
            </div>
            {expandedSections['consultant-kb'] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections['consultant-kb'] && (
          <CardContent className="p-6 space-y-4">
            <p className="text-sm text-slate-600">
              Your Knowledge Base is <strong>YOUR asset</strong>, separate from client data. Store methodology, win strategies,
              and templates without them being absorbed into client KBs.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Asset Type</th>
                    <th className="px-3 py-2 text-left font-medium">Examples</th>
                    <th className="px-3 py-2 text-left font-medium">Purpose</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr><td className="px-3 py-2 font-medium">Methodology</td><td className="px-3 py-2 text-slate-600">Capture frameworks</td><td className="px-3 py-2 text-slate-600">Your proprietary process</td></tr>
                  <tr><td className="px-3 py-2 font-medium">Win Strategies</td><td className="px-3 py-2 text-slate-600">Industry-specific themes</td><td className="px-3 py-2 text-slate-600">Reusable across clients</td></tr>
                  <tr><td className="px-3 py-2 font-medium">Templates</td><td className="px-3 py-2 text-slate-600">Exec summary templates</td><td className="px-3 py-2 text-slate-600">Accelerate drafting</td></tr>
                  <tr><td className="px-3 py-2 font-medium">White Papers</td><td className="px-3 py-2 text-slate-600">Thought leadership</td><td className="px-3 py-2 text-slate-600">Demonstrate expertise</td></tr>
                </tbody>
              </table>
            </div>

            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-medium text-red-800">Privacy Guarantee</p>
              <ul className="text-sm text-red-700 mt-1 space-y-1">
                <li>• KB assets are NEVER copied into client Knowledge Bases</li>
                <li>• Clients cannot see or download your proprietary documents</li>
                <li>• You maintain full ownership and control</li>
              </ul>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Network Effect */}
      <Card className="border-purple-200 bg-purple-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <Zap className="w-6 h-6 text-purple-600 mt-1" />
            <div>
              <p className="font-semibold text-purple-900">The Consultant Network Effect</p>
              <p className="text-sm text-purple-700 mt-1">
                By using FedBD.ai to manage 5 clients more easily than email and Word docs, consultants become the platform's
                primary sales force—insisting that new clients join to streamline collaboration.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ContractorAccountContent() {
  return (
    <div className="space-y-6">
      <Card className="border-teal-200 shadow-sm bg-gradient-to-r from-teal-50 to-white">
        <CardHeader className="border-b border-teal-100">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-teal-100 rounded-xl">
              <Building2 className="w-6 h-6 text-teal-700" />
            </div>
            <div>
              <CardTitle className="text-xl text-teal-900">Contractor Account User Guide</CardTitle>
              <p className="text-sm text-teal-600">Complete onboarding and feature documentation for contractor organizations</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 prose prose-sm max-w-none">
          {/* First 30 Minutes Section */}
          <div className="mb-8">
            <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-teal-600" />
              Your First 30 Minutes
            </h2>
            
            <div className="space-y-4">
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-2">Step 1: Log In & Explore (5 minutes)</h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Log in with credentials from welcome email</li>
                  <li>• Land on <strong>Pursuit Pipeline</strong> - your command center</li>
                  <li>• Sidebar: Dashboard → Knowledge Base → Settings</li>
                </ul>
              </div>

              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-2">Step 2: Set Up Knowledge Base (15 minutes)</h3>
                <p className="text-sm text-slate-600 mb-3">Upload your "Gold Source" corporate assets:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Document Type</th>
                        <th className="px-3 py-2 text-left font-medium">Purpose</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      <tr><td className="px-3 py-2">Capability Statement</td><td className="px-3 py-2 text-slate-600">Matches strengths to opportunities</td></tr>
                      <tr><td className="px-3 py-2">Past Performance</td><td className="px-3 py-2 text-slate-600">Powers fit analysis</td></tr>
                      <tr><td className="px-3 py-2">Key Personnel Resumes</td><td className="px-3 py-2 text-slate-600">Personnel fit evaluation</td></tr>
                      <tr><td className="px-3 py-2">Certifications (8(a), SDVOSB)</td><td className="px-3 py-2 text-slate-600">Compliance & teaming</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-2">Step 3: Start First Pursuit (10 minutes)</h3>
                <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                  <li>Click <strong>+ New Pursuit</strong> from Pipeline</li>
                  <li>Paste SAM.gov URL or enter manually</li>
                  <li>FedBD extracts requirements & generates Fit Score</li>
                  <li>Click "Run AI Analysis" for Go/No-Go recommendation</li>
                </ol>
              </div>
            </div>
          </div>

          {/* RBAC Section */}
          <div className="mb-8">
            <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Role-Based Access Control
            </h2>
            
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Capability</th>
                    <th className="px-3 py-2 text-center font-medium">Admin</th>
                    <th className="px-3 py-2 text-center font-medium">Bid Mgr</th>
                    <th className="px-3 py-2 text-center font-medium">Reviewer</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr className="bg-slate-50"><td className="px-3 py-2 font-medium text-slate-700" colSpan="4">Organization</td></tr>
                  <tr><td className="px-3 py-2">Add/Remove team</td><td className="px-3 py-2 text-center text-emerald-600">✓</td><td className="px-3 py-2 text-center text-slate-400">-</td><td className="px-3 py-2 text-center text-slate-400">-</td></tr>
                  <tr><td className="px-3 py-2">Billing/Settings</td><td className="px-3 py-2 text-center text-emerald-600">✓</td><td className="px-3 py-2 text-center text-slate-400">-</td><td className="px-3 py-2 text-center text-slate-400">-</td></tr>
                  <tr className="bg-slate-50"><td className="px-3 py-2 font-medium text-slate-700" colSpan="4">Knowledge Base</td></tr>
                  <tr><td className="px-3 py-2">Upload/View assets</td><td className="px-3 py-2 text-center text-emerald-600">✓</td><td className="px-3 py-2 text-center text-emerald-600">✓</td><td className="px-3 py-2 text-center text-slate-400">-</td></tr>
                  <tr><td className="px-3 py-2">Delete assets</td><td className="px-3 py-2 text-center text-emerald-600">✓</td><td className="px-3 py-2 text-center text-slate-400">-</td><td className="px-3 py-2 text-center text-slate-400">-</td></tr>
                  <tr className="bg-slate-50"><td className="px-3 py-2 font-medium text-slate-700" colSpan="4">Pursuits</td></tr>
                  <tr><td className="px-3 py-2">View all org pursuits</td><td className="px-3 py-2 text-center text-emerald-600">✓</td><td className="px-3 py-2 text-center text-emerald-600">✓</td><td className="px-3 py-2 text-center text-amber-600">Assigned</td></tr>
                  <tr><td className="px-3 py-2">Create/Edit pursuits</td><td className="px-3 py-2 text-center text-emerald-600">✓</td><td className="px-3 py-2 text-center text-emerald-600">✓</td><td className="px-3 py-2 text-center text-slate-400">-</td></tr>
                  <tr><td className="px-3 py-2">Delete pursuits</td><td className="px-3 py-2 text-center text-emerald-600">✓</td><td className="px-3 py-2 text-center text-slate-400">-</td><td className="px-3 py-2 text-center text-slate-400">-</td></tr>
                  <tr><td className="px-3 py-2">Comments/Annotations</td><td className="px-3 py-2 text-center text-emerald-600">✓</td><td className="px-3 py-2 text-center text-emerald-600">✓</td><td className="px-3 py-2 text-center text-emerald-600">✓</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Managing External Experts */}
          <div className="mb-8">
            <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              Managing External Experts
            </h2>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Safely bring in consultants, red team reviewers, or SMEs</strong> without exposing your entire pipeline.
              </p>
            </div>

            <div className="space-y-3">
              <div className="p-3 bg-white rounded-lg border border-slate-200">
                <p className="font-medium text-slate-900 mb-1">Guest Bid Manager</p>
                <p className="text-sm text-slate-600">Can upload docs, edit drafts, run AI on assigned pursuits only</p>
              </div>
              <div className="p-3 bg-white rounded-lg border border-slate-200">
                <p className="font-medium text-slate-900 mb-1">Guest Reviewer</p>
                <p className="text-sm text-slate-600">Read-only + comments on assigned pursuits only</p>
              </div>
            </div>

            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-medium text-amber-800">Security Boundaries</p>
              <ul className="text-sm text-amber-700 mt-2 space-y-1">
                <li>• Guests see <strong>only</strong> assigned pursuit(s)</li>
                <li>• Cannot access Knowledge Base (Gold Source)</li>
                <li>• Access auto-revokes when pursuit is archived/deleted</li>
              </ul>
            </div>
          </div>

          {/* Data Isolation */}
          <div className="mb-8">
            <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-600" />
              Data Isolation ("Safe-Box")
            </h2>
            
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <p className="text-sm text-emerald-800">
                Your organization operates in a <strong>completely isolated environment</strong>:
              </p>
              <ul className="text-sm text-emerald-700 mt-2 space-y-1">
                <li>• Your data is NEVER visible to other contractors</li>
                <li>• Knowledge Base is NEVER searchable by consultants</li>
                <li>• AI models do NOT train on your proprietary data</li>
                <li>• Super Admin sees metadata only, never proposal content</li>
              </ul>
            </div>
          </div>

          {/* Retention Policy */}
          <div>
            <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              10-Day Retention Policy
            </h2>
            
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 mb-2">
                Documents auto-purge after <strong>10 days of inactivity</strong> unless promoted.
              </p>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Warning emails sent at 2 days and 1 day before purge</li>
                <li>• <strong>Promoted</strong> documents never auto-purge</li>
                <li>• Toggle auto-purge in Settings → Retention</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// SIGNUP FLOW DOCUMENTATION
// ============================================================================
function SignupFlowContent({ toggleSection, expandedSections }) {
  return (
    <div className="space-y-6" data-testid="signup-flow-content">
      {/* Header Card */}
      <Card className="border-teal-200 shadow-sm bg-gradient-to-r from-teal-50 to-white">
        <CardHeader className="border-b border-teal-100">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-teal-100 rounded-xl">
              <UserPlus className="w-6 h-6 text-teal-700" />
            </div>
            <div>
              <CardTitle className="text-xl text-teal-900">User Signup Flow Documentation</CardTitle>
              <p className="text-sm text-teal-600">Step-by-step walkthrough of the signup process for Contractors and Consultants</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Key Insight: Gated Signup</p>
                <p className="text-sm text-amber-700 mt-1">
                  All signups are <strong>gated by admin approval</strong>. Users submit a trial request, and only after the Super Admin 
                  approves it do they receive an invite to complete account setup. This is a B2B SaaS pattern designed to qualify leads before granting access.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Starting Point */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
          onClick={() => toggleSection('signup-landing')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Globe className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">Starting Point: Landing Page</CardTitle>
                <p className="text-sm text-slate-500">Route: / (LandingPage.jsx)</p>
              </div>
            </div>
            {expandedSections['signup-landing'] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections['signup-landing'] && (
          <CardContent className="p-6">
            <p className="text-sm text-slate-600 mb-4">
              When a new user visits the landing page and clicks <strong>"Get Started"</strong> (either in the nav or hero section), 
              a modal opens presenting 3 options:
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg border border-teal-200">
                <Building2 className="w-5 h-5 text-teal-600" />
                <div>
                  <p className="font-medium text-teal-800">"I'm a Contractor"</p>
                  <p className="text-sm text-teal-600">Navigates to <code className="bg-teal-100 px-1 rounded">/pricing?type=contractor</code></p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <Briefcase className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="font-medium text-purple-800">"I'm an Independent Consultant"</p>
                  <p className="text-sm text-purple-600">Navigates to <code className="bg-purple-100 px-1 rounded">/pricing?type=consultant</code></p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Mail className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-800">"Join an Existing Organization"</p>
                  <p className="text-sm text-blue-600">User enters an invite code → Redirected to <code className="bg-blue-100 px-1 rounded">/invite/{'{token}'}</code></p>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Flow A: Contractor Signup */}
      <Card className="border-teal-200 shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-teal-50 transition-colors border-b border-teal-100"
          onClick={() => toggleSection('signup-contractor')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <Building2 className="w-4 h-4 text-teal-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-teal-900">Flow A: Contractor Signup</CardTitle>
                <p className="text-sm text-teal-600">Complete step-by-step contractor registration flow</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-teal-100 text-teal-700">6 Steps</Badge>
              {expandedSections['signup-contractor'] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </div>
          </div>
        </CardHeader>
        {expandedSections['signup-contractor'] && (
          <CardContent className="p-6 space-y-6">
            {/* Screen 1 */}
            <div className="relative pl-8 border-l-2 border-teal-200">
              <div className="absolute -left-3 top-0 w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center text-white text-xs font-bold">1</div>
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-2">Screen 1: Pricing Page</h4>
                <p className="text-xs text-slate-500 mb-2">Route: <code className="bg-slate-100 px-1 rounded">/pricing?type=contractor</code></p>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Shows 3 contractor plans: <strong>Small Business</strong> ($250/mo), <strong>Growth</strong> ($950/mo), <strong>Enterprise</strong> (Custom)</li>
                  <li>• User selects a plan (default: Growth)</li>
                  <li>• Clicks <strong>"Continue with [Plan Name]"</strong></li>
                  <li>• Opens the <strong>Trial Request Modal</strong></li>
                </ul>
              </div>
            </div>

            {/* Screen 2 */}
            <div className="relative pl-8 border-l-2 border-teal-200">
              <div className="absolute -left-3 top-0 w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center text-white text-xs font-bold">2</div>
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-2">Screen 2: Trial Request Modal - Step 1 (Organization Details)</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm mt-2">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Field</th>
                        <th className="px-3 py-2 text-left font-medium">Required</th>
                        <th className="px-3 py-2 text-left font-medium">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      <tr><td className="px-3 py-2 font-medium">Company Name</td><td className="px-3 py-2 text-emerald-600">Yes</td><td className="px-3 py-2 text-slate-600">Legal company name</td></tr>
                      <tr><td className="px-3 py-2 font-medium">UEI</td><td className="px-3 py-2 text-slate-400">No</td><td className="px-3 py-2 text-slate-600">Unique Entity ID</td></tr>
                      <tr><td className="px-3 py-2 font-medium">CAGE Code</td><td className="px-3 py-2 text-slate-400">No</td><td className="px-3 py-2 text-slate-600">5-character code</td></tr>
                      <tr><td className="px-3 py-2 font-medium">Business Size/Set-Aside</td><td className="px-3 py-2 text-emerald-600">Yes</td><td className="px-3 py-2 text-slate-600">Small, WOSB, 8(a), HUBZone, SDVOSB, Large</td></tr>
                      <tr><td className="px-3 py-2 font-medium">Industry Sector</td><td className="px-3 py-2 text-emerald-600">Yes</td><td className="px-3 py-2 text-slate-600">Defense, IT, Healthcare, etc.</td></tr>
                      <tr><td className="px-3 py-2 font-medium">Corporate Website</td><td className="px-3 py-2 text-slate-400">No</td><td className="px-3 py-2 text-slate-600">Company URL</td></tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-slate-600 mt-3">Clicks <strong>"Continue"</strong> to proceed</p>
              </div>
            </div>

            {/* Screen 3 */}
            <div className="relative pl-8 border-l-2 border-teal-200">
              <div className="absolute -left-3 top-0 w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center text-white text-xs font-bold">3</div>
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-2">Screen 3: Trial Request Modal - Step 2 (Contact Information)</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm mt-2">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Field</th>
                        <th className="px-3 py-2 text-left font-medium">Required</th>
                        <th className="px-3 py-2 text-left font-medium">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      <tr><td className="px-3 py-2 font-medium">First Name</td><td className="px-3 py-2 text-emerald-600">Yes</td><td className="px-3 py-2 text-slate-600">-</td></tr>
                      <tr><td className="px-3 py-2 font-medium">Last Name</td><td className="px-3 py-2 text-emerald-600">Yes</td><td className="px-3 py-2 text-slate-600">-</td></tr>
                      <tr><td className="px-3 py-2 font-medium">Work Email</td><td className="px-3 py-2 text-emerald-600">Yes</td><td className="px-3 py-2 text-red-600 font-medium">Must be corporate email (Gmail, Yahoo blocked)</td></tr>
                      <tr><td className="px-3 py-2 font-medium">Phone</td><td className="px-3 py-2 text-slate-400">No</td><td className="px-3 py-2 text-slate-600">-</td></tr>
                      <tr><td className="px-3 py-2 font-medium">Job Title</td><td className="px-3 py-2 text-slate-400">No</td><td className="px-3 py-2 text-slate-600">-</td></tr>
                      <tr><td className="px-3 py-2 font-medium">Message</td><td className="px-3 py-2 text-slate-400">No</td><td className="px-3 py-2 text-slate-600">Capture needs description</td></tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-slate-600 mt-3">Clicks <strong>"Request Trial"</strong></p>
              </div>
            </div>

            {/* Screen 4 */}
            <div className="relative pl-8 border-l-2 border-teal-200">
              <div className="absolute -left-3 top-0 w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center text-white text-xs font-bold">4</div>
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-2">Screen 4: Success Confirmation</h4>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mt-2">
                  <p className="font-medium text-emerald-800">"Request Submitted!"</p>
                  <p className="text-sm text-emerald-700 mt-1">What happens next:</p>
                  <ol className="text-sm text-emerald-700 mt-2 space-y-1 list-decimal list-inside">
                    <li>Our team reviews your application</li>
                    <li>You'll receive an email invitation to activate your trial</li>
                    <li>Set up your account and start your 7-day free trial</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Screen 5 */}
            <div className="relative pl-8 border-l-2 border-amber-200">
              <div className="absolute -left-3 top-0 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-white text-xs font-bold">5</div>
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h4 className="font-semibold text-amber-900 mb-2">Admin Approval Required</h4>
                <p className="text-sm text-amber-800">
                  The user waits for Super Admin approval. Once approved, an email invitation is sent with a unique token.
                </p>
              </div>
            </div>

            {/* Screen 6 */}
            <div className="relative pl-8 border-l-2 border-teal-200">
              <div className="absolute -left-3 top-0 w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center text-white text-xs font-bold">6</div>
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-2">Screen 6: Invite Acceptance Page</h4>
                <p className="text-xs text-slate-500 mb-2">Route: <code className="bg-slate-100 px-1 rounded">/invite/{'{token}'}</code></p>
                <p className="text-sm text-slate-600">See <strong>Flow C: Invitation-Based Signup</strong> for details</p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Flow B: Consultant Signup */}
      <Card className="border-purple-200 shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-purple-50 transition-colors border-b border-purple-100"
          onClick={() => toggleSection('signup-consultant')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Briefcase className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-purple-900">Flow B: Consultant Signup</CardTitle>
                <p className="text-sm text-purple-600">Complete step-by-step consultant registration flow</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-purple-100 text-purple-700">Same as Contractor</Badge>
              {expandedSections['signup-consultant'] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </div>
          </div>
        </CardHeader>
        {expandedSections['signup-consultant'] && (
          <CardContent className="p-6 space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm text-purple-800">
                The Consultant signup flow is <strong>identical to the Contractor flow</strong>, with two key differences:
              </p>
            </div>
            
            <div className="space-y-3">
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-2">Difference 1: Pricing Plans</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• <strong>Independent</strong> ($149/mo) - 2 client slots</li>
                  <li>• <strong>Agency</strong> ($499/mo) - 5 client slots, 3 associates</li>
                  <li>• <strong>Enterprise</strong> (Custom) - Unlimited</li>
                </ul>
              </div>
              
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-2">Difference 2: Request Type</h4>
                <p className="text-sm text-slate-600">
                  The Trial Request Modal passes <code className="bg-slate-100 px-1 rounded">requestType='CONSULTANCY'</code> instead of <code className="bg-slate-100 px-1 rounded">'CONTRACTOR'</code>
                </p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Flow C: Invitation-Based Signup */}
      <Card className="border-blue-200 shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-blue-50 transition-colors border-b border-blue-100"
          onClick={() => toggleSection('signup-invite')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-blue-900">Flow C: Invitation-Based Signup</CardTitle>
                <p className="text-sm text-blue-600">For team members joining via invite link</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-100 text-blue-700">Final Account Setup</Badge>
              {expandedSections['signup-invite'] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </div>
          </div>
        </CardHeader>
        {expandedSections['signup-invite'] && (
          <CardContent className="p-6 space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 font-medium mb-2">This flow is used when:</p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• An existing <strong>Contractor Admin</strong> invites team members (Bid Manager, Reviewer)</li>
                <li>• A <strong>Consultant</strong> invites associates</li>
                <li>• An <strong>Admin approves a trial request</strong> and sends an activation invite</li>
              </ul>
            </div>

            {/* Loading State */}
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <h4 className="font-semibold text-slate-900 mb-2">Loading State</h4>
              <p className="text-sm text-slate-600">Shows "Verifying your invitation..." spinner while validating the token</p>
            </div>

            {/* Error States */}
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <h4 className="font-semibold text-red-900 mb-2">Error States</h4>
              <div className="space-y-2 text-sm text-red-700">
                <p><strong>Invalid Invitation:</strong> "This invitation link is invalid or has already been used."</p>
                <p><strong>Expired Invitation:</strong> "This invitation link has expired. Please contact the person who invited you."</p>
              </div>
              <p className="text-sm text-red-600 mt-2">Buttons: "Go to Login" or "Back to Home"</p>
            </div>

            {/* Valid Invite Form */}
            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <h4 className="font-semibold text-slate-900 mb-3">Screen 1: Invite Acceptance Page (Valid Token)</h4>
              <p className="text-xs text-slate-500 mb-3">Route: <code className="bg-slate-100 px-1 rounded">/invite/{'{token}'}</code></p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Pre-filled (Read-only)</p>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Organization name</li>
                    <li>• Email (invite recipient)</li>
                    <li>• Assigned Role (e.g., "Contractor Admin", "Bid Manager")</li>
                    <li>• Who invited them</li>
                  </ul>
                </div>
                <div className="p-3 bg-teal-50 rounded-lg">
                  <p className="text-xs text-teal-600 uppercase tracking-wider mb-2">User Input</p>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• <strong>First Name*</strong></li>
                    <li>• <strong>Last Name*</strong></li>
                    <li>• <strong>Password*</strong> (min 8 chars)</li>
                    <li>• <strong>Confirm Password*</strong></li>
                  </ul>
                </div>
              </div>
              <p className="text-sm text-slate-600 mt-3">Clicks <strong>"Accept Invitation & Join"</strong></p>
            </div>

            {/* Success */}
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <h4 className="font-semibold text-emerald-900 mb-2">Screen 2: Success</h4>
              <p className="text-sm text-emerald-800">"Welcome to the Team!"</p>
              <p className="text-sm text-emerald-700 mt-1">Displays: "You've joined [Organization Name] as a [Role]"</p>
              <p className="text-sm text-emerald-700 mt-2">
                Button: <strong>"Go to Dashboard"</strong> → Auto-logs in and redirects to workspace
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Comparison Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2">
            <Table className="w-5 h-5 text-slate-600" />
            Flow Comparison Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Step</th>
                  <th className="px-4 py-3 text-left font-medium text-teal-700">Contractor</th>
                  <th className="px-4 py-3 text-left font-medium text-purple-700">Consultant</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="px-4 py-3 font-medium">1</td>
                  <td className="px-4 py-3 text-slate-600">Landing Page → "I'm a Contractor"</td>
                  <td className="px-4 py-3 text-slate-600">Landing Page → "I'm a Consultant"</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">2</td>
                  <td className="px-4 py-3 text-slate-600">Pricing Page (<code>?type=contractor</code>)</td>
                  <td className="px-4 py-3 text-slate-600">Pricing Page (<code>?type=consultant</code>)</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">3</td>
                  <td className="px-4 py-3 text-slate-600">Trial Request - Organization Details</td>
                  <td className="px-4 py-3 text-slate-600">Trial Request - Organization Details</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">4</td>
                  <td className="px-4 py-3 text-slate-600">Trial Request - Contact Info</td>
                  <td className="px-4 py-3 text-slate-600">Trial Request - Contact Info</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">5</td>
                  <td className="px-4 py-3 text-slate-600">"Request Submitted" confirmation</td>
                  <td className="px-4 py-3 text-slate-600">"Request Submitted" confirmation</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-4 py-3 font-medium">6</td>
                  <td className="px-4 py-3 text-amber-700 font-medium" colSpan="2">⏳ Wait for Admin Approval</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">7</td>
                  <td className="px-4 py-3 text-slate-600">Receive email → <code>/invite/{'{token}'}</code></td>
                  <td className="px-4 py-3 text-slate-600">Receive email → <code>/invite/{'{token}'}</code></td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">8</td>
                  <td className="px-4 py-3 text-slate-600">Complete profile (name + password)</td>
                  <td className="px-4 py-3 text-slate-600">Complete profile (name + password)</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-medium">9</td>
                  <td className="px-4 py-3 text-emerald-700">→ Contractor Dashboard</td>
                  <td className="px-4 py-3 text-emerald-700">→ Consultant Dashboard</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Technical Reference */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
          onClick={() => toggleSection('signup-tech-ref')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Code className="w-4 h-4 text-slate-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">Technical Reference</CardTitle>
                <p className="text-sm text-slate-500">File paths and API endpoints</p>
              </div>
            </div>
            {expandedSections['signup-tech-ref'] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections['signup-tech-ref'] && (
          <CardContent className="p-6 space-y-4">
            <div>
              <h4 className="font-semibold text-slate-900 mb-2">Frontend Files</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded font-mono">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span>/src/pages/LandingPage.jsx</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded font-mono">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span>/src/pages/PricingPage.jsx</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded font-mono">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span>/src/components/modals/TrialRequestModal.jsx</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded font-mono">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span>/src/pages/auth/InviteAcceptancePage.jsx</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded font-mono">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span>/src/pages/auth/ClientRegistrationPage.jsx</span>
                  <Badge className="bg-amber-100 text-amber-700 text-xs">Legacy - Direct Registration</Badge>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-slate-900 mb-2">API Endpoints</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
                  <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">POST</Badge>
                  <code className="text-slate-700">/api/trial/request</code>
                  <span className="text-slate-500">- Submit trial request</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded">
                  <Badge className="bg-emerald-100 text-emerald-700 font-mono text-xs">GET</Badge>
                  <code className="text-slate-700">/api/auth/invite/{'{token}'}</code>
                  <span className="text-slate-500">- Validate invite token</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
                  <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">POST</Badge>
                  <code className="text-slate-700">/api/auth/invite/{'{token}'}/accept</code>
                  <span className="text-slate-500">- Accept invite & create account</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
                  <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">POST</Badge>
                  <code className="text-slate-700">/api/auth/register</code>
                  <span className="text-slate-500">- Direct registration (legacy)</span>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="font-medium text-amber-800 text-sm">Note: Corporate Email Validation</p>
              <p className="text-sm text-amber-700 mt-1">
                Personal email domains (Gmail, Yahoo, Hotmail, etc.) are no longer hard-blocked.
                Soft warning is shown and the request is flagged with <code className="bg-amber-100 px-1 rounded">is_personal_email=true</code> on the Admin dashboard.
              </p>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}

// ============================================================================
// BILLING & MONETIZATION DOC COMPONENTS (Stripe v1, Phase 1-8 + Phase 6c)
// ============================================================================

function StripeOverviewContent() {
  return (
    <div className="space-y-6">
      <Card className="border-emerald-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-slate-900 flex items-center gap-2"
                     style={{ fontFamily: 'Manrope, sans-serif' }}>
            <DollarSign className="w-6 h-6 text-emerald-600" />
            Stripe Billing v1 — Architecture Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm text-slate-700 leading-relaxed">
          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">What we built</p>
            <p>
              FedBD&apos;s billing layer combines three revenue surfaces into one Stripe-backed system:
              <strong> recurring subscriptions</strong>, <strong>one-time pursuit-slot top-ups</strong>,
              and <strong>self-serve customer portal</strong>. AI usage billing was removed — every
              org now brings its own BYOM provider key; there is no platform-metered AI product.
              Frontend never sends prices — it sends <code className="bg-slate-100 px-1 rounded">plan_id</code> +
              <code className="bg-slate-100 px-1 rounded"> seats</code> + <code className="bg-slate-100 px-1 rounded">cycle</code>;
              backend resolves to the canonical price from <code className="bg-slate-100 px-1 rounded">app/billing/plans.py</code>.
            </p>
          </div>

          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">Key files</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><code className="bg-slate-100 px-1 rounded">app/billing/plans.py</code> — Plan + slot package definitions, feature-pegged discount math, founding-member coupon spec. Single source of truth for pricing. (AI credit pack definitions + markup constants removed along with the AI Credits product.)</li>
              <li><code className="bg-slate-100 px-1 rounded">app/billing/stripe_prices.py</code> — Lazy-creates Stripe Products + Prices for each plan, cached in <code className="bg-slate-100 px-1 rounded">stripe_product_cache</code> / <code className="bg-slate-100 px-1 rounded">stripe_price_cache</code>. Pins SDK to <code className="bg-slate-100 px-1 rounded">api_version=2024-09-30.acacia</code>.</li>
              <li><code className="bg-slate-100 px-1 rounded">app/billing/coupons.py</code> — Idempotent FOUNDER20 + feature-release-discount Coupon / PromotionCode provisioning.</li>
              <li><code className="bg-slate-100 px-1 rounded">app/middleware/plan_gating.py</code> — <code className="bg-slate-100 px-1 rounded">assert_user_can_use_feature(user, feature_id, request)</code>. Two-axis gate (Feature Release Console flag + plan tier feature_set). Wired into 5 Sentinel routes; reusable for other paid features.</li>
              <li><code className="bg-slate-100 px-1 rounded">app/routes/llm_router.py</code> — <code className="bg-slate-100 px-1 rounded">resolve_llm_config(org_id)</code> resolves an org&apos;s BYOM key only (no platform fallback, no billing); <code className="bg-slate-100 px-1 rounded">call_llm(&hellip;)</code> is a pure direct-SDK caller now.</li>
              <li><code className="bg-slate-100 px-1 rounded">app/routes/stripe_billing.py</code> — All HTTP endpoints + webhook receiver (handles SLOT_TOPUP, SUBSCRIPTION).</li>
              <li><code className="bg-slate-100 px-1 rounded">frontend/src/pages/billing/SubscribePage.jsx</code> — Plan picker + live quote.</li>
              <li><code className="bg-slate-100 px-1 rounded">frontend/src/pages/PricingPage.jsx</code> — Live catalog + comparison table. Still contains a legacy &quot;AI Credits&quot; marketing section pending a copy rewrite (tracked separately — needs product/legal sign-off, not a mechanical deletion).</li>
              <li><code className="bg-slate-100 px-1 rounded">frontend/src/pages/consultant/ConsultancySettings.jsx::OrgAIConfigurationCard</code> — Shared BYOM config card (named-exported, also imported by <code className="bg-slate-100 px-1 rounded">WorkspaceSettings.jsx</code>).</li>
              <li><code className="bg-slate-100 px-1 rounded">frontend/src/components/modals/SlotTopUpModal.jsx</code> — Slot top-up purchase flow.</li>
              <li><code className="bg-slate-100 px-1 rounded">frontend/src/components/CustomerPortalButton.jsx</code> — Stripe-hosted portal launcher.</li>
              <li><code className="bg-slate-100 px-1 rounded">frontend/src/components/TrialEndBanner.jsx</code> — In-last-3-days conversion banner.</li>
            </ul>
          </div>

          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">Configuration</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><code className="bg-slate-100 px-1 rounded">STRIPE_API_KEY</code> in <code className="bg-slate-100 px-1 rounded">backend/.env</code> — sk_test_... for staging, sk_live_... for production.</li>
              <li>Stripe API version pinned (some accounts default to a beta version that renames params).</li>
              <li>Customer Portal must be activated once at <a href="https://dashboard.stripe.com/test/settings/billing/portal" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">dashboard.stripe.com/test/settings/billing/portal</a>. Backend returns 503 with a helpful pointer if not configured.</li>
            </ul>
          </div>

          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">Endpoint inventory</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Method</th>
                    <th className="text-left px-3 py-2 font-semibold">Path</th>
                    <th className="text-left px-3 py-2 font-semibold">Purpose</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {[
                    ['GET', '/api/billing/health', 'Super Admin: confirm Stripe is wired'],
                    ['GET', '/api/billing/plans', 'Public: plan catalog + slot packages + feature-pegged pricing fields'],
                    ['POST', '/api/billing/quote', 'Server-side price calc honoring release discount'],
                    ['POST', '/api/billing/ai-credit-quote', 'Apply 1.5× / 0.5× markup to an underlying cost'],
                    ['POST', '/api/billing/slots/checkout', 'Start one-time slot top-up Checkout'],
                    ['POST', '/api/billing/subscribe/checkout', 'Start subscription Checkout (auto-attaches release-discount coupon)'],
                    ['GET', '/api/billing/checkout/status/{id}', 'Frontend polls after redirect'],
                    ['GET', '/api/billing/subscription', 'Calling org\u2019s current sub state'],
                    ['POST', '/api/billing/coupons/ensure', 'Super Admin: ensure FOUNDER20 exists'],
                    ['POST', '/api/billing/founding-member/toggle', 'Super Admin: flip is_founding_member'],
                    ['GET', '/api/billing/ai-credits/packages', 'Public: prepaid AI credit pack catalog ($200/$500/$1000)'],
                    ['GET', '/api/billing/ai-credits/balance', 'Customer: current balance + ledger; honors pursuit-owner override'],
                    ['POST', '/api/billing/ai-credits/checkout', 'Customer: buy a prepaid credit pack'],
                    ['PUT', '/api/billing/ai-credits/auto-recharge', 'Customer: configure auto-recharge thresholds'],
                    ['POST', '/api/billing/portal/session', 'Customer: Stripe-hosted portal launcher'],
                    ['POST', '/api/webhook/stripe', 'Stripe push (signature-verified)'],
                  ].map(([method, path, desc]) => (
                    <tr key={path}>
                      <td className="px-3 py-1.5"><Badge className="bg-blue-100 text-blue-700 text-xs">{method}</Badge></td>
                      <td className="px-3 py-1.5"><code className="text-xs">{path}</code></td>
                      <td className="px-3 py-1.5 text-slate-600">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <p className="font-medium text-emerald-900 text-sm mb-1">Money never lies</p>
            <p className="text-sm text-emerald-800">
              Every charge originates from a server-side lookup in <code className="bg-emerald-100 px-1 rounded">plans.py</code>.
              Frontend can&apos;t supply a custom amount. Webhooks + status-poll race protection ensures slot increments
              and subscription provisioning happen <strong>exactly once</strong> per payment.
            </p>
          </div>

          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">Post-payment user experience (Apr 2026 sprint)</p>
            <p className="text-slate-700 mb-2">
              The post-payment flow is now uniform across all three Stripe surfaces (slot top-up, AI credits, subscription).
              See Release Notes v3.5 for the full timeline; this is the architecture summary.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Top-level return routes</strong>: <code className="bg-slate-100 px-1 rounded">/billing/slots/return</code>, <code className="bg-slate-100 px-1 rounded">/billing/ai-credits/return</code>, <code className="bg-slate-100 px-1 rounded">/billing/subscribe/return</code>.
                Workspace-scoped duplicates exist as fallbacks. Initiator code paths now pass <code className="bg-slate-100 px-1 rounded">origin_url=&#36;origin/&#36;slug</code> so success/cancel URLs land inside the tenant workspace.
              </li>
              <li>
                <strong>Idempotency anchor</strong>: <code className="bg-slate-100 px-1 rounded">transactions</code> collection. Webhook + status-poll both look up the row by <code className="bg-slate-100 px-1 rounded">stripe_session_id</code> and short-circuit when status is already <code className="bg-slate-100 px-1 rounded">COMPLETED</code>. Slot/credit/subscription mutations are <code className="bg-slate-100 px-1 rounded">$inc</code>-based against the <code className="bg-slate-100 px-1 rounded">organizations</code> doc.
              </li>
              <li>
                <strong>Return page state machine</strong>: polling (up to 20 attempts × 2s = 40s window) → success (3-second hold + auto-redirect to workspace settings) → failed (with soft &ldquo;still processing&rdquo; copy when timed out, since the webhook may simply be slow). Verified via <code className="bg-slate-100 px-1 rounded">/api/billing/checkout/status/&#123;id&#125;</code>.
              </li>
              <li>
                <strong>Cross-page success toast</strong>: <code className="bg-slate-100 px-1 rounded">lib/pendingCheckout.js</code> drops a localStorage record on every Checkout initiation. <code className="bg-slate-100 px-1 rounded">DashboardLayout</code> mounts <code className="bg-slate-100 px-1 rounded">usePendingCheckoutToast()</code> which polls once on dashboard mount for any record older than 10s and fires a Sonner success/failed toast — so a user who closed the return tab still gets acknowledgment of their purchase next time they hit the dashboard. Records auto-expire after 30 minutes.
              </li>
              <li>
                <strong>AI Credits Balance Pill</strong>: mounted in the global <code className="bg-slate-100 px-1 rounded">DashboardLayout</code> top header. Refreshes every 60s + on window focus. Color-coded (emerald / amber / red), inline &quot;+ Top up&quot; CTA when low or exhausted, click → workspace Settings. Hidden for Super Admin.
              </li>
              <li>
                <strong>Truthful AI errors</strong>: <code className="bg-slate-100 px-1 rounded">CreditsExhaustedError(402)</code> propagates with structured detail <code className="bg-slate-100 px-1 rounded">&#123; code: &quot;AI_CREDITS_EXHAUSTED&quot;, balance_cents, org_id, message &#125;</code>. Bid Pursuit Scorecard frontend renders a Sonner toast with a working &quot;Top up&quot; CTA. (Pre-Apr 2026 the per-chunk handler in <code className="bg-slate-100 px-1 rounded">scorecard.py::_score_chunk</code> swallowed it and returned a misleading &quot;AI returned invalid format&quot; — fixed.)
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PlanCatalogContent() {
  return (
    <div className="space-y-6">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-slate-900 flex items-center gap-2"
                     style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Layers className="w-6 h-6 text-emerald-600" />
            Plan Catalog & Pricing Logic
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm text-slate-700 leading-relaxed">
          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">Hybrid pricing model</p>
            <p>
              Each plan has a <strong>flat base</strong> + <strong>included seats/pursuits</strong> + <strong>per-unit overage</strong>.
              Annual cycle = 10× monthly base (2 months free); per-seat overage stays monthly even on annual plans (Stripe handles proration).
            </p>
          </div>

          {/* ── Feature-Pegged Dynamic Pricing (added when admin discovered
                that toggling a feature flag silently re-prices every paid
                tier through the release-discount mechanism). ──────────── */}
          <div className="rounded-md border border-amber-300 bg-amber-50 p-4">
            <p className="text-base font-semibold text-amber-900 mb-2 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Feature-Pegged Dynamic Pricing
            </p>
            <p className="mb-3">
              Each plan also declares a <code className="font-mono bg-white px-1 rounded">max_release_discount_pct</code>.
              The actual discount applied at checkout is <strong>linearly interpolated</strong>
              by how many of the plan's features are currently live in production:
            </p>
            <pre className="bg-slate-900 text-slate-100 px-3 py-2 rounded text-[11px] overflow-x-auto leading-relaxed">{`discount% = max_release_discount_pct × (1 − features_live ÷ features_in_plan)
            ↳ then snapped to the nearest 2.5%`}</pre>
            <p className="mt-3 mb-2">
              <strong>Net effect:</strong> turning a feature flag ON shrinks every plan's release discount —
              raising the effective monthly price for every <em>new</em> signup. Existing subscribers stay
              on their locked rate until upgrade / downgrade / renewal.
            </p>
            <table className="w-full text-xs border border-amber-200 mt-2">
              <thead className="bg-amber-100">
                <tr>
                  <th className="px-3 py-2 text-left">Plan</th>
                  <th className="px-3 py-2 text-right">Base / mo</th>
                  <th className="px-3 py-2 text-right">Max release discount</th>
                  <th className="px-3 py-2 text-right">Today (2/12 features)</th>
                  <th className="px-3 py-2 text-right">After +3 features (5/12)</th>
                  <th className="px-3 py-2 text-right">Δ / mo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-200">
                <tr><td className="px-3 py-2 font-mono">SMALL_BUSINESS</td><td className="px-3 py-2 text-right">$250</td><td className="px-3 py-2 text-right">20%</td><td className="px-3 py-2 text-right">$231.25</td><td className="px-3 py-2 text-right">$250.00</td><td className="px-3 py-2 text-right text-emerald-700 font-semibold">+$18.75</td></tr>
                <tr><td className="px-3 py-2 font-mono">GROWTH</td><td className="px-3 py-2 text-right">$950</td><td className="px-3 py-2 text-right">40%</td><td className="px-3 py-2 text-right">$641.25</td><td className="px-3 py-2 text-right">$736.25</td><td className="px-3 py-2 text-right text-emerald-700 font-semibold">+$95.00</td></tr>
                <tr><td className="px-3 py-2 font-mono">CONTRACTOR_ENTERPRISE</td><td className="px-3 py-2 text-right">CUSTOM</td><td className="px-3 py-2 text-right">—</td><td className="px-3 py-2 text-right">—</td><td className="px-3 py-2 text-right">—</td><td className="px-3 py-2 text-right text-slate-400">no change</td></tr>
                <tr><td className="px-3 py-2 font-mono">INDEPENDENT</td><td className="px-3 py-2 text-right">$149</td><td className="px-3 py-2 text-right">20%</td><td className="px-3 py-2 text-right">$137.83</td><td className="px-3 py-2 text-right">$149.00</td><td className="px-3 py-2 text-right text-emerald-700 font-semibold">+$11.17</td></tr>
                <tr><td className="px-3 py-2 font-mono">AGENCY</td><td className="px-3 py-2 text-right">$499</td><td className="px-3 py-2 text-right">40%</td><td className="px-3 py-2 text-right">$336.83</td><td className="px-3 py-2 text-right">$386.73</td><td className="px-3 py-2 text-right text-emerald-700 font-semibold">+$49.90</td></tr>
                <tr><td className="px-3 py-2 font-mono">CONSULTANCY_ENTERPRISE</td><td className="px-3 py-2 text-right">CUSTOM</td><td className="px-3 py-2 text-right">—</td><td className="px-3 py-2 text-right">—</td><td className="px-3 py-2 text-right">—</td><td className="px-3 py-2 text-right text-slate-400">no change</td></tr>
              </tbody>
            </table>
            <p className="text-[11px] text-amber-800 mt-3 italic">
              Example uses the three flagged features <code className="font-mono">go_no_go</code> + <code className="font-mono">gap_analysis</code> + <code className="font-mono">compliance_tracker</code> being turned ON. The Feature Release Console (<code>/admin/features</code>) now shows this exact preview table before committing any toggle so the admin sees the $ delta in real time.
            </p>
            <p className="text-[11px] text-amber-800 mt-2">
              <strong>API:</strong> <code className="font-mono">POST /api/billing/plans/preview</code> with body <code className="font-mono">{`{ enabled_features: [...] }`}</code> returns the full <code className="font-mono">/api/billing/plans</code> shape for any hypothetical flag set. Useful for backend automation / audit reports.
            </p>
            <p className="text-[11px] text-amber-800 mt-2">
              <strong>Repricing existing customers:</strong> the discount is snapshotted on the Stripe Subscription Item at checkout. To roll a price change to existing accounts, wait for natural renewal OR issue a Stripe coupon for grandfathered customers and prorate via <code className="font-mono">subscriptions.update(proration_behavior=&quot;always_invoice&quot;)</code>.
            </p>
          </div>

          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">Contractor plans</p>
            <table className="w-full text-xs border border-slate-200">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-3 py-2 text-left">Plan</th>
                  <th className="px-3 py-2 text-right">Base / mo</th>
                  <th className="px-3 py-2 text-right">Annual</th>
                  <th className="px-3 py-2 text-right">Seats incl.</th>
                  <th className="px-3 py-2 text-right">$/extra seat</th>
                  <th className="px-3 py-2 text-right">Pursuits incl.</th>
                  <th className="px-3 py-2 text-right">$/extra pursuit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr><td className="px-3 py-1.5">Small Business</td><td className="px-3 py-1.5 text-right">$250</td><td className="px-3 py-1.5 text-right">$2,500</td><td className="px-3 py-1.5 text-right">3</td><td className="px-3 py-1.5 text-right">$49</td><td className="px-3 py-1.5 text-right">2</td><td className="px-3 py-1.5 text-right">$99</td></tr>
                <tr><td className="px-3 py-1.5"><strong>Growth</strong></td><td className="px-3 py-1.5 text-right">$950</td><td className="px-3 py-1.5 text-right">$9,500</td><td className="px-3 py-1.5 text-right">10</td><td className="px-3 py-1.5 text-right">$79</td><td className="px-3 py-1.5 text-right">10</td><td className="px-3 py-1.5 text-right">$149</td></tr>
                <tr><td className="px-3 py-1.5">Enterprise</td><td colSpan={6} className="px-3 py-1.5 text-center text-slate-500 italic">Custom — sales-driven</td></tr>
              </tbody>
            </table>
          </div>

          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">Consultancy plans</p>
            <table className="w-full text-xs border border-slate-200">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-3 py-2 text-left">Plan</th>
                  <th className="px-3 py-2 text-right">Base / mo</th>
                  <th className="px-3 py-2 text-right">Annual</th>
                  <th className="px-3 py-2 text-right">Seats incl.</th>
                  <th className="px-3 py-2 text-right">$/extra seat</th>
                  <th className="px-3 py-2 text-right">Clients incl.</th>
                  <th className="px-3 py-2 text-right">$/extra client</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr><td className="px-3 py-1.5">Independent</td><td className="px-3 py-1.5 text-right">$149</td><td className="px-3 py-1.5 text-right">$1,490</td><td className="px-3 py-1.5 text-right">1</td><td className="px-3 py-1.5 text-right">$49</td><td className="px-3 py-1.5 text-right">2</td><td className="px-3 py-1.5 text-right">$79</td></tr>
                <tr><td className="px-3 py-1.5"><strong>Agency</strong></td><td className="px-3 py-1.5 text-right">$499</td><td className="px-3 py-1.5 text-right">$4,990</td><td className="px-3 py-1.5 text-right">4</td><td className="px-3 py-1.5 text-right">$89</td><td className="px-3 py-1.5 text-right">5</td><td className="px-3 py-1.5 text-right">$99</td></tr>
                <tr><td className="px-3 py-1.5">Enterprise</td><td colSpan={6} className="px-3 py-1.5 text-center text-slate-500 italic">Custom — sales-driven</td></tr>
              </tbody>
            </table>
          </div>

          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">Slot top-up packages (one-time)</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>5 slots</strong> = $399 (great for a single quarter spike)</li>
              <li><strong>10 slots</strong> = $699 (12% saving vs the 5-pack)</li>
              <li><strong>25 slots</strong> = $1,499 (best value — high-velocity teams)</li>
            </ul>
          </div>

          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">Feature-Pegged Pricing (since v3.2)</p>
            <p>
              Each non-custom plan declares a <code className="bg-slate-100 px-1 rounded">feature_set</code> (the
              feature IDs it advertises) and a <code className="bg-slate-100 px-1 rounded">max_release_discount_pct</code> (the
              biggest haircut the customer gets when ZERO of those features are live). Live discount is
              <strong> linear interpolation, snapped to the nearest 2.5%</strong>:
            </p>
            <pre className="bg-slate-900 text-slate-100 text-xs p-3 rounded mt-2 overflow-x-auto">
{`released_ratio = |feature_set ∩ enabled_features| / |feature_set|
discount_pct   = max_release_discount_pct × (1 − released_ratio)
               # snapped half-up to the nearest 2.5%
effective_base = base_monthly × (1 − discount_pct/100)`}
            </pre>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>SMALL_BUSINESS / INDEPENDENT</strong> → feature_set = <code>opportunity_intake, document_ingestion, go_no_go</code>; max discount <strong>20%</strong>. Sentinel intentionally NOT in base tier.</li>
              <li><strong>GROWTH / AGENCY</strong> → feature_set = base + Sentinel + Gap Analysis + Compliance Tracker + Bid Scorecard + Capability Matrix + Compliance Matrix + Proposal Workspace + Submission Guide + Knowledge Base; max discount <strong>40%</strong>.</li>
              <li><strong>ENTERPRISE</strong> (both org types) — custom, no auto-discount.</li>
            </ul>
            <p className="mt-2 text-xs text-slate-500">
              Overage (per-seat / per-pursuit) is NEVER discounted — only the base. The
              Pricing page surfaces the effective price + struck-through full price + amber
              &quot;X% off · pre-release&quot; badge + &quot;N of M features released&quot; sub-line.
              Stripe checkout auto-attaches a <code className="bg-slate-100 px-1 rounded">forever</code>-duration coupon
              (cached in <code className="bg-slate-100 px-1 rounded">stripe_coupon_cache</code> by % value)
              when no founding-member or typed promo is already active.
            </p>
          </div>

          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">Plan-Based Feature Gating (since v3.2)</p>
            <p>
              Two-axis check on every paid feature:
            </p>
            <ol className="list-decimal pl-5 space-y-1 mt-1">
              <li>Platform Feature Release Console flag must be ON → otherwise <strong>403 FEATURE_NOT_RELEASED</strong>.</li>
              <li>Org&apos;s <code className="bg-slate-100 px-1 rounded">subscription_plan.feature_set</code> must include the feature → otherwise <strong>402 UPGRADE_REQUIRED</strong> with <code className="bg-slate-100 px-1 rounded">current_plan</code> in the body.</li>
            </ol>
            <p className="mt-2">
              Implemented via <code className="bg-slate-100 px-1 rounded">app/middleware/plan_gating.py::assert_user_can_use_feature(user, feature_id, request)</code>.
              Super Admins and Consultants (cross-tenant operators) bypass the gate. Currently wired into
              5 Sentinel routes (<code className="bg-slate-100 px-1 rounded">/api/sentinel/opportunities</code> GET,
              <code className="bg-slate-100 px-1 rounded">/opportunities/&#123;id&#125;/claim</code>,
              <code className="bg-slate-100 px-1 rounded">/scan</code>,
              <code className="bg-slate-100 px-1 rounded">/preferences</code> GET + PUT). Helper is generic;
              add to other paid routes (Bid Scorecard, Capability Matrix, Compliance Matrix, etc.) as they ship.
            </p>
          </div>

          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">Founding-member coupon</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><code className="bg-slate-100 px-1 rounded">FOUNDER20</code> = 20% off, repeating for 12 months.</li>
              <li>Auto-applied when <code className="bg-slate-100 px-1 rounded">organizations.is_founding_member=true</code> at subscription checkout time.</li>
              <li>Customers can also type <code className="bg-slate-100 px-1 rounded">FOUNDER20</code> as a promo code on the Subscribe page.</li>
              <li>Toggle via <code className="bg-slate-100 px-1 rounded">POST /api/billing/founding-member/toggle</code> (Super Admin only).</li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="font-medium text-blue-900 text-sm mb-1">Quote math example</p>
            <p className="text-sm text-blue-800 font-mono">
              GROWTH + 12 seats + 12 pursuits, monthly:<br/>
              $950 base + 2 extra seats × $79 + 2 extra pursuits × $149 = <strong>$1,406/mo</strong>
            </p>
            <p className="text-sm text-blue-800 font-mono mt-2">
              Same selection, annual: $9,500 base + 12×(2×$79) + 12×(2×$149) = <strong>$14,972/yr</strong>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AiCreditBillingContent() {
  return (
    <div className="space-y-6">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-slate-900 flex items-center gap-2"
                     style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Cpu className="w-6 h-6 text-emerald-600" />
            AI Usage — Pure BYOM (Bring Your Own Model)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm text-slate-700 leading-relaxed">

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800">
            <strong>Current model:</strong> There is no platform credit ledger, prepaid balance, or
            markup billing. Every organization configures its own LLM provider API key under
            Settings → AI Config. AI calls are billed directly by the provider (Anthropic / OpenAI /
            Google) to the org's own account — the platform does not intermediate, meter, or
            markup usage cost. See <code className="bg-emerald-100 px-1 rounded">resolve_llm_config()</code> in
            <code className="bg-emerald-100 px-1 rounded ml-1">app/routes/llm_router.py</code>.
          </div>

          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">No org key configured</p>
            <p>
              <code className="bg-slate-100 px-1 rounded">resolve_llm_config(org_id)</code> raises
              <code className="bg-slate-100 px-1 rounded ml-1">400 BYOM_NOT_CONFIGURED</code>. Individual
              callers handle this gracefully rather than crashing — e.g. the support-ticket classifier
              falls back to a static response, roadmap-proposal generation returns a placeholder
              message. There is no vendor-hosted or system-wide fallback key.
            </p>
          </div>

          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">Pursuit-attributed BYOM</p>
            <p>
              When a consultant invited to a contractor&apos;s pursuit makes an LLM call, the BYOM
              key resolution falls on the <strong>pursuit owner</strong> (the contractor), not the
              consultant. Single chokepoint:
              <code className="bg-slate-100 px-1 rounded ml-1">resolve_billing_org(user_org_id, pursuit_id) → billing_org_id</code>.
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li><code className="bg-slate-100 px-1 rounded">resolve_llm_config_for_pursuit(pursuit_id, user_org_id)</code> resolves the key against the pursuit owner's org, not the caller's own org (prevents a consultant's own BYOM key from being used against a contractor's pursuit, or vice versa).</li>
              <li>Used by <code className="bg-slate-100 px-1 rounded">mongo_pursuits.py</code> strategic-tip generation and other pursuit-scoped AI features.</li>
            </ul>
          </div>

          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">Wired call sites</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Universal LLM path — <code className="bg-slate-100 px-1 rounded">call_llm()</code> in <code className="bg-slate-100 px-1 rounded">app/routes/llm_router.py</code>.</li>
              <li>PWIN Oracle, AI Swarm Shredder, AI Swarm Compliance Matrix, Compliance Matrix full build, Scorecard AI prefill, Capability Matrix LCAT extraction, Support Ticket Classifier — all flow through <code className="bg-slate-100 px-1 rounded">call_llm()</code>, each resolving its own org's (or the pursuit owner's) BYOM key first.</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function WebhooksAuditContent() {
  return (
    <div className="space-y-6">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-slate-900 flex items-center gap-2"
                     style={{ fontFamily: 'Manrope, sans-serif' }}>
            <GitBranch className="w-6 h-6 text-emerald-600" />
            Webhooks, Audit & Idempotency
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm text-slate-700 leading-relaxed">
          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">Webhook endpoint</p>
            <p>
              <code className="bg-slate-100 px-1 rounded">POST /api/webhook/stripe</code> verifies the
              <code className="bg-slate-100 px-1 rounded"> Stripe-Signature</code> header via <code className="bg-slate-100 px-1 rounded">stripe.Webhook.construct_event()</code> (native Stripe SDK).
              Returns <strong>400</strong> on a missing or invalid signature. The endpoint is idempotent:
              re-deliveries of the same <code className="bg-slate-100 px-1 rounded">event_id</code> are detected
              against <code className="bg-slate-100 px-1 rounded">billing_events</code> and short-circuited.
            </p>
          </div>

          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">Events handled</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><code className="bg-slate-100 px-1 rounded">checkout.session.completed</code> — Branches by <code className="bg-slate-100 px-1 rounded">metadata.purpose</code>: <strong>SLOT_TOPUP</strong> increments <code className="bg-slate-100 px-1 rounded">purchased_slots</code>; <strong>SUBSCRIPTION</strong> persists <code className="bg-slate-100 px-1 rounded">stripe_subscription_id</code>, status, current_period_end. Idempotent via the <code className="bg-slate-100 px-1 rounded">payment_transactions</code> row keyed on <code className="bg-slate-100 px-1 rounded">stripe_session_id</code>.</li>
              <li><code className="bg-slate-100 px-1 rounded">customer.subscription.updated</code> — Sync status (active / past_due / canceled / trialing).</li>
              <li><code className="bg-slate-100 px-1 rounded">customer.subscription.deleted</code> — Mark canceled.</li>
              <li><code className="bg-slate-100 px-1 rounded">invoice.paid</code> — payment_state = active.</li>
              <li><code className="bg-slate-100 px-1 rounded">invoice.payment_failed</code> — payment_state = past_due (dunning trigger).</li>
              <li><code className="bg-slate-100 px-1 rounded">customer.subscription.trial_will_end</code> — logged; banner already covers UX.</li>
            </ul>
          </div>

          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">Audit collections</p>
            <table className="w-full text-xs border border-slate-200">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-3 py-2 text-left">Collection</th>
                  <th className="px-3 py-2 text-left">What it stores</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr><td className="px-3 py-1.5"><code>payment_transactions</code></td><td className="px-3 py-1.5">One row per Checkout intent (INITIATED → COMPLETED / FAILED / EXPIRED). Includes session_id, organization_id, purpose (<code>SUBSCRIPTION</code> | <code>SLOT_TOPUP</code> | <code>OTHER</code>), amount, metadata.</td></tr>
                <tr><td className="px-3 py-1.5"><code>billing_events</code></td><td className="px-3 py-1.5">Raw webhook log with event_id deduping. Marked <code>processed: true</code> after side-effects run.</td></tr>
                <tr><td className="px-3 py-1.5"><code>stripe_product_cache</code></td><td className="px-3 py-1.5">plan_id → stripe_product_id. Prevents duplicate Product creation across restarts.</td></tr>
                <tr><td className="px-3 py-1.5"><code>stripe_price_cache</code></td><td className="px-3 py-1.5">(plan_id, kind) → price_id. Kind = base_monthly / base_annual / seat_monthly / pursuit_monthly.</td></tr>
                <tr><td className="px-3 py-1.5"><code>stripe_coupon_cache</code></td><td className="px-3 py-1.5">FOUNDER20 + per-percentage release-discount coupons (keys: <code>RELEASE_DISCOUNT_&lt;pct&gt;</code>).</td></tr>
                <tr><td className="px-3 py-1.5"><code>ai_credit_ledger</code></td><td className="px-3 py-1.5"><strong>v3 source-of-truth</strong>. Immutable per-call +/- with running balance, source, is_byom, model, actor_user_id, pursuit_id, stripe_session_id.</td></tr>
                <tr><td className="px-3 py-1.5"><code>ai_usage_events</code></td><td className="px-3 py-1.5">Kept for analytics back-compat. Now mirrored from <code>debit_credits()</code> path.</td></tr>
                <tr><td className="px-3 py-1.5 text-slate-400 line-through"><code>ai_billing_runs</code></td><td className="px-3 py-1.5 text-slate-400">DEPRECATED — month-end aggregator. New billing is real-time prepaid via <code>ai_credit_ledger</code>.</td></tr>
              </tbody>
            </table>
          </div>

          <div>
            <p className="text-base font-semibold text-slate-900 mb-2">Idempotency guarantee</p>
            <p>
              For Checkout completion, the frontend status-poll AND the webhook can race. Both call
              <code className="bg-slate-100 px-1 rounded">record_transaction_completed()</code> which uses an atomic
              <code className="bg-slate-100 px-1 rounded">update_one</code> matching <code className="bg-slate-100 px-1 rounded">status=&quot;INITIATED&quot;</code>.
              Only the <strong>first</strong> caller flips it to COMPLETED; only the first caller&apos;s
              <code className="bg-slate-100 px-1 rounded"> _apply_completed_purchase()</code> runs. Slots can&apos;t double-credit.
              Subscription provisioning can&apos;t double-fire.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="font-medium text-amber-900 text-sm mb-1">Observability tip</p>
            <p className="text-sm text-amber-800">
              All billing actions log structured lines:
              <code className="bg-amber-100 px-1 rounded ml-1">slot_topup_applied</code>,
              <code className="bg-amber-100 px-1 rounded ml-1">subscription_provisioned</code>,
              <code className="bg-amber-100 px-1 rounded ml-1">ai_credit_topup_applied</code>,
              <code className="bg-amber-100 px-1 rounded ml-1">ai_credits_added</code>,
              <code className="bg-amber-100 px-1 rounded ml-1">pursuit_byom_override</code>,
              <code className="bg-amber-100 px-1 rounded ml-1">release_discount_auto_applied</code>.
              Grep <code className="bg-amber-100 px-1 rounded">/var/log/supervisor/backend.*.log</code> for live audit.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// AUTH ROADMAP — passwordless / MFA design exploration (NOT yet implemented)
// ============================================================================
// Captured from a Feb 2026 brainstorm. The current prod auth is email +
// bcrypt password + 24h JWT. This page documents the target architecture
// and the phased migration so we don't lose context before execution.

function AuthRoadmapContent() {
  return (
    <div className="space-y-6">
      {/* Status banner */}
      <Card className="border-amber-300 shadow-sm bg-gradient-to-r from-amber-50 to-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-5 h-5" />
            Status: DESIGN — not yet implemented
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0 space-y-2 text-sm text-amber-800">
          <p>
            <strong>Current state (Feb 2026):</strong> email + bcrypt password,
            24-hour JWT, no MFA. Test credentials in
            <code className="font-mono mx-1 bg-white px-1 rounded">/app/memory/test_credentials.md</code>.
          </p>
          <p>
            <strong>Target state:</strong> Passwordless with two distinct
            authentication factors per session. Keep this doc in sync as
            decisions land.
          </p>
        </CardContent>
      </Card>

      {/* Goal */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <Target className="w-5 h-5 text-teal-600" />
            Goal
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-3 text-sm text-slate-700">
          <p>
            Replace passwords with a passwordless 2-factor login. The starting
            user proposal was <strong>Email-OTP + TOTP authenticator app</strong>.
            That works, but for a federal-contracting platform a stronger
            target is <strong>Passkey (WebAuthn) primary + TOTP fallback +
            magic-link recovery + backup codes</strong>.
          </p>
          <p className="text-xs text-slate-500">
            This page captures both options, NIST 800-63B compliance impact,
            edge cases, and the phased rollout we plan to execute later.
          </p>
        </CardContent>
      </Card>

      {/* Email-OTP + TOTP evaluation */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <Mail className="w-5 h-5 text-blue-600" />
            Option A — Email OTP + TOTP (the original proposal)
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">Two factors: email-account control + cryptographic shared secret.</p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="grid sm:grid-cols-2 divide-x divide-slate-100">
            <div className="p-5">
              <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-3">Pros</p>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /><span>Eliminates password reuse and credential-stuffing attacks</span></li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /><span>No password storage = no password-leak liability</span></li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /><span>Two distinct sign-in events per login = strong audit trail</span></li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /><span>"Forgot password" flow goes away</span></li>
              </ul>
            </div>
            <div className="p-5">
              <p className="text-xs font-semibold text-red-700 uppercase tracking-wider mb-3">Cons</p>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex gap-2"><X className="w-4 h-4 text-red-600 mt-0.5 shrink-0" /><span>Email OTP is real-time phishable (attacker proxies the login page). Passkeys are not.</span></li>
                <li className="flex gap-2"><X className="w-4 h-4 text-red-600 mt-0.5 shrink-0" /><span>Both factors are "something you have" — strict NIST AAL2 wants you-have + you-know OR you-have + you-are.</span></li>
                <li className="flex gap-2"><X className="w-4 h-4 text-red-600 mt-0.5 shrink-0" /><span>Phone loss = both factors lost (email and authenticator usually live on the same device).</span></li>
                <li className="flex gap-2"><X className="w-4 h-4 text-red-600 mt-0.5 shrink-0" /><span>Email deliverability (10–60s SES/SendGrid delays, spam filtering) makes login feel slower than passwords.</span></li>
                <li className="flex gap-2"><X className="w-4 h-4 text-red-600 mt-0.5 shrink-0" /><span>Onboarding friction: every user must set up an authenticator app, not just power users.</span></li>
                <li className="flex gap-2"><X className="w-4 h-4 text-red-600 mt-0.5 shrink-0" /><span>Shared role accounts (BD coordinator handling 5 logins) — TOTP secrets aren't shareable.</span></li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* NIST compliance table */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <Shield className="w-5 h-5 text-purple-600" />
            NIST 800-63B Authenticator Assurance Levels
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">Federal customers (CMMC L2 / FedRAMP Moderate) require AAL2; FedRAMP High asks for AAL3.</p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Level</th>
                  <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Requirement</th>
                  <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Email OTP + TOTP</th>
                  <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Passkey + TOTP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr className="hover:bg-slate-50">
                  <td className="p-3 font-mono font-semibold">AAL1</td>
                  <td className="p-3">Single-factor anything</td>
                  <td className="p-3"><Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Exceeds</Badge></td>
                  <td className="p-3"><Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Exceeds</Badge></td>
                </tr>
                <tr className="hover:bg-slate-50">
                  <td className="p-3 font-mono font-semibold">AAL2</td>
                  <td className="p-3">Two factors of distinct types</td>
                  <td className="p-3"><Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Borderline (liberal reading)</Badge></td>
                  <td className="p-3"><Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Clears</Badge></td>
                </tr>
                <tr className="hover:bg-slate-50">
                  <td className="p-3 font-mono font-semibold">AAL3</td>
                  <td className="p-3">Hardware-bound + verifier impersonation resistance</td>
                  <td className="p-3"><Badge className="bg-red-100 text-red-700 hover:bg-red-100">Fails</Badge></td>
                  <td className="p-3"><Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Eligible</Badge></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="px-5 py-3 bg-amber-50 border-t border-amber-100 text-xs text-amber-800">
            <strong>Key NIST citation:</strong> SP 800-63-3 §5.1.3.3 deprecates SMS OOB and discourages email OOB as a primary authenticator. Email-OTP combined with TOTP <em>technically</em> clears AAL2, but it doesn't reach AAL3 — only hardware-bound cryptographic authenticators (FIDO2/WebAuthn) do.
          </div>
        </CardContent>
      </Card>

      {/* Three architectures */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <GitBranch className="w-5 h-5 text-teal-600" />
            Architectures Considered
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 divide-y divide-slate-100">
          {/* Option A */}
          <div className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 font-mono">A</Badge>
              <span className="font-semibold text-slate-900">Email OTP + TOTP (the original proposal)</span>
              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Workable</Badge>
            </div>
            <p className="text-sm text-slate-600 mb-2">Cheap to build, removes password attack surface, but email-as-primary is the weak link. CISA threat reports rank phished email as the #1 federal-vendor compromise vector.</p>
            <p className="text-xs text-slate-500"><strong>Verdict:</strong> Acceptable for CMMC L2 / FedRAMP Moderate. Not future-proof if customers move to FedRAMP High.</p>
          </div>

          {/* Option B */}
          <div className="p-5 bg-emerald-50/40">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 font-mono">B</Badge>
              <span className="font-semibold text-slate-900">Passkey (WebAuthn) primary + TOTP fallback + 10 backup codes</span>
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Recommended</Badge>
            </div>
            <p className="text-sm text-slate-600 mb-2">Phishing-resistant by design (origin-bound, no shared secret). Native browser support across Chrome / Safari / Edge / Firefox. iCloud Keychain / Google Password Manager / 1Password / Windows Hello sync handles multi-device. AAL3-eligible with platform attestation.</p>
            <p className="text-xs text-slate-500"><strong>Verdict:</strong> ~2× implementation effort vs Option A. The <code className="font-mono bg-white px-1 rounded">webauthn</code> Python library and <code className="font-mono bg-white px-1 rounded">@simplewebauthn/browser</code> make it tractable. TOTP covers shared kiosks and older browsers.</p>
          </div>

          {/* Option C */}
          <div className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 font-mono">C</Badge>
              <span className="font-semibold text-slate-900">Magic-link primary + TOTP step-up for sensitive ops only</span>
              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Lowest friction</Badge>
            </div>
            <p className="text-sm text-slate-600 mb-2">One click from email = logged in. Sensitive flows (BYOM key changes, bulk delete, billing, role escalation) demand TOTP on top. Used successfully by Slack, Notion, Vercel.</p>
            <p className="text-xs text-slate-500"><strong>Verdict:</strong> Stale magic links in shared inboxes are a real exfil risk. Best for products where most ops are read-only.</p>
          </div>
        </CardContent>
      </Card>

      {/* Edge cases */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Edge Cases Specific to This App
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">These bite in production if not designed up-front.</p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100">
            {[
              { concern: 'Testing agent / E2E suites', detail: 'testing_agent_v3_fork logs in as super admin / contractor / consultant constantly. Need a service-account API token path that bypasses MFA — bind to a fixed IP allowlist or a feature flag like MFA_BYPASS_FOR_E2E.' },
              { concern: 'Stripe post-payment auto-redirect', detail: 'Today the success page reads a JWT cookie and lands on the workspace. Passwordless makes this flow cleaner but we must not require fresh MFA mid-checkout.' },
              { concern: 'Pursuit-invited consultants', detail: 'Email invite already IS the email-OTP factor. Combine "claim invite" + "set up TOTP/passkey" in one screen so consultants are never stuck with one factor.' },
              { concern: 'Multi-tenant org switching', detail: 'A consultant in 5 tenants must not re-MFA on every workspace switch. Trust window of ~30 minutes for re-auth.' },
              { concern: 'Stripe subscription lapse → re-activate', detail: 'Login during grace period must not be MFA-gated, otherwise the customer is locked out while trying to pay us.' },
              { concern: 'AI Credits exhausted lockout', detail: 'If an org runs out of credits AND the only admin loses their authenticator, they cannot reach the top-up page. Recovery flow must handle "MFA-locked + billing-locked" intersection.' },
              { concern: 'Federal evaluator demo accounts', detail: 'Public demo accounts cannot be MFA-protected the way a real account is. Demo mode = magic-link only with watermarked sessions.' },
              { concern: 'Audit logging granularity', detail: 'Today we log JWT issuance. With MFA we must log every factor independently — who signed in, when, from where, with which factor.' },
            ].map((row, idx) => (
              <div key={idx} className="p-4 hover:bg-slate-50">
                <p className="font-semibold text-sm text-slate-900 mb-1">{row.concern}</p>
                <p className="text-xs text-slate-600">{row.detail}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Migration path */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <Workflow className="w-5 h-5 text-violet-600" />
            Phased Migration Path
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">Incremental, reversible, and locks nobody out.</p>
        </CardHeader>
        <CardContent className="p-5">
          <div className="space-y-3">
            {[
              { phase: 'Phase 1', dur: '~1 week', risk: 'Low', title: 'TOTP as optional second factor on top of password', detail: 'Per-user opt-in. Adds AAL2 for users who turn it on. Zero risk because nothing else changes.' },
              { phase: 'Phase 2', dur: '~1–2 weeks', risk: 'Low', title: 'Add WebAuthn / passkey as alternative to password', detail: 'Users now mix-and-match: password / TOTP / passkey. Passkeys auto-sync via iCloud Keychain / Google Password Manager.' },
              { phase: 'Phase 3', dur: '~1 week', risk: 'Medium', title: 'Per-org "MFA required for admins" policy flag', detail: 'Federal customers turn it on for their workspace. Admins must enroll a second factor before next login.' },
              { phase: 'Phase 4', dur: '~1 week', risk: 'Medium', title: 'Per-org "passwordless mode" flag', detail: 'Email magic-link replaces password; TOTP/passkey still required as the second factor. Per-org rollout means we can dogfood internally first.' },
              { phase: 'Phase 5', dur: 'Later', risk: 'High', title: 'Deprecate password entirely once 95% of orgs are passwordless', detail: 'Legacy password accounts get a 30-day forced-migration banner, then disabled.' },
            ].map((p, idx) => (
              <div key={idx} className="p-4 bg-white rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 font-mono">{p.phase}</Badge>
                  <span className="font-semibold text-sm text-slate-900">{p.title}</span>
                  <Badge variant="outline" className="text-xs">{p.dur}</Badge>
                  <Badge className={cn(
                    'text-xs hover:bg-current',
                    p.risk === 'Low' && 'bg-emerald-100 text-emerald-700',
                    p.risk === 'Medium' && 'bg-amber-100 text-amber-700',
                    p.risk === 'High' && 'bg-red-100 text-red-700',
                  )}>{p.risk} risk</Badge>
                </div>
                <p className="text-xs text-slate-600 ml-1">{p.detail}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recommendation */}
      <Card className="border-emerald-300 shadow-sm bg-gradient-to-r from-emerald-50 to-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-emerald-800">
            <Check className="w-5 h-5" />
            Recommendation
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 pt-0 space-y-2 text-sm text-emerald-900">
          <p>
            Ship <strong>Option B</strong> (Passkey-primary + TOTP-fallback +
            magic-link-recovery + backup-codes). Same effort tier as the
            original proposal, materially stronger security posture, and we
            can put <em>"Passkey & FIDO2 ready"</em> on the marketing page —
            federal evaluators light up at that.
          </p>
          <p>
            Start with <strong>Phase 1 (TOTP-as-optional-second-factor)</strong>{' '}
            regardless of which long-term architecture we land on. It's the
            lowest-risk, highest-immediate-value step and unblocks federal
            customers who already require MFA for admins today.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// BRAND ASSETS — logos and favicons for FedBD
// ============================================================================
// Inline file paths (strings, not imports) so they remain copy-paste-friendly
// even when the contents change. Files live in /app/frontend/public/ which is
// served from the site root, so each `path` doubles as the live URL.
const BRAND_ASSETS = [
  {
    id: 'option-a',
    name: 'Option A — Shield & Signal',
    description:
      'Heraldic shield outline (teal #0f766e) with a white EKG / signal pulse cutting horizontally through the middle. Suggests federal authority + Sentinel "watching the signal" intelligence framing.',
    use: 'Best when leading with the Sentinel + pWin Oracle features. Reads as defense / intel.',
    files: [
      { kind: 'Logo (master)', path: '/logo-option-a.svg', size: '512×512', url: '/logo-option-a.svg' },
      { kind: 'Favicon', path: '/favicon-option-a.svg', size: '64×64 (simplified)', url: '/favicon-option-a.svg' },
    ],
  },
  {
    id: 'option-b',
    name: 'Option B — Crosshair Target',
    description:
      'Two concentric teal rings (#0f766e) with cardinal-direction notches breaking through, plus a solid teal diamond at the bullseye. Reads as a precision targeting reticle.',
    use: 'Best when leading with the Bid Pursuit Scorecard + Go/No-Go capture-management workflow. Reads as precision / "lock onto the right opportunity."',
    files: [
      { kind: 'Logo (master)', path: '/logo-option-b.svg', size: '512×512', url: '/logo-option-b.svg' },
      { kind: 'Favicon', path: '/favicon-option-b.svg', size: '64×64 (simplified)', url: '/favicon-option-b.svg' },
    ],
  },
];

const BRAND_PALETTE = [
  { name: 'Primary Teal', hex: '#0f766e', usage: 'Logo fill, brand primary' },
  { name: 'Logo White', hex: '#ffffff', usage: 'Negative space inside logo (Option A signal line)' },
];

function BrandAssetsContent() {
  const [copied, setCopied] = useState(null);
  const copyValue = (value, key, label) => {
    Promise.resolve(navigator.clipboard.writeText(value))
      .then(() => {
        setCopied(key);
        toast.success(label || 'Copied', { id: `brand-${key}` });
        setTimeout(() => setCopied((c) => (c === key ? null : c)), 1800);
      })
      .catch(() => toast.error('Could not copy', { id: `brand-${key}` }));
  };

  return (
    <div className="space-y-6">
      {/* Intro banner */}
      <Card className="border-teal-200 shadow-sm bg-gradient-to-r from-teal-50 to-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-teal-800">
            <Image className="w-5 h-5" />
            FedBD Brand Assets
          </CardTitle>
          <p className="text-sm text-teal-700 mt-1">
            Two candidate logo systems, each with a master SVG and a
            simplified favicon. Files live at <code className="font-mono bg-white px-1 rounded">/app/frontend/public/</code>
            and are served from the site root, so the path you see below is also the live URL.
          </p>
        </CardHeader>
      </Card>

      {/* Logo cards */}
      {BRAND_ASSETS.map((asset) => (
        <Card key={asset.id} className="border-slate-200 shadow-sm" data-testid={`brand-asset-${asset.id}`}>
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-slate-800 text-base">{asset.name}</CardTitle>
            <p className="text-xs text-slate-500">{asset.description}</p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid md:grid-cols-2 gap-0 divide-x divide-slate-100">
              {/* Visual preview pane */}
              <div className="p-6 bg-slate-50 flex flex-col items-center justify-center gap-4">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Master logo • 192×192 preview</p>
                <img
                  src={asset.files[0].url}
                  alt={`${asset.name} master logo`}
                  className="w-48 h-48 bg-white rounded-lg shadow-sm p-3"
                  data-testid={`brand-asset-${asset.id}-logo`}
                />
                <div className="flex items-end gap-4 mt-2">
                  <div className="flex flex-col items-center gap-1">
                    <img src={asset.files[1].url} alt="favicon 64" className="w-16 h-16 bg-white rounded shadow-sm p-1" />
                    <span className="text-[10px] text-slate-400">64px</span>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <img src={asset.files[1].url} alt="favicon 32" className="w-8 h-8 bg-white rounded shadow-sm p-0.5" />
                    <span className="text-[10px] text-slate-400">32px</span>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <img src={asset.files[1].url} alt="favicon 16" className="w-4 h-4 bg-white rounded shadow-sm" />
                    <span className="text-[10px] text-slate-400">16px</span>
                  </div>
                </div>
                <p className="text-[10px] text-slate-400 italic mt-2">Favicon at typical browser tab sizes</p>
              </div>

              {/* File table pane */}
              <div className="p-5">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">When to use</p>
                <p className="text-sm text-slate-700 mb-4">{asset.use}</p>

                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Files</p>
                <div className="space-y-2">
                  {asset.files.map((file, fIdx) => {
                    const copyKey = `${asset.id}-${fIdx}`;
                    return (
                      <div key={fIdx} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-md border border-slate-200">
                        <Badge variant="outline" className="text-[10px] shrink-0">{file.kind}</Badge>
                        <code className="text-xs text-slate-700 font-mono flex-1 truncate" title={file.path}>{file.path}</code>
                        <span className="text-[10px] text-slate-400 shrink-0">{file.size}</span>
                        <button
                          type="button"
                          onClick={() => copyValue(file.path, copyKey, `${file.kind} path copied`)}
                          className="p-1 rounded text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors shrink-0"
                          title="Copy path"
                          data-testid={`copy-brand-${copyKey}`}
                        >
                          {copied === copyKey ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                        <a
                          href={file.url}
                          download
                          className="p-1 rounded text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors shrink-0"
                          title="Download"
                          data-testid={`download-brand-${copyKey}`}
                        >
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                  <strong>Server location:</strong> <code className="font-mono">/app/frontend/public/{asset.files[0].path.slice(1)}</code>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Side-by-side preview helper */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-slate-800 text-base">Side-by-side preview</CardTitle>
          <p className="text-xs text-slate-500">A standalone HTML page that renders both logos at multiple sizes.</p>
        </CardHeader>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-md border border-slate-200">
            <Badge variant="outline" className="text-[10px]">HTML</Badge>
            <code className="text-xs text-slate-700 font-mono flex-1">/logo-preview.html</code>
            <a
              href="/logo-preview.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-teal-600 hover:text-teal-800 flex items-center gap-1"
              data-testid="brand-preview-link"
            >
              Open <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Palette */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-slate-800 text-base">Brand Palette</CardTitle>
          <p className="text-xs text-slate-500">Hex values used by the logo system; mirrors the in-app teal accent.</p>
        </CardHeader>
        <CardContent className="p-5">
          <div className="grid sm:grid-cols-2 gap-3">
            {BRAND_PALETTE.map((c) => (
              <div key={c.hex} className="flex items-center gap-3 p-3 rounded-md border border-slate-200">
                <div
                  className="w-12 h-12 rounded-md border border-slate-200 shrink-0"
                  style={{ backgroundColor: c.hex }}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-slate-900">{c.name}</p>
                  <p className="text-xs text-slate-500">{c.usage}</p>
                </div>
                <button
                  type="button"
                  onClick={() => copyValue(c.hex, `palette-${c.hex}`, `${c.hex} copied`)}
                  className="px-2 py-1 rounded font-mono text-xs bg-slate-100 text-slate-700 hover:bg-teal-50 hover:text-teal-700 transition-colors flex items-center gap-1"
                  data-testid={`copy-palette-${c.hex.replace('#', '')}`}
                >
                  {copied === `palette-${c.hex}` ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                  {c.hex}
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Retrieval cheatsheet */}
      <Card className="border-slate-200 shadow-sm bg-slate-900 text-slate-100">
        <CardHeader>
          <CardTitle className="text-white text-base">Retrieval Cheatsheet</CardTitle>
          <p className="text-xs text-slate-400">Use these from the bash terminal of the running pod or your local clone.</p>
        </CardHeader>
        <CardContent className="p-5 space-y-3 text-sm">
          <pre className="bg-slate-950 text-slate-200 px-3 py-2 rounded text-xs overflow-x-auto">{`# List all logo + favicon files
ls -la /app/frontend/public/ | grep -E "logo|favicon"

# Open in the browser (replace BASE with your REACT_APP_BACKEND_URL)
$BASE/logo-option-a.svg
$BASE/logo-option-b.svg
$BASE/favicon-option-a.svg
$BASE/favicon-option-b.svg
$BASE/logo-preview.html

# Pull file contents
cat /app/frontend/public/logo-option-a.svg
cat /app/frontend/public/logo-option-b.svg`}</pre>
        </CardContent>
      </Card>
    </div>
  );
}

function VersioningPolicyContent() {
  return (
    <div className="space-y-6">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-slate-900 flex items-center gap-2"
                     style={{ fontFamily: 'Manrope, sans-serif' }}>
            <GitBranch className="w-6 h-6 text-teal-600" />
            Versioning Policy
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            How FedBD versions itself, when to bump, and what every contributor must do on every release.
          </p>
        </CardHeader>
        <CardContent className="space-y-6 text-sm text-slate-700 leading-relaxed">

          <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
            <p className="font-semibold text-teal-900 mb-1">Canonical playbook</p>
            <p className="text-teal-900 text-xs">
              The authoritative reference is <code className="bg-teal-100 px-1 rounded">/app/memory/VERSIONING.md</code>.
              Every fork/agent session is required to read it on start. This page summarises it for human operators.
            </p>
          </div>

          <div>
            <p className="font-semibold text-slate-900 mb-2">Three parallel tracks</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>Platform SemVer</strong> (current: <code className="bg-slate-100 px-1 rounded">v3.16.1</code>) — bumped on every customer-visible change. Sources of truth: <code className="bg-slate-100 px-1 rounded">backend/app/__version__.py</code> + <code className="bg-slate-100 px-1 rounded">frontend/src/lib/version.js</code> + <code className="bg-slate-100 px-1 rounded">frontend/package.json</code>.</li>
              <li><strong>Legal-doc SemVer</strong> (current: <code className="bg-slate-100 px-1 rounded">v3.0</code>) — Terms and Privacy share a single number. Bumping triggers re-acceptance modal on next login.</li>
              <li><strong>API contract</strong> — currently unversioned (<code className="bg-slate-100 px-1 rounded">/api/*</code>). Move to <code className="bg-slate-100 px-1 rounded">/api/v2/...</code> only when shipping a public API or a breaking contract change.</li>
            </ul>
          </div>

          <div>
            <p className="font-semibold text-slate-900 mb-2">Bump decision matrix</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border border-slate-200 rounded">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700">Change</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700">Bump</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700">Re-prompt users</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700">GitHub push reminder</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr><td className="px-3 py-2">Bug fix / copy edit / refactor</td><td className="px-3 py-2 font-mono">PATCH (3.6.0 → 3.6.1)</td><td className="px-3 py-2">No</td><td className="px-3 py-2 text-slate-500">Optional</td></tr>
                  <tr><td className="px-3 py-2">New customer-visible feature, sub-tab, endpoint, role, pricing tier</td><td className="px-3 py-2 font-mono">MINOR (3.6.x → 3.7.0)</td><td className="px-3 py-2">No</td><td className="px-3 py-2 font-semibold text-emerald-700">YES</td></tr>
                  <tr><td className="px-3 py-2">Breaking URL / auth / billing / API change</td><td className="px-3 py-2 font-mono">MAJOR (3.x → 4.0.0)</td><td className="px-3 py-2">No</td><td className="px-3 py-2 font-semibold text-emerald-700">YES</td></tr>
                  <tr><td className="px-3 py-2">Substantive Terms / Privacy change</td><td className="px-3 py-2 font-mono">Legal MAJOR (1.x → 2.0)</td><td className="px-3 py-2 font-semibold text-amber-700">YES — modal blocks</td><td className="px-3 py-2 font-semibold text-emerald-700">YES</td></tr>
                  <tr><td className="px-3 py-2">Legal clarification / typo / contact-email</td><td className="px-3 py-2 font-mono">Legal MINOR (1.0 → 1.1)</td><td className="px-3 py-2">Silent banner (TODO)</td><td className="px-3 py-2 text-slate-500">Optional</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <p className="font-semibold text-slate-900 mb-2">Single-bump ritual (every release)</p>
            <ol className="list-decimal pl-6 space-y-1.5">
              <li>Update <code className="bg-slate-100 px-1 rounded">frontend/package.json</code> &quot;version&quot;.</li>
              <li>Update <code className="bg-slate-100 px-1 rounded">frontend/src/lib/version.js</code> (<code>APP_VERSION</code>).</li>
              <li>Update <code className="bg-slate-100 px-1 rounded">backend/app/__version__.py</code> (<code>__version__</code>).</li>
              <li>Add a section to the top of <code className="bg-slate-100 px-1 rounded">/app/CHANGELOG.md</code> using the Keep-a-Changelog format.</li>
              <li>Add a customer-friendly entry to System Docs → Release → Release Notes for MINOR/MAJOR.</li>
              <li>For legal bumps only: also bump <code className="bg-slate-100 px-1 rounded">LATEST_POLICY_VERSION</code> in both files and the inline version on the Terms / Privacy page.</li>
              <li>Commit locally (<code className="bg-slate-100 px-1 rounded">git add / git commit</code>) only when the platform owner explicitly asks — this repo has no remote and nothing auto-pushes.</li>
            </ol>
          </div>

          <div>
            <p className="font-semibold text-slate-900 mb-2">Re-acceptance flow (legal MAJOR only)</p>
            <ol className="list-decimal pl-6 space-y-1.5">
              <li><code className="bg-slate-100 px-1 rounded">DashboardLayout</code> compares the current user&apos;s <code className="bg-slate-100 px-1 rounded">policyVersion</code> against <code className="bg-slate-100 px-1 rounded">LATEST_POLICY_VERSION</code> on every dashboard mount. Mismatch ⇒ blocking modal.</li>
              <li><code className="bg-slate-100 px-1 rounded">LoginPage</code> repeats the same check on the login response.</li>
              <li><code className="bg-slate-100 px-1 rounded">PolicyAcceptanceModal</code> POSTs to <code className="bg-slate-100 px-1 rounded">/api/auth/accept-policy</code> with the new version.</li>
              <li>Backend rejects mismatched versions to prevent a stale client from re-acknowledging an older version.</li>
              <li>Backend appends an immutable row to <code className="bg-slate-100 px-1 rounded">policy_acceptances</code> with <code className="bg-slate-100 px-1 rounded">{`{user_id, email, organization_id, policy_version, accepted_at, ip, user_agent}`}</code>.</li>
              <li>This audit trail is what makes the new Terms enforceable against existing users.</li>
            </ol>
          </div>

          <div>
            <p className="font-semibold text-slate-900 mb-2">In-app surfaces</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>Version pill</strong> top-right of every Super Admin page (<code className="bg-slate-100 px-1 rounded">data-testid=&quot;app-version-pill&quot;</code>).</li>
              <li><strong>CHANGELOG.md</strong> — engineer-facing canonical history at <code className="bg-slate-100 px-1 rounded">/app/CHANGELOG.md</code>. P2 future: refactor the Release Notes tab to read from this file at build time.</li>
              <li><strong>Release Notes</strong> sub-tab — narrative, customer-facing.</li>
              <li><strong>Effective date</strong> on Terms / Privacy — bumps with the legal version.</li>
            </ul>
          </div>

          <div>
            <p className="font-semibold text-slate-900 mb-2">Database schema versioning</p>
            <p>Currently uses idempotent startup hooks in <code className="bg-slate-100 px-1 rounded">mongo_models.py</code> that check-then-write (e.g., <code className="bg-slate-100 px-1 rounded">seed_extra_super_admins()</code>). Each new migration follows the same pattern. When the team grows past three developers, switch to a numbered-script runner. The legacy <code className="bg-slate-100 px-1 rounded">backend/migrations/*.sql</code> files are frozen and slated for removal in a future MAJOR.</p>
          </div>

          <div>
            <p className="font-semibold text-slate-900 mb-2">Git &amp; deploy</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>This repo is local-only — no remote is configured. <code className="bg-slate-100 px-1 rounded">git commit</code> happens locally, only when the platform owner explicitly asks; there is no <code className="bg-slate-100 px-1 rounded">git push</code>/<code className="bg-slate-100 px-1 rounded">git pull</code> anywhere in the workflow.</li>
              <li>Production is a self-managed AWS Lightsail instance. There is no git on the server — deploys are file-copy based (rsync/scp of <code className="bg-slate-100 px-1 rounded">backend/</code> and the built <code className="bg-slate-100 px-1 rounded">frontend/build</code>), never a <code className="bg-slate-100 px-1 rounded">git pull</code>.</li>
              <li>All fixes/features are tested in local dev first. Nothing is copied to Lightsail or the backend service/nginx restarted there until the platform owner explicitly approves a deploy.</li>
            </ul>
          </div>

        </CardContent>
      </Card>
    </div>
  );
}




// ============================================================================
// GSA eBuy Sentinel — v3.18.23
// ============================================================================
function EbuySentinelContent() {
  return (
    <div className="space-y-6">
      <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50">
        <CardHeader>
          <CardTitle className="text-cyan-900 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            GSA eBuy Sentinel — Folder-Upload Ingest
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-700 leading-relaxed">
            GSA eBuy has no public API and is gated to GSA Schedule holders. Users run a
            local scraper that drops an <code>Output/</code> folder; FedBD ingests it via
            the browser's File System Access API and applies SIN-based tier scoring
            (Gold/Silver/Bronze) parallel to SAM.gov's NAICS/PSC tiers.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-slate-800">Output Folder Structure</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="text-xs bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto">
{`Output/
├── state.json                  ← REQUIRED. { "last_scan": ISO, ... }
├── scans/                      ← REQUIRED. Time-series of CSV snapshots.
│   └── scan_<TS>.csv           ← Latest-named file wins (lex-max).
│       Columns: Request ID, Title, Category (SIN), Buyer (multi-line),
│       Source, Posted, Close, Amendments, Q&A, _contract_num, _url,
│       _mras_link, _is_mras, _cancelled, _award_key
└── MAS|GWAC|OASIS/             ← OPTIONAL. RFQ attachments.
    └── <RFQID>_<title-slug>/   ← Stored in GridFS bucket "ebuy_attachments"
        ├── solicitation.pdf
        └── attachment_*.docx`}
          </pre>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-slate-800">SIN Tier Logic</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded">
            <Badge className="bg-amber-100 text-amber-800">Gold · 50pt</Badge>
            <span className="text-sm text-slate-700">
              Category ∈ <code>gsa_sin_codes</code> — exact SIN match the firm is awarded on.
            </span>
          </div>
          <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded">
            <Badge className="bg-slate-100 text-slate-700">Silver · 30pt</Badge>
            <span className="text-sm text-slate-700">
              Category ∈ <code>gsa_related_sin_codes</code> — adjacent capability the firm
              can credibly team on.
            </span>
          </div>
          <div className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded">
            <Badge className="bg-orange-100 text-orange-800">Bronze · 10pt</Badge>
            <span className="text-sm text-slate-700">
              No SIN match. Worth scanning but not a fit.
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-slate-800">Endpoints</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[
              { method: 'POST', path: '/api/ebuy/ingest', desc: 'Multipart: state_json + scan_csv + optional archives[]. Returns sync_run_id + new/updated/gone counts + suggested_sins.' },
              { method: 'GET', path: '/api/ebuy/opportunities', desc: 'Filters: status, tier, is_mras, request_id_search.' },
              { method: 'POST', path: '/api/ebuy/opportunities/{id}/claim', desc: 'Promotes RFQ to pursuit. Stamps added_via_sentinel + ebuy_request_id.' },
              { method: 'POST', path: '/api/ebuy/opportunities/{id}/dismiss', desc: 'Marks RFQ status=DISMISSED.' },
              { method: 'GET', path: '/api/ebuy/profile', desc: 'Per-org SIN profile (gsa_sin_codes, gsa_related_sin_codes, gsa_schedule_contracts).' },
              { method: 'PUT', path: '/api/ebuy/profile', desc: 'Consultant can pass ?organization_id= to edit a linked client\'s profile.' },
              { method: 'GET', path: '/api/ebuy/sync-runs', desc: 'Audit log of past ingest runs.' },
            ].map((ep, i) => (
              <div key={i} className="flex items-start gap-3 p-2 hover:bg-slate-50 rounded">
                <Badge className={ep.method === 'GET' ? 'bg-emerald-100 text-emerald-700' : ep.method === 'PUT' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>
                  {ep.method}
                </Badge>
                <code className="text-sm font-mono text-slate-800 flex-shrink-0">{ep.path}</code>
                <span className="text-sm text-slate-600">{ep.desc}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-slate-800">Diff Semantics</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-slate-700">
            <li><strong>NEW</strong> — Never seen before for this org. <code>first_seen=now</code>.</li>
            <li><strong>UPDATED</strong> — Amendments blob changed since last scan, OR was previously marked GONE.</li>
            <li><strong>GONE</strong> — Was in a prior scan, NOT in the latest CSV. Auto-flipped via <code>$nin</code> on the request_id set.</li>
            <li><strong>CLAIMED / DISMISSED</strong> — Terminal user actions. <em>Never</em> overwritten by ingest.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// Consultant ↔ Contractor Collaboration Model — v3.18.22+
// ============================================================================
function CollaborationContent() {
  return (
    <div className="space-y-6">
      <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
        <CardHeader>
          <CardTitle className="text-purple-900 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Consultant ↔ Contractor Collaboration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-700 leading-relaxed">
            FedBD supports two distinct contractor modes. Every Knowledge Base, Sentinel,
            and Pursuit endpoint resolves scope through a single helper —
            <code className="bg-white px-1 rounded ml-1">get_consultant_org_id(db, user)</code>
            in <code className="bg-white px-1 rounded">routes/global_kb.py:174</code>.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-slate-800">The Two Contractor Modes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-2">Mode</th>
                  <th className="text-left p-2">Org Type</th>
                  <th className="text-left p-2">Managed By</th>
                  <th className="text-left p-2">Data Ownership</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="p-2 font-medium">Standalone</td>
                  <td className="p-2"><code>CONTRACTOR</code></td>
                  <td className="p-2 text-slate-500">—</td>
                  <td className="p-2">Own org_id</td>
                </tr>
                <tr>
                  <td className="p-2 font-medium">Managed</td>
                  <td className="p-2"><code>CONTRACTOR</code></td>
                  <td className="p-2">A <code>CONSULTANCY</code> via <code>consultant_client_links</code></td>
                  <td className="p-2">Stored under consultancy_org_id (shadow client)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-slate-800">Scope Resolver — get_consultant_org_id</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="text-xs bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto">
{`async def get_consultant_org_id(db, user):
    user_org = await db.organizations.find_one({"id": user["organization_id"]})

    # (1) User belongs to a consultancy → own consultancy
    if user_org and user_org.get("type") == "CONSULTANCY":
        return user_org["id"]

    # (2) Managed contractor → resolve to managing consultancy
    link = await db.consultant_client_links.find_one({
        "client_org_id": user["organization_id"],
        "status": "ACTIVE"
    })

    # (3) Standalone contractor → own org
    return link["consultant_org_id"] if link else user["organization_id"]`}
          </pre>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-slate-800">Reusable Asset Library vs Client Library</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-2 w-1/4"></th>
                  <th className="text-left p-2">Reusable Asset Library<br /><span className="text-xs font-normal text-slate-500">(sidebar)</span></th>
                  <th className="text-left p-2">Client Library<br /><span className="text-xs font-normal text-slate-500">(Client Profile)</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="p-2 font-medium">Collection</td>
                  <td className="p-2"><code>consultancy_global_kb</code></td>
                  <td className="p-2">Per-client: <code>key_personnel</code>, <code>certifications</code>, <code>past_performances</code>, <code>winning_proposals</code></td>
                </tr>
                <tr>
                  <td className="p-2 font-medium">Scope key</td>
                  <td className="p-2"><code>consultant_org_id</code> (resolved)</td>
                  <td className="p-2"><code>organization_id</code> (the specific client)</td>
                </tr>
                <tr>
                  <td className="p-2 font-medium">Standalone Contractor</td>
                  <td className="p-2">Sees own org's library. Single library.</td>
                  <td className="p-2 text-slate-500">N/A — contractors have no "clients"</td>
                </tr>
                <tr>
                  <td className="p-2 font-medium">Consultant</td>
                  <td className="p-2">Sees cross-portfolio library</td>
                  <td className="p-2">Switches per selected client</td>
                </tr>
                <tr>
                  <td className="p-2 font-medium">Managed Contractor</td>
                  <td className="p-2">Sees consultancy's library (uploads gated to consultant)</td>
                  <td className="p-2">Sees own org's client data</td>
                </tr>
                <tr>
                  <td className="p-2 font-medium">Use case</td>
                  <td className="p-2">Templates, win themes, boilerplate reused across MANY clients</td>
                  <td className="p-2">Federal-grade evidence (past perf, certs, KP) for ONE client</td>
                </tr>
                <tr>
                  <td className="p-2 font-medium">Add gate</td>
                  <td className="p-2">Frontend: <code>isConsultantOrAdmin || (CONTRACTOR_ADMIN && !isManagedByConsultancy)</code>. Backend defensive 403 when library belongs to a different org. (v3.18.26)</td>
                  <td className="p-2">All authors with edit perm on the client</td>
                </tr>
                <tr>
                  <td className="p-2 font-medium">Promotion path</td>
                  <td className="p-2 text-slate-500">— (target)</td>
                  <td className="p-2">"Promote to Library" button on each row → copies to consultancy_global_kb</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-slate-800">Pursuit Visibility — origin + collaborators</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-700 mb-3">
            Two collaborators see the same pursuit without copying data. The list endpoint
            attaches per-viewer context:
          </p>
          <ul className="space-y-2 text-sm text-slate-700">
            <li><code className="bg-slate-100 px-1 rounded">origin</code> — <code>"SELF_CREATED"</code> if owner, else <code>"INVITED"</code> (viewing through a consultant_client_link)</li>
            <li><code className="bg-slate-100 px-1 rounded">collaborators[]</code> — owner + every consultancy with an ACTIVE link</li>
            <li><code className="bg-slate-100 px-1 rounded">added_via_sentinel + sentinel_source</code> — drives the "SAM.gov · consultant" / "eBuy · contractor" badge on the Pipeline card</li>
            <li><code className="bg-slate-100 px-1 rounded">billing_org_id</code> — whose Universal Key budget pays for AI on this pursuit. Consultant-claimed pursuits bill the consultancy; client-started pursuits bill the client.</li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-slate-800">Sentinel Claim on Behalf of a Client</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2 text-sm text-slate-700 list-decimal list-inside">
            <li>Consultant opens Marketplace Sentinel, picks Client X from the switcher.</li>
            <li>Sentinel scopes the scan + listing to X's <code>sentinel_config</code> (NAICS/PSC for SAM.gov; SIN profile for eBuy).</li>
            <li>Consultant clicks <strong>Claim → Pursuit</strong>.</li>
            <li>Backend writes <code>db.pursuits</code> with:
              <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded mt-2 overflow-x-auto">
{`{
  organization_id:    "<Client X org_id>",
  added_via_sentinel: true,
  sentinel_source:    "sam.gov" | "ebuy.gsa.gov",
  added_by_org_id:    "<consultancy_id>",
  added_by_org_type:  "CONSULTANT",
  billing_org_id:     "<consultancy_id>"
}`}
              </pre>
            </li>
            <li>Pursuit appears on BOTH pipelines: Client X's (owner) AND consultant's (via active link).</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}

